// ---------------------------------------------------------------------------
// API client for the FREEEMAN hub (invoice-review Worker)
// ---------------------------------------------------------------------------

const API_BASE = 'https://invoice-review.system-0f7.workers.dev';

// ---------------------------------------------------------------------------
// Receipt Tracking (schema-v18, 2026-04-22)
// ---------------------------------------------------------------------------
export type ReceiptStatus =
  | 'pending' | 'in_triage' | 'proposed' | 'approved'
  | 'registered' | 'rejected' | 'skipped' | 'deferred';

export interface AricaReceiptTracking {
  id: number;
  arica_rowid: number;
  partner_id: number | null;
  partner_name: string | null;
  receive_date: string;
  processing_status: ReceiptStatus;
  reversible: number;
  last_updated: string;
  handled_by: string | null;
  reason: string | null;
  parent_ref: string | null;
  ref_number: string | null;
  proposal_id: number | null;
  document_type: string | null;
  issue_date_pdf: string | null;
  amount_pdf: number | null;
  tracked_at: string;
}

export async function fetchReceipts(params: {
  from?: string; to?: string; status?: ReceiptStatus | ''; partner_id?: number;
  limit?: number; offset?: number;
}): Promise<{ items: AricaReceiptTracking[]; total: number; by_status: Record<string, number> }> {
  const q = new URLSearchParams();
  if (params.from) q.set('from', params.from);
  if (params.to) q.set('to', params.to);
  if (params.status) q.set('status', params.status);
  if (params.partner_id) q.set('partner_id', String(params.partner_id));
  if (params.limit) q.set('limit', String(params.limit));
  if (params.offset) q.set('offset', String(params.offset));
  const r = await fetch(`${API_BASE}/api/arica/receipts?${q}`);
  if (!r.ok) throw new Error(`fetchReceipts failed: ${r.status}`);
  return r.json();
}

export async function restoreReceipt(aricaRowid: number, reason?: string): Promise<any> {
  const r = await fetch(`${API_BASE}/api/arica/receipts/${aricaRowid}/restore`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ changed_by: 'web_app_user', reason }),
  });
  if (!r.ok) throw new Error(`restore failed: ${r.status}`);
  return r.json();
}

export async function fetchReceiptAuditLog(aricaRowid: number): Promise<any> {
  const r = await fetch(`${API_BASE}/api/arica/receipts/${aricaRowid}/audit-log`);
  if (!r.ok) throw new Error(`audit log failed: ${r.status}`);
  return r.json();
}

// ---------------------------------------------------------------------------
// Partner Billing Cadence (schema-v18)
// ---------------------------------------------------------------------------
export interface PartnerCadence {
  partner_id: number;
  partner_name: string | null;
  issue_day_pattern: string;
  issue_anchor_day: number | null;
  issue_business_day_adjust: number;
  confidence: number;
  sample_size: number;
  mode_ratio: number | null;
  manual_override: number;
  last_reviewed_at: string;
  notes: string | null;
}

export async function fetchPartnerCadences(pattern?: string, minConfidence?: number): Promise<{ items: PartnerCadence[]; count: number }> {
  const q = new URLSearchParams();
  if (pattern) q.set('pattern', pattern);
  if (minConfidence != null) q.set('min_confidence', String(minConfidence));
  const r = await fetch(`${API_BASE}/api/partner-cadence?${q}`);
  if (!r.ok) throw new Error(`fetchPartnerCadences failed: ${r.status}`);
  return r.json();
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type InstructionType =
  | 'correct_account_item'
  | 'reject_entry'
  | 'add_note'
  | 'reprocess_invoice'
  | 'approve_entry'
  | 'register_approved_batch'
  | 'user_message';

// ---------------------------------------------------------------------------
// Batch registration
// ---------------------------------------------------------------------------

export interface BatchDetail {
  accountItemId: number;
  taxCode: number;
  sectionId: number;
  itemId?: number | null;
  amount: number;
  description?: string;
  tagIds?: number[];
}

export interface BatchEntry {
  entry_id: string;
  rowid: number;
  // Canonical dedupe key (e.g., 'arica:21474-A'). Required for Worker-side programmatic guard.
  ref_number: string;
  partner_id: number;
  partner_name: string;
  issue_date: string;
  due_date: string;
  grand_total: number;
  details: BatchDetail[];
  pdf_rowid: number;
  idempotency_key: string;
}

export interface CreateInstructionRequest {
  instruction_type: InstructionType;
  target_entity_type: string; // 'journal_entry' | 'deal' | 'invoice'
  target_entity_id: string;
  payload: Record<string, any>;
  created_by: string;
  note?: string;
}

export interface InstructionResponse {
  /** Hub returns `id` (some older docs referenced `instruction_id`) */
  id?: string;
  instruction_id?: string;
  status?: string;
  created_at?: string;
  [key: string]: any;
}

/** Returns the instruction id from either field. */
export function instructionIdOf(r: InstructionResponse): string {
  return r.instruction_id || r.id || '';
}

export interface EventItem {
  event_seq: number;
  event_type: string;
  status?: string;
  /**
   * Hub API returns `summary` as a flexible JSON value - in the live data it
   * is almost always an object (e.g. { deal_id, message }). We keep it as
   * `any` to tolerate both object and string shapes.
   */
  summary?: string | Record<string, any> | null;
  created_at: string;
  instruction_id?: string;
  entry_id?: string;
  metadata?: Record<string, any>;
}

export interface EventsResponse {
  items: EventItem[];
  latest_seq: number;
  count: number;
}

export interface ActivityItem {
  activity_id?: string;
  activity_type: string; // 'claude_message' | 'tool_call' | 'tool_result' | 'event' | ...
  actor?: string; // 'claude' | 'user' | 'tool' | system
  content?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface ActivityResponse {
  items: ActivityItem[];
}

// ---------------------------------------------------------------------------
// Timeline (unified) – /api/timeline
// ---------------------------------------------------------------------------

export type TimelineKind =
  | 'progress'
  | 'tool'
  | 'message'
  | 'result'
  | 'error'
  | 'warning'
  | 'success'
  | 'info';

export type TimelineSource = 'event' | 'activity';

export interface TimelineItem {
  id: string;
  source_id: string;
  dedup_key: string;
  sequence: number; // global_seq
  timestamp: string;
  workspace_id: string;
  source: TimelineSource;
  source_agent: string;
  session_id?: string | null;
  run_id?: string | null;
  instruction_id?: string | null;
  invoice_id?: string | null;
  entry_id?: string | null;
  kind: TimelineKind;
  actor: string;
  title: string;
  content: string;
  progress?: { current: number; total: number } | null;
  metadata?: Record<string, unknown> | null;
  status?: 'succeeded' | 'failed' | 'in_progress' | null;
}

export interface TimelineResponse {
  items: TimelineItem[];
  count: number;
  latest_seq: number;
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Audit blocking (session open 409) — 2026-07-15 blockfix
//   worker の /api/claude/session/open が audit blocking violations で 409 を返すと
//   user の正常操作が全 UI で止まる。ここで全ページ共通の CustomEvent に変換し、
//   AppShell 常駐の AuditBlockDialog が日本語列挙 + [自動修復を試す] を表示する。
//   body 無し / 形式不明でも壊れない (defensive)。
// ---------------------------------------------------------------------------
export interface AuditBlockViolation {
  rule?: string;
  rule_ref?: string;
  severity?: string;
  count?: number;
  fix?: string;
  error?: string;
  [k: string]: any;
}

export const AUDIT_BLOCKED_EVENT = 'freeeman:audit-blocked';

export function notifyAuditBlocked(body: any): boolean {
  try {
    const errKey = body && typeof body === 'object' ? String(body.error || '') : '';
    if (errKey !== 'audit_blocking_violations' && errKey !== 'audit_blocking_violations_during_heartbeat') {
      return false;
    }
    window.dispatchEvent(new CustomEvent(AUDIT_BLOCKED_EVENT, { detail: body }));
    return true;
  } catch {
    return false;
  }
}

// POST /api/admin/auto-fix-audit-violations — worker 側 決定論 repairer 群の実行
// (推測ゼロ: canonical/curated/実データからの補完のみ。補完不能は不触/機械 reject)
export async function runAutoFixAuditViolations(): Promise<{
  ok?: boolean; summary?: Record<string, any>; actions?: any[]; [k: string]: any;
}> {
  const r = await fetch(`${API_BASE}/api/admin/auto-fix-audit-violations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirm_token: 'auto_fix_audit_20260518' }),
  });
  let body: any = null;
  try { body = await r.json(); } catch { body = null; }
  if (!r.ok) {
    throw new Error(`自動修復 API 失敗: ${body?.error || `${r.status} ${r.statusText}`}`);
  }
  return body || {};
}

// 自動修復後の session open 再試行 (成功時は session_id を localStorage に保存)
export async function retryClaudeSessionOpen(): Promise<{ ok: boolean; status: number; body: any }> {
  let r: Response;
  try {
    r = await fetch(`${API_BASE}/api/claude/session/open`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ workspace_id: 'kumagai-corp', reason: 'AuditBlockDialog retry after auto-fix' }),
    });
  } catch (e) {
    return { ok: false, status: 0, body: { error: e instanceof Error ? e.message : String(e) } };
  }
  let body: any = null;
  try { body = await r.json(); } catch { body = null; }
  if (r.ok && body?.session_id) {
    try { localStorage.setItem('claude_session_id', body.session_id); } catch { /* ignore */ }
  }
  return { ok: r.ok, status: r.status, body };
}

async function request<T>(
  path: string,
  opts: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers ?? {}),
    },
  });
  if (!res.ok) {
    let body: any = null;
    try { body = await res.json(); } catch { /* ignore */ }
    // session open / heartbeat の audit blocking 409 → 全ページ共通ダイアログへ通知
    if (res.status === 409) notifyAuditBlocked(body);
    const msg = body?.message || body?.error || `${res.status} ${res.statusText}`;
    const err: Error & { body?: any; status?: number } = new Error(`API ${path} failed: ${msg}`);
    err.body = body;
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export function createInstruction(
  body: CreateInstructionRequest,
): Promise<InstructionResponse> {
  return request<InstructionResponse>('/api/instructions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fetchEvents(
  after: number = 0,
  limit: number = 100,
): Promise<EventsResponse> {
  const q = `?after=${encodeURIComponent(String(after))}&limit=${encodeURIComponent(String(limit))}`;
  return request<EventsResponse>(`/api/events${q}`);
}

export function fetchActivity(
  limit: number = 50,
): Promise<ActivityResponse> {
  const q = `?limit=${encodeURIComponent(String(limit))}`;
  return request<ActivityResponse>(`/api/activity${q}`);
}

export function createActivity(
  body: Partial<ActivityItem>,
): Promise<ActivityItem> {
  return request<ActivityItem>('/api/activity', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fetchTimeline(
  after: number = 0,
  limit: number = 100,
): Promise<TimelineResponse> {
  const q =
    `?after=${encodeURIComponent(String(after))}` +
    `&limit=${encodeURIComponent(String(limit))}` +
    `&workspace_id=kumagai-corp`;
  return request<TimelineResponse>(`/api/timeline${q}`);
}

export function registerApprovedBatch(
  entries: BatchEntry[],
  batchId?: string,
): Promise<InstructionResponse> {
  const bid = batchId || `batch_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}_${Date.now()}`;
  return createInstruction({
    instruction_type: 'register_approved_batch',
    target_entity_type: 'batch',
    target_entity_id: bid,
    payload: { batch_id: bid, entries, total_count: entries.length },
    created_by: 'web_app',
    note: `一括登録: ${entries.length}件`,
  } as any);
}

export function sendUserMessage(message: string): Promise<InstructionResponse> {
  return createInstruction({
    instruction_type: 'user_message',
    target_entity_type: 'chat',
    target_entity_id: 'claude_activity',
    payload: { message, source: 'activity_panel' },
    created_by: 'web_app_chat',
  } as any);
}

export function approveEntry(entryId: string, context?: Record<string, any>): Promise<InstructionResponse> {
  // Stable dedupe_key: repeated rapid clicks or React Strict-Mode double invocation
  // resolve to the same pending instruction instead of spawning duplicates.
  return createInstruction({
    instruction_type: 'approve_entry',
    target_entity_type: 'entry',
    target_entity_id: entryId,
    payload: { entry_id: entryId, ...context },
    created_by: 'web_app_ui',
    dedupe_key: `approve:${entryId}`,
  } as any);
}

export function rejectEntry(entryId: string, reason?: string, context?: Record<string, any>): Promise<InstructionResponse> {
  return createInstruction({
    instruction_type: 'reject_entry_simple',
    target_entity_type: 'entry',
    target_entity_id: entryId,
    payload: { entry_id: entryId, reason: reason || null, ...context },
    created_by: 'web_app_ui',
    dedupe_key: `reject:${entryId}`,
  } as any);
}

// Programmatic dedupe check: given a list of ref_numbers (arica:{rowid}[-suffix]),
// returns which are already registered (with their freee_deal_id) and which are new.
export type InvoiceRegistrationCheck = {
  registered: Array<{ ref_number: string; freee_deal_id: number | null; status: string }>;
  available: string[];
};

export function checkRegisteredRefs(refNumbers: string[]): Promise<InvoiceRegistrationCheck> {
  return request<InvoiceRegistrationCheck>('/api/invoice-registrations/check', {
    method: 'POST',
    body: JSON.stringify({ ref_numbers: refNumbers }),
  });
}

// -- Deal proposals (user approval gate per MASTER_PIPELINE_RULES §0.1.1) --

export interface DealProposalDetail {
  accountItemId?: number;
  account_item_id?: number;
  taxCode?: number;
  tax_code?: number;
  sectionId?: number | null;
  section_id?: number | null;
  sectionName?: string;
  itemId?: number | null;
  item_id?: number | null;
  itemName?: string;
  amount: number;
  description?: string;
  tagIds?: number[];
}

export interface DealProposal {
  id: number;
  ref_number: string;
  partner_id: number;
  partner_name: string | null;
  issue_date: string | null;
  due_date: string | null;
  target_payment_month: string | null; // schema-v20: 'YYYY-MM' or null
  amount: number | null;
  pdf_rowid: string | null;
  status: 'proposed' | 'approved' | 'rejected' | 'registered';
  proposed_at: string;
  approved_at: string | null;
  approved_by: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  registered_at: string | null;
  freee_deal_id: number | null;
  proposed_deal_json: {
    partner_id: number;
    issue_date: string;
    due_date: string;
    ref_number: string;
    details: DealProposalDetail[];
    pdf_rowid?: string | number;
    idempotency_key?: string;
    flow_type?: string | null;
  };
  audit_summary: Record<string, any> | null;
  receipt_ids: number[] | null;
  notes: string | null;
  // Phase 4-A (2026-05-11): derived by worker from audit_summary / proposed_deal_json
  flow_type?: string | null;
}

// pagination loop: status × フィルタ条件 で全件取得 (limit=500/chunk、 worker hard cap 2000)
// 2026-05-08: 200 cap truncation bug fix (request param に limit 渡さず chunk 自動 loop)
const PROPOSALS_PAGE_SIZE = 500;
const PROPOSALS_MAX_PAGES = 20; // safety: 最大 10,000 件 / status (実運用は ~300/月)

async function fetchDealProposalsPaginated(
  baseQuery: URLSearchParams,
): Promise<{ items: DealProposal[]; count: number }> {
  const all: DealProposal[] = [];
  let offset = 0;
  for (let page = 0; page < PROPOSALS_MAX_PAGES; page++) {
    const q = new URLSearchParams(baseQuery);
    q.set('limit', String(PROPOSALS_PAGE_SIZE));
    q.set('offset', String(offset));
    const r = await request<{ items: DealProposal[]; count: number; total?: number; has_more?: boolean }>(
      `/api/deal-proposals?${q.toString()}`,
    );
    const items = r.items || [];
    all.push(...items);
    // 終了条件: has_more=false (新 worker) or items 0 件 or 取得済が total に到達
    const hasMore = r.has_more !== undefined
      ? r.has_more
      : items.length === PROPOSALS_PAGE_SIZE && (r.total === undefined || all.length < r.total);
    if (!hasMore || items.length === 0) break;
    offset += items.length;
  }
  return { items: all, count: all.length };
}

export function fetchDealProposals(
  status: string = 'proposed',
  options?: { flow_type?: string | null },
): Promise<{ items: DealProposal[]; count: number }> {
  const q = new URLSearchParams({ status });
  if (options?.flow_type) q.set('flow_type', options.flow_type);
  return fetchDealProposalsPaginated(q);
}

// Phase 4-A (2026-05-11): flow_type-scoped fetcher
//   flow_type='bank_auto_debit'         : 口座引落 のみ (= BankAutoDebitInvoicePage 用)
//   flow_type='exclude_bank_auto_debit' : 25日払い通常請求書のみ (= JournalVerificationPage 用)
export function fetchDealProposalsByFlowType(
  flowType: 'bank_auto_debit' | 'exclude_bank_auto_debit',
  status: string = 'proposed',
): Promise<{ items: DealProposal[]; count: number }> {
  const q = new URLSearchParams({ status, flow_type: flowType });
  return fetchDealProposalsPaginated(q);
}

// 月次ページ用: issue_month で絞り込み (legacy, 2026-04-22)
// DEPRECATED: 新 UI は fetchDealProposalsByTargetMonth を使う (schema-v20)
export function fetchDealProposalsByMonth(
  month: string,
  status: string = 'proposed',
): Promise<{ items: DealProposal[]; count: number }> {
  const q = new URLSearchParams({
    status,
    issue_month: month,
  });
  return fetchDealProposalsPaginated(q);
}

// 新 UI 用: target_payment_month (支払月) で絞り込み (schema-v20, 2026-04-22)
// Phase 4-A (2026-05-11): flow_type filter 引数追加
export function fetchDealProposalsByTargetMonth(
  targetMonth: string,
  status: string = 'proposed',
  options?: { flow_type?: string | null },
): Promise<{ items: DealProposal[]; count: number }> {
  const q = new URLSearchParams({
    status,
    target_payment_month: targetMonth,
  });
  if (options?.flow_type) q.set('flow_type', options.flow_type);
  return fetchDealProposalsPaginated(q);
}

// 月次サマリー (tab badge 用)
export interface MonthlySummary {
  month: string;
  arica: {
    total: number;
    proposed: number;
    approved: number;
    rejected: number;
    registered: number;
    total_amount: number;
  };
  fixed_recurring: {
    total: number;
    pending: number;
    registered: number;
    total_amount: number;
  };
  month_closed: boolean;
}

export function fetchMonthlySummary(month: string): Promise<MonthlySummary> {
  return request(`/api/monthly-summary?month=${encodeURIComponent(month)}`);
}

export function fetchMonthlySummaryRange(from: string, to: string): Promise<{ items: MonthlySummary[]; count: number }> {
  const q = new URLSearchParams({ from, to });
  return request(`/api/monthly-summary/range?${q.toString()}`);
}

// 2026-05-20 (G-step2): v5 bulk dispatch (= dual-reader + orchestrator)
//   server 側 endpoint /api/admin/invoice-multi-reader-extract-v42/bulk は alias 名のみ保持、
//   内部は v5 path (= runReadersV42 stub → runReadersV5 redirect)。
//   force_pipeline:'v5' を明示注入して将来の env override に影響されないようにする。
export function bulkDispatchV5(rowids: number[]): Promise<any> {
  return request(`/api/admin/invoice-multi-reader-extract-v42/bulk`, {
    method: 'POST',
    body: JSON.stringify({ rowids, partner_lookup: true, force_pipeline: 'v5' }),
  });
}

/** @deprecated 2026-05-20 G-step2: use bulkDispatchV5 instead. 既存 callsite 互換のため残置。 */
export const bulkDispatchV42 = bulkDispatchV5;

/**
 * approve 応答 (2026-07-10 auto_enroll): worker が auto_enroll 時に単票バッチへ enroll し
 * batch_id を返す。UI はこれを fetchBatchDetail で 5s ポーリングして行単位の
 * 「登録中→照合中→登録済み/失敗」リアルタイム進捗を出す。
 */
export interface ApproveDealProposalResponse {
  ok?: boolean;
  batch_id?: string;
  [k: string]: any;
}

/**
 * 承認 POST。
 * opts.auto_enroll (既定 true = 単体承認用): worker 側でバッチ完全性台帳へ自動 enroll し、
 * 応答に batch_id を含める。一括承認経路は自前で POST /api/batches するため
 * auto_enroll:false を渡して二重 enroll を防ぐ。
 */
export function approveDealProposal(
  refNumber: string,
  approvedBy?: string,
  opts?: { auto_enroll?: boolean },
): Promise<ApproveDealProposalResponse> {
  return request(`/api/deal-proposals/${encodeURIComponent(refNumber)}/approve`, {
    method: 'POST',
    body: JSON.stringify({
      approved_by: approvedBy || 'web_app_user',
      auto_enroll: opts?.auto_enroll !== false,
    }),
  });
}

export function rejectDealProposal(refNumber: string, reason?: string, rejectedBy?: string): Promise<any> {
  return request(`/api/deal-proposals/${encodeURIComponent(refNumber)}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason || null, rejected_by: rejectedBy || 'web_app_user' }),
  });
}

/**
 * Option A (2026-05-19): worker-side freee POST + mark-registered.
 * UI click 起点で deal_proposals.status='approved' → freee /api/1/deals POST →
 * mark-registered の 副作用 5 種 (= invoice_registrations / outcome_log / pattern_index /
 * partner_details_cache / status=registered) を worker 自前 OAuth で完結。
 * R2 [[feedback_verification_cleanup]] 準拠 (= UI click 起点)。
 */
export function registerProposalViaWorker(
  refNumber: string,
  sessionId: string,
  opts?: { force_register?: boolean; strict?: boolean },
): Promise<any> {
  return request(`/api/deal-proposals/${encodeURIComponent(refNumber)}/register-via-worker`, {
    method: 'POST',
    body: JSON.stringify({
      session_id: sessionId,
      force_register: opts?.force_register === true,
      strict: opts?.strict === true,
    }),
  });
}

/**
 * Backfill PDF receipts for registered deals (= 2026-05-22 緊急対応)
 * refs="all" → registered AND freee_receipt_id NULL の全件
 * 各 ref を 独立処理: ARICA PDF fetch → freee receipts upload → PUT deals receipt_ids → GET verify → D1 update
 */
export function backfillReceipts(
  sessionId: string,
  refs: string[] | 'all' = 'all',
  opts?: { dry_run?: boolean },
): Promise<any> {
  return request(`/api/deal-proposals/backfill-receipts`, {
    method: 'POST',
    body: JSON.stringify({
      session_id: sessionId,
      refs,
      dry_run: opts?.dry_run === true,
    }),
  });
}

// ---------------------------------------------------------------------------
// Fixed-Amount Recurring bulk-register (schema-v19, 2026-04-22)
// ---------------------------------------------------------------------------
export interface FixedRecurringPartner {
  id: number;
  partner_id: number;
  partner_name: string;
  template_json: any;
  monthly_amount: number;
  amount_stddev: number | null;
  issue_day_pattern: string;
  due_day_pattern: string;
  confidence: number;
  receipt_presence_ratio: number | null;
  enabled: number;
  source: 'user_specified' | 'dynamic_discovery';
  last_registered_month: string | null;
  evidence_json: any;
  created_at: string;
  updated_at: string;
}

export interface FixedRecurringPreview {
  partner_id: number;
  partner_name: string;
  monthly_amount: number;
  amount_stddev: number | null;
  confidence: number;
  source: string;
  issue_day_pattern: string;
  due_day_pattern: string;
  ref_number: string;
  issue_date: string;
  due_date: string;
  target_payment_month: string | null; // schema-v20
  proposed_deal_json: any;
  already_registered_month: boolean;
  existing_freee_deal_id: number | null;
  existing_proposal_status: string | null;
  last_registered_month?: string | null;
  receipt_presence_ratio?: number | null;
  evidence_json?: any;
  enabled?: number;
}

export interface FixedRecurringBulkResult {
  partner_id: number;
  partner_name?: string;
  status: 'queued' | 'skipped' | 'failed';
  reason?: string;
  ref_number?: string;
  issue_date?: string;
  due_date?: string;
  amount?: number;
  freee_deal_id?: number;
}

export function fetchFixedRecurringPartners(): Promise<{ items: FixedRecurringPartner[]; count: number }> {
  return request('/api/fixed-recurring/partners');
}

export function toggleFixedRecurringPartner(id: number, enabled: boolean): Promise<any> {
  return request(`/api/fixed-recurring/partners/${id}`, {
    method: 'POST',
    body: JSON.stringify({ enabled: enabled ? 1 : 0 }),
  });
}

export function previewFixedRecurring(targetMonth: string, partnerIds?: number[]): Promise<{
  target_month: string; count: number; previews: FixedRecurringPreview[];
}> {
  return request('/api/fixed-recurring/preview', {
    method: 'POST',
    body: JSON.stringify({ target_month: targetMonth, partner_ids: partnerIds }),
  });
}

export function bulkRegisterFixedRecurring(
  sessionId: string,
  targetMonth: string,
  partnerIds: number[],
): Promise<{
  target_month: string;
  total: number;
  queued: number;
  skipped: number;
  failed: number;
  results: FixedRecurringBulkResult[];
}> {
  return request('/api/fixed-recurring/bulk-register', {
    method: 'POST',
    body: JSON.stringify({ session_id: sessionId, target_month: targetMonth, partner_ids: partnerIds }),
  });
}

export function openSessionForFixedRecurring(): Promise<{ session_id: string; audit_ok_until: string }> {
  return request('/api/claude/session/open', {
    method: 'POST',
    body: JSON.stringify({ workspace_id: 'kumagai-corp' }),
  });
}

// POST /api/fixed-recurring/partners/:id/skip-month?month=YYYY-MM
// fixed_amount_recurring_partners.last_registered_month = month に上書き (当月処理済扱い)
export function skipFixedRecurringMonth(partnerId: number, month: string): Promise<{
  partner_id: number; skipped_month: string; updated: number;
}> {
  return request(`/api/fixed-recurring/partners/${partnerId}/skip-month?month=${encodeURIComponent(month)}`, {
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// Bank Processing (schema-v21/v22, Phase 2 — 2026-04-22)
// ---------------------------------------------------------------------------

export type BankProcessingStatus =
  | 'pending' | 'queued' | 'processing' | 'analyzing' | 'suggested' | 'needs_review'
  | 'mechanical_suggest_stage1' | 'mechanical_reconcile_stage2'
  | 'approved' | 'registered' | 'skipped' | 'error';

export type BankFlowType =
  | 'bank_auto_debit' | 'bank_transfer_in' | 'transfer' | 'card_payment' | 'unknown';

export interface AutoRegisterRuleBrief {
  id: number | null;
  status: 'active' | 'pending_confirmation' | 'superseded' | 'manual_confirmed' | null;
  freee_rule_id: number | null;
}

// ---------------------------------------------------------------------------
// 2026-07-14: 根拠 evidence_sources (worker 新契約 — bankNormalizeEvidenceSources sanitize 済み)
//   取得場所: GET /api/bank-wallet-txns/:id → processing.evidence_sources (array | null)。
//   null = 旧形式 (根拠構造化前) → UI は reconcile_judge_reason 表示に fallback する。
//   要素は server 側で whitelist / 長さ上限 / 最大12件 sanitize 済み (不正要素は破棄され、
//   有効0件なら key 自体が無い)。正本は wallet_txn_processing.audit_summary JSON 内
//   key 'evidence_sources' (deal_proposals.audit_summary mirror にも同梱)。
// ---------------------------------------------------------------------------

export type BankEvidenceSourceType =
  | 'arica_purchase' | 'arica_statement' | 'past_deal'
  | 'learned_pattern' | 'freee_master' | 'none';

export type BankEvidenceUsedFor =
  | 'partner' | 'account' | 'section' | 'amount' | 'tax' | 'item' | 'split';

export interface BankEvidenceSource {
  /** 情報源種別 (server whitelist 強制) */
  source_type: BankEvidenceSourceType;
  /** ARICA family 名 (≤40)。'none' でも「探した family」が入り得る */
  family: string | null;
  /** 注文番号 / ARICA rowid / 契約id / token 等 (≤120) */
  ref: string | null;
  /** past_deal 時の参照 freee deal id (数値化不能は null) */
  deal_id: number | null;
  /** learned_pattern 時のキー (≤120, 例 'vendor_default_section:貴千') */
  pattern_key: string | null;
  /** 何が一致したか (≤300)。source_type='none' は常に null */
  matched: string | null;
  /** 根拠が使われた対象 (dedupe 済・[] あり得る) */
  used_for: BankEvidenceUsedFor[];
  /** 人間向け日本語1行サマリ (必須・1〜300字) — 表示の主役 */
  ja_summary: string;
}

export interface BankProcessingInfo {
  ref_number: string;
  processing_status: BankProcessingStatus;
  flow_type: BankFlowType;
  partner_id: number | null;
  partner_name: string | null;
  pattern_source: string | null;
  pattern_confidence: number | null;
  readers_passed: number;
  reconcile_verdict: string | null;
  reconcile_judge_reason?: string | null;
  missing_evidence?: any;
  reader_a_result?: any;
  reader_b_result?: any;
  reader_c_result?: any;
  proposed_deal_json?: any;
  audit_summary?: any;
  proposal_id: number | null;
  freee_deal_id: number | null;
  registered_at: string | null;
  last_updated: string | null;
  session_id?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  error_message?: string | null;
  instruction_id?: string | null;
  auto_register_rule: AutoRegisterRuleBrief | null;
  // 2026-07-13: 消込副状態 (worker側で追加予定 — 応答に無くても UI は壊れない防御的 optional)
  //   'reconcile_pending' = 消込待ち(自動回復中) / 'freee_fee_drift' = 差額要確認
  reconcile_state?: string | null;
  // 2026-07-14 確定設計 §3: 証憑 vision 結果 (detail GET 同載 — 応答に無くても UI は壊れない)
  receipt_vision?: BankReceiptVisionResult[] | null;
  // 2026-07-14: 根拠 evidence_sources (detail GET のみ同載。null = 旧形式 →
  //   UI は reconcile_judge_reason の従来表示に fallback する防御的 optional)
  evidence_sources?: BankEvidenceSource[] | null;
}

// 2026-04-23 Reader B: 3-stage partner inference
export interface ResolvedPartner {
  partner_id: number | null;
  partner_name: string | null;
  source: 'processing' | 'none';
}

export interface SuggestedPartnerEvidence {
  rule_type: 'bank_flow_partner_registry' | 'auto_register_rule_cache' | 'description_token_history';
  match_token?: string | null;
  flow_type?: string | null;
  rule_id?: number | null;
  freee_rule_id?: number | null;
  token_key?: string | null;
  observed_hits?: number | null;
}

export interface SuggestedPartner {
  name: string | null;
  partner_id: number | null;
  source: 'A' | 'B' | 'C';
  confidence: number;
  evidence: SuggestedPartnerEvidence;
}

export interface FreeeAutoHint {
  detectable: boolean;
  rule_matched: boolean;
  note: string | null;
}

export interface BankWalletTxnRow {
  wallet_txn_id: number;
  walletable_id: number;
  walletable_type: 'bank_account' | 'credit_card' | 'wallet';
  date: string;
  entry_side: 'income' | 'expense';
  amount: number;
  balance: number | null;
  description: string | null;
  due_amount: number | null;
  status_raw: 1 | 2;
  rule_matched: boolean;
  synced_at: string;
  last_changed_at: string | null;
  change_count: number;
  processing: BankProcessingInfo | null;
  // 2026-04-23 Reader B additive fields
  resolved_partner?: ResolvedPartner;
  suggested_partner?: SuggestedPartner | null;
  freee_auto_hint?: FreeeAutoHint;
  // 2026-07-13: 消込副状態 (top-level で返す実装になっても拾えるようにする防御的 fallback)
  reconcile_state?: string | null;
}

export interface BankWalletTxnListResponse {
  items: BankWalletTxnRow[];
  count: number;
  total: number;
  next_offset: number | null;
  // 2026-07-13: すべて optional (worker の応答に無いキーがあっても UI は壊れない防御的実装。
  // ページ側は local 集計 fallback とマージして undefined 表示を防ぐ)
  chip_counts?: {
    all?: number;
    needs_review?: number;
    error?: number;
    pending?: number;
    analyzing?: number;
    mechanical_suggest?: number;
    mechanical_reconcile?: number;
    rule_waiting?: number;
    registered?: number;
    approved_unregistered?: number;
  };
}

// 2026-04-22 Bug-2: expanded sort order enum exposed by worker
// 2026-04-23 Reader A: income / expense columns split (independent sort keys)
export type BankWalletTxnsListOrder =
  | 'date_desc' | 'date_asc'
  | 'amount_desc' | 'amount_asc'
  | 'balance_desc' | 'balance_asc'
  | 'updated_at_desc' | 'updated_at_asc'
  | 'fetched_at_desc' | 'fetched_at_asc'
  | 'status_desc' | 'status_asc'
  | 'account_desc' | 'account_asc'
  | 'income_desc' | 'income_asc'
  | 'expense_desc' | 'expense_asc';

export interface BankSummary {
  range: { start: string | null; end: string | null };
  totals: Record<string, number> & { wallet_txn_count: number };
  by_walletable: Array<{
    walletable_id: number; walletable_type: string; name: string | null;
    total: number; unprocessed: number; processed: number;
    rule_matched_count: number; latest_date: string | null;
    earliest_date: string | null; last_sync_at: string | null;
  }>;
  by_flow_type: Record<string, Record<string, number>>;
  walletables_total: number;
}

export interface BankWalletTxnDetail extends BankWalletTxnRow {
  description_token_key: string | null;
  auto_register_rule: any;
}

export interface BankAuditEvent {
  ts: string;
  from: string | null;
  to: string;
  by: string;
  reason: string | null;
  metadata: any;
}

export interface BankFreeeDealView {
  wallet_txn_id: number;
  ref_number: string | null;
  freee_deal_id: number | null;
  partner_id: number | null;
  partner_name: string | null;
  registered_at: string | null;
  deal: any;
  audit_summary: any;
  reader_a_result: any;
  reader_b_result: any;
  reconcile_verdict: string | null;
  invoice_registration: any | null;
  external_link: string | null;
  // 2026-04-22 Bug-1: freee marked the wallet_txn registered (status_raw=2) but
  // FREEEMAN reconciliation has not yet populated freee_deal_id. UI should surface a
  // "Reconcile required" notice with a manual Retry button (user-triggered).
  not_reconciled?: boolean;
  hint?: string;
  reconcile_hint?: { walletable_id: number; date: string } | null;
  processing_status?: string;
  // 2026-08-20: 自社口座間振替はfreee側でdealを作らないため not_reconciled の
  // 「未同期」判定が恒久的に成立してしまう(reconcile-freee-dealsを実行しても常にunmatched)。
  // その場合はこちらが true になり not_reconciled は false で返る。
  transfer_confirmed?: boolean;
  transfer_note?: string;
}

export async function fetchBankWalletTxns(p: {
  walletable_id?: number; walletable_type?: string;
  status_raw?: 1 | 2; rule_matched?: 0 | 1;
  start_date?: string; end_date?: string;
  processing_status?: string; flow_type?: string; partner_id?: number;
  search?: string; order?: BankWalletTxnsListOrder;
  limit?: number; offset?: number;
} = {}): Promise<BankWalletTxnListResponse> {
  const q = new URLSearchParams();
  if (p.walletable_id) q.set('walletable_id', String(p.walletable_id));
  if (p.walletable_type) q.set('walletable_type', p.walletable_type);
  if (p.status_raw !== undefined) q.set('status_raw', String(p.status_raw));
  if (p.rule_matched !== undefined) q.set('rule_matched', String(p.rule_matched));
  if (p.start_date) q.set('start_date', p.start_date);
  if (p.end_date) q.set('end_date', p.end_date);
  if (p.processing_status) q.set('processing_status', p.processing_status);
  if (p.flow_type) q.set('flow_type', p.flow_type);
  if (p.partner_id) q.set('partner_id', String(p.partner_id));
  if (p.search) q.set('search', p.search);
  if (p.order) q.set('order', p.order);
  q.set('limit', String(p.limit || 200));
  q.set('offset', String(p.offset || 0));
  return request<BankWalletTxnListResponse>(`/api/bank-wallet-txns?${q}`);
}

export async function fetchBankSummary(start?: string, end?: string): Promise<BankSummary> {
  const q = new URLSearchParams();
  if (start) q.set('start_date', start);
  if (end) q.set('end_date', end);
  const qs = q.toString();
  return request<BankSummary>(`/api/bank-wallet-txns/summary${qs ? '?' + qs : ''}`);
}

export async function fetchBankWalletTxnDetail(id: number): Promise<BankWalletTxnDetail> {
  return request<BankWalletTxnDetail>(`/api/bank-wallet-txns/${id}`);
}

export async function fetchBankFreeeDeal(id: number): Promise<BankFreeeDealView> {
  return request<BankFreeeDealView>(`/api/bank-wallet-txns/${id}/freee-deal`);
}

export async function fetchBankAuditLog(id: number): Promise<{ wallet_txn_id: number; events: BankAuditEvent[] }> {
  return request(`/api/bank-wallet-txns/${id}/audit-log`);
}

export async function enqueueBankAnalysis(id: number, sessionId: string, minReaderCount?: number): Promise<{
  wallet_txn_id: number; ref_number: string; processing_status: string;
  instruction_id: string; dispatched_to: string; min_reader_count: number;
}> {
  return request(`/api/bank-wallet-txns/${id}/enqueue-analysis`, {
    method: 'POST',
    body: JSON.stringify({ session_id: sessionId, min_reader_count: minReaderCount }),
  });
}

export async function approveBankWalletTxn(
  id: number, approvedBy: string, sessionId?: string, modifications?: any,
  // 2026-07-14 部門接地設計 v1 (A1/A2): 部門未確定行を「不明のまま登録」する明示 ack。
  // worker W8 (bank_section_gate) が honor する forward-compatible field — 現行 worker は無視する
  // だけなので送っても無害。UI 側は ack なしの未確定承認を自前で拒否する。
  // 2026-09-19 W13 (wt 2451538138 Amazon incident): overrideReconcileDisagree =
  //   「部門だけ未確定で disagree」の行を、AI の指摘を読んだうえで人間が部門を確定して
  //   承認する明示 ack。worker 側は部門以外が欠けた行では ack があっても 409 のまま。
  //   一括承認はこの ack を送らない = 一括では disagree 行を越えられない (設計)。
  opts?: { allowSectionUnknown?: boolean; overrideReconcileDisagree?: boolean },
): Promise<any> {
  return request(`/api/bank-wallet-txns/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({
      approved_by: approvedBy,
      session_id: sessionId,
      modifications,
      ...(opts?.allowSectionUnknown ? { allow_section_unknown: true } : {}),
      ...(opts?.overrideReconcileDisagree ? { override_reconcile_disagree: true } : {}),
    }),
  });
}

// ---------------------------------------------------------------------------
// POST /api/learn — 学習書込の正規経路 (worker handleLearn / D1 正本)。
//   kind='vendor_default_section': 業者名 (部分一致キー) → 既定部門。
//   worker 側で sections_cache canonical 解決 + ops_flags 'invoice_vendor_default_sections'
//   JSON merge + rules(domain='vendor_default') 即時ミラー。冪等 (同値再POSTは changed:false)。
//   D1 のみ・freee 書込なし (R2 非抵触)・partner 自動作成なし (R3 非抵触)。
// ---------------------------------------------------------------------------
export interface LearnVendorDefaultSectionResult {
  ok: boolean;
  kind: string;
  changed: boolean;
  vendor_key: string;
  section_id: number;
  section_name: string;
  prev_section_name: string | null;
  rule_id: string | null;
}

export async function learnVendorDefaultSection(p: {
  vendorKey: string;
  section: number | string; // section_id or canonical 部門名
  instructionExcerpt: string; // provenance 必須 (でっち上げ学習の防止、4文字以上)
  by?: string;
}): Promise<LearnVendorDefaultSectionResult> {
  return request<LearnVendorDefaultSectionResult>('/api/learn', {
    method: 'POST',
    body: JSON.stringify({
      kind: 'vendor_default_section',
      payload: { vendor_key: p.vendorKey, section: p.section },
      provenance: { by: p.by || 'web_app_user', instruction_excerpt: p.instructionExcerpt },
    }),
  });
}

export async function rejectBankWalletTxn(id: number, reason: string, rejectedBy: string): Promise<any> {
  return request(`/api/bank-wallet-txns/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ rejected_by: rejectedBy, reason }),
  });
}

// ---------------------------------------------------------------------------
// 学習機構 metrics (= schema-v33 admin endpoints)
// ---------------------------------------------------------------------------

export interface LearningMetricsSummary {
  ai_skip_rate_recent: number | null;
  ai_dispatched_recent: number;
  mechanical_decisions_recent: number;
  total_stage_decisions_recent: number;
  token_saved_recent: number;
  token_cost_saved_usd_recent: string;
}

export interface LearningMetricsCoverage {
  total_registered?: number;
  with_learning_event?: number;
  with_pattern_log?: number;
}

export interface LearningMetricsAuditViolations {
  rule50_skips?: number;
  rule51_skips?: number;
}

export interface LearningMetricsPatternStats {
  total_patterns?: number;
  high_conf?: number;
  mid_conf?: number;
  robust?: number;
  avg_conf?: number;
  total_approved?: number;
  total_rejected?: number;
  total_modified?: number;
}

export interface LearningMetrics {
  ok: boolean;
  days: number;
  generated_at: string;
  summary: LearningMetricsSummary;
  coverage: LearningMetricsCoverage;
  audit_violations: LearningMetricsAuditViolations;
  pattern_stats: LearningMetricsPatternStats;
  stage_decisions: any[];
  event_log_breakdown: any[];
}

export async function fetchLearningMetrics(days: number = 30): Promise<LearningMetrics> {
  return request<LearningMetrics>(`/api/admin/learning-metrics?days=${days}`);
}

export interface LearningPattern {
  description_token_key: string;
  walletable_id: number;
  entry_side: 'income' | 'expense';
  amount_band: 'micro' | 'small' | 'medium' | 'large';
  best_partner_id: number | null;
  best_partner_name: string | null;
  ai_confidence: number;
  sample_count: number;
  approved_count: number;
  rejected_count: number;
  modified_count: number;
  last_confirmed_at: string | null;
  last_rebuilt_at: string;
}

export async function fetchLearningTopPatterns(limit: number = 20, minSamples: number = 1): Promise<{
  ok: boolean;
  count: number;
  patterns: LearningPattern[];
}> {
  return request(`/api/admin/learning-top-patterns?limit=${limit}&min_samples=${minSamples}`);
}

export interface LearningQualityIssue {
  description_token_key: string;
  walletable_id: number;
  entry_side: string;
  amount_band: string;
  best_partner_name: string | null;
  sample_count: number;
  approved_count?: number;
  rejected_count?: number;
  modified_count?: number;
  ai_confidence: number;
  reject_rate?: number;
  last_confirmed_at: string | null;
  last_rebuilt_at?: string;
}

export async function fetchLearningQualityIssues(): Promise<{
  ok: boolean;
  high_rejection_rate: LearningQualityIssue[];
  stale_patterns: LearningQualityIssue[];
  low_confidence_high_sample: LearningQualityIssue[];
}> {
  return request(`/api/admin/learning-quality-issues`);
}

export interface ChatLearningStructured {
  mode: 'structured';
  token: string;
  walletable_id: number;
  entry_side: 'income' | 'expense';
  amount_band: 'micro' | 'small' | 'medium' | 'large';
  partner_id?: number | null;
  partner_name?: string | null;
  section_id?: number | null;
  account_item_id?: number | null;
  item_id?: number | null;
  tax_code?: number | null;
  user_id?: string;
}

export interface ChatLearningFreeText {
  mode: 'free_text';
  text: string;
  user_id?: string;
  walletable_id?: number;
}

export async function postLearningChatIngest(
  body: ChatLearningStructured | ChatLearningFreeText,
): Promise<any> {
  return request(`/api/admin/learning-chat-ingest`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function postMonthlyPatternRebuild(): Promise<{
  ok: boolean;
  elapsed_ms: number;
  rebuilt_count: number;
  pre: { total: number; avg_conf: number };
  post: { total: number; avg_conf: number; high_conf: number };
}> {
  return request(`/api/cron/monthly-pattern-rebuild`, { method: 'POST' });
}

// ---------------------------------------------------------------------------
// 請求書 学習機構 UI phase 1 (= schema-v37/v38, 2026-05-08)
// ---------------------------------------------------------------------------

export interface InvoiceLearningMissingPartner {
  partner_id: number;
  partner_name: string | null;
  deal_count: number;
}

export interface InvoiceLearningMetrics {
  ok: boolean;
  days: number;
  generated_at: string;
  arica: {
    total: number;
    registered: number;
    unprocessed: number;
    null_issue: number;
    registered_rate: number | null;
  };
  cadence: {
    high: number;
    medium: number;
    low: number;
    irregular: number;
    total: number;
  };
  curated_patterns: {
    total_partners_in_md: number;
    total_in_d1: number;
    trusted_in_index: number;
    missing_in_index: number;
    total_in_index: number;
    missing_partners_with_high_deals: InvoiceLearningMissingPartner[];
  };
  duplicate_detection: {
    fired_30d: number;
    by_source: { arica: number; filebox: number; manual: number };
  };
  outcome_log: {
    total: number;
    approved_as_is: number;
    approved_with_modification: number;
    rejected: number;
    no_baseline?: number;
    // v39 (Codex F2): Recommend 提案率 / 採用率 / 完全一致率 を分離
    prefill_emitted_count?: number;
    prefill_used_count: number;
    prefill_match_count?: number;
    prefill_emitted_rate?: number | null;
    prefill_used_rate: number | null;
    prefill_match_rate?: number | null;
  };
}

export async function fetchInvoiceLearningMetrics(days: number = 30): Promise<InvoiceLearningMetrics> {
  return request<InvoiceLearningMetrics>(`/api/admin/invoice-learning-metrics?days=${days}`);
}

export interface PartnerCadenceItem {
  partner_id: number;
  partner_name: string | null;
  issue_day_pattern: string;
  issue_anchor_day: number | null;
  due_day_pattern: string;
  due_anchor_day: number | null;
  confidence: number;
  sample_size: number;
  mode_ratio: number | null;
  last_reviewed_at: string;
  manual_override: number;
}

export async function fetchInvoicePartnerCadences(status: 'high' | 'medium' | 'low' | 'irregular' | 'all' = 'all'): Promise<{
  ok: boolean;
  status_filter: string;
  count: number;
  cadences: PartnerCadenceItem[];
}> {
  return request(`/api/admin/partner-cadences?status=${status}`);
}

export interface InvoiceQualityIssues {
  ok: boolean;
  generated_at: string;
  stuck_arica_proposals: any[];
  missing_receipt_links: any[];
  missing_audit_summary: any[];
  low_confidence_cadences: any[];
}

export async function fetchInvoiceQualityIssues(): Promise<InvoiceQualityIssues> {
  return request(`/api/admin/invoice-quality-issues`);
}

// ---------------------------------------------------------------------------
// Team N (2026-05-11): 業者別 学習状況 dashboard
// ---------------------------------------------------------------------------

export interface PartnerLearningTemplate {
  id: number;
  layout_fingerprint: string | null;
  amount_paradigm: string | null;
  template_status: 'draft' | 'promoted' | 'demoted' | string;
  sample_count: number;
  approved_count: number;
  rejected_count: number;
  modified_count: number;
  ai_confidence: number;
  source: string | null;
  pdf_producer: string | null;
  pdf_page_size: string | null;
  section_split_method: string | null;
  first_observed_at: string | null;
  last_confirmed_at: string | null;
  promoted_at: string | null;
  demoted_at: string | null;
  last_drift_detected_at: string | null;
}

export interface PartnerLearningCurated {
  id: number;
  default_account_item_id: number | null;
  default_account_item_name: string | null;
  default_section_id: number | null;
  default_section_name: string | null;
  default_item_id: number | null;
  default_tax_code: number | null;
}

export interface PartnerLearningCanonicalMatch {
  dominant_account_item_id: number | null;
  dominant_tax_code: number | null;
  expected_account_item_id: number | null;
  expected_tax_code: number | null;
  match_rate: number | null;
}

export interface PartnerLearningCadence {
  confidence: number | null;
  sample_size: number | null;
}

export interface PartnerLearningRow {
  partner_id: number;
  partner_name: string;
  pattern_status: string | null;
  category_hint: string | null;
  deal_count: number;
  cadence: PartnerLearningCadence;
  template: PartnerLearningTemplate | null;
  curated: PartnerLearningCurated | null;
  canonical_match: PartnerLearningCanonicalMatch;
}

export interface ReaderPerformance {
  reader_type: string;
  total_runs: number;
  post_verified_correct: number;
  post_verified_wrong: number;
  current_weight: number;
  last_updated: string | null;
}

export interface PartnerLearningKpi {
  total_partners: number;
  with_template: number;
  promoted: number;
  draft: number;
  demoted: number;
  food_vendor: number;
  utility: number;
  repair: number;
  curated_present: number;
  avg_canonical_match: number | null;
  avg_ai_confidence: number | null;
}

export interface PartnerLearningStatusResponse {
  ok: boolean;
  generated_at: string;
  filters: { category: string; status: string; sort: string; order: string };
  pagination: { total: number; limit: number; offset: number; returned: number };
  kpi: PartnerLearningKpi;
  reader_performance: ReaderPerformance[];
  partners: PartnerLearningRow[];
}

export interface PartnerLearningStatusParams {
  category?: string;
  status?: 'promoted' | 'draft' | 'demoted' | 'no_template' | 'all';
  sort?: 'deal_count' | 'confidence' | 'sample' | 'name';
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export async function fetchPartnerLearningStatus(
  params: PartnerLearningStatusParams = {},
): Promise<PartnerLearningStatusResponse> {
  const q = new URLSearchParams();
  if (params.category) q.set('category', params.category);
  if (params.status) q.set('status', params.status);
  if (params.sort) q.set('sort', params.sort);
  if (params.order) q.set('order', params.order);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.offset) q.set('offset', String(params.offset));
  return request(`/api/admin/partner-learning-status?${q}`);
}

export interface PartnerLearningTimelineEvent {
  at: string;
  type: string;
  template_id?: number;
  detail?: string;
}

export interface PartnerLearningDealSample {
  deal_id: number;
  issue_date: string | null;
  amount: number | null;
  account_item_id: number | null;
  account_item_name: string | null;
  tax_code: number | null;
  section_id: number | null;
  section_long_name: string | null;
  item_id: number | null;
  item_name: string | null;
  description: string | null;
  entry_side: string | null;
  vat: number | null;
}

export interface PartnerLearningProposalSample {
  id: number;
  ref_number: string;
  issue_date: string | null;
  due_date: string | null;
  amount: number | null;
  status: string;
  proposed_at: string | null;
  approved_at: string | null;
  registered_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  freee_deal_id: number | null;
  audit_summary: string | null;
}

export interface PartnerLearningReceipt {
  arica_rowid: number;
  receive_date: string;
  processing_status: string;
  ref_number: string | null;
  amount_pdf: number | null;
  issue_date_pdf: string | null;
  due_date_expected: string | null;
  document_type: string | null;
  reason: string | null;
  last_updated: string;
  proposal_id: number | null;
}

export interface PartnerLearningAudit {
  arica_rowid: number;
  audit_result: 'pass' | 'warn' | 'fail' | 'skip' | string;
  audit_score: number;
  issues_json: string | null;
  expected_account_item_id: number | null;
  expected_tax_code: number | null;
  partner_category: string | null;
  multi_store_split_required: number;
  audited_at: string;
}

export interface PartnerLearningOutcome {
  id: number;
  proposal_id: number | null;
  arica_rowid: number | null;
  outcome: string;
  prefill_emitted: number;
  prefill_used: number;
  prefill_match: number | null;
  modified_fields: string | null;
  recommend_source: string | null;
  recommend_confidence: number | null;
  final_account_item_id: number | null;
  final_tax_code: number | null;
  created_at: string;
}

export interface PartnerLearningDetail {
  ok: boolean;
  partner_id: number;
  partner: any;
  curated: any;
  templates: PartnerLearningTemplate[];
  cadence: any;
  paradigm_learning: {
    preferred_paradigm: string | null;
    detail_sum_correct: number;
    pdf_label_correct: number;
    mismatch_count: number;
    confidence: number;
    sample_size: number;
  };
  deal_samples: PartnerLearningDealSample[];
  recent_proposals: PartnerLearningProposalSample[];
  recent_receipts: PartnerLearningReceipt[];
  recent_audits: PartnerLearningAudit[];
  drifts: any[];
  outcome_trail: any[];
  outcomes: PartnerLearningOutcome[];
  timeline: PartnerLearningTimelineEvent[];
  hook_events?: PartnerLearningHookEvent[];
  hook_stats?: PartnerLearningHookStat[];
  conflicts?: PartnerLearningConflict[];
}

export interface PartnerLearningHookEvent {
  id: number;
  event_type: string;
  source_path: string | null;
  outcome: string | null;
  payload_json: string | null;
  created_at: string;
}

export interface PartnerLearningHookStat {
  event_type: string;
  source_path: string | null;
  n: number;
}

export interface PartnerLearningConflict {
  id: number;
  partner_id: number;
  conflict_field: string;
  hook_a_source: string;
  hook_a_value: string | null;
  hook_a_observed_at: string | null;
  hook_b_source: string;
  hook_b_value: string | null;
  hook_b_observed_at: string | null;
  detected_at: string;
  resolution: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  notes: string | null;
}

export async function fetchPartnerLearningDetail(partnerId: number): Promise<PartnerLearningDetail> {
  return request(`/api/admin/partner-learning-detail/${partnerId}`);
}

// Phase 4-B (2026-05-11): 学習機構 多 timing UI endpoints
export interface LearningEvent {
  id: number;
  wallet_txn_id: number;
  event_type: string;
  source_path: string | null;
  outcome: string | null;
  partner_id: number | null;
  description_token_key: string | null;
  pattern_index_updated: number;
  pattern_learning_log_id: number | null;
  session_id: string | null;
  payload_json: string | null;
  created_at: string;
}

export interface LearningEventBreakdown {
  event_type: string;
  source_path: string | null;
  n: number;
}

export interface LearningEventsResponse {
  ok: boolean;
  count: number;
  events: LearningEvent[];
  breakdown: LearningEventBreakdown[];
  filters: any;
}

export async function fetchLearningEvents(params: {
  source?: string;
  since?: string;
  partner_id?: number;
  event_type?: string;
  limit?: number;
} = {}): Promise<LearningEventsResponse> {
  const q = new URLSearchParams();
  if (params.source) q.set('source', params.source);
  if (params.since) q.set('since', params.since);
  if (params.partner_id) q.set('partner_id', String(params.partner_id));
  if (params.event_type) q.set('event_type', params.event_type);
  if (params.limit) q.set('limit', String(params.limit));
  return request(`/api/admin/learning-events?${q}`);
}

export interface LearningConflict {
  id: number;
  partner_id: number;
  partner_name?: string;
  conflict_field: string;
  hook_a_source: string;
  hook_a_value: string | null;
  hook_a_observed_at: string | null;
  hook_b_source: string;
  hook_b_value: string | null;
  hook_b_observed_at: string | null;
  detected_at: string;
  resolution: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  notes: string | null;
}

export interface LearningConflictsResponse {
  ok: boolean;
  count: number;
  conflicts: LearningConflict[];
  stats: {
    total?: number;
    unresolved?: number;
    resolved?: number;
    partners_with_conflicts?: number;
  };
}

export async function fetchLearningConflicts(params: {
  partner_id?: number;
  resolved?: 'true' | 'false';
  limit?: number;
} = {}): Promise<LearningConflictsResponse> {
  const q = new URLSearchParams();
  if (params.partner_id) q.set('partner_id', String(params.partner_id));
  if (params.resolved) q.set('resolved', params.resolved);
  if (params.limit) q.set('limit', String(params.limit));
  return request(`/api/admin/learning-conflicts?${q}`);
}

export async function resolveLearningConflict(body: {
  conflict_id: number;
  resolution: 'a_wins' | 'b_wins' | 'manual_override';
  notes?: string;
  resolved_by?: string;
}): Promise<any> {
  return request(`/api/admin/learning-conflicts/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export interface LearningIntegrityIssue {
  field: string;
  learned: any;
  learned_name?: string;
  deal_canonical: any;
  deal_canonical_name?: string;
  deal_frequency: number;
  severity: 'warn' | 'info';
}

export interface LearningIntegrityResponse {
  ok: boolean;
  partner_id: number;
  curated: any;
  deal_canonical: {
    account_items: any[];
    sections: any[];
    tax_codes: any[];
  };
  integrity_issues: LearningIntegrityIssue[];
  total_issues: number;
  verdict: 'consistent' | 'inconsistent_warn' | 'minor_drift';
}

export async function fetchLearningIntegrityCheck(partnerId: number): Promise<LearningIntegrityResponse> {
  return request(`/api/admin/learning-integrity-check?partner_id=${partnerId}`);
}

// ---------------------------------------------------------------------------
// freee 証憑 (receipts) 添付 (= 2026-05-08, schema-v33+)
// ---------------------------------------------------------------------------

export interface FreeeReceipt {
  id: number;
  status?: string;
  description?: string | null;
  issue_date?: string | null;
  file_src?: string | null;
  origin?: string | null;
  created_at?: string;
}

export async function fetchFreeeReceipts(params: {
  category?: 'without_deal' | 'with_deal' | 'all';
  start_date?: string;
  end_date?: string;
  description?: string;
  limit?: number;
} = {}): Promise<{ ok: boolean; receipts: FreeeReceipt[]; count: number }> {
  const q = new URLSearchParams();
  q.set('category', params.category || 'without_deal');
  if (params.start_date) q.set('start_date', params.start_date);
  if (params.end_date) q.set('end_date', params.end_date);
  if (params.description) q.set('description', params.description);
  q.set('limit', String(params.limit || 50));
  return request(`/api/freee-public/receipts?${q}`);
}

export async function uploadFreeeReceipt(file: File, opts: {
  description?: string;
  issue_date?: string;
} = {}): Promise<{ ok: boolean; receipt_id: number; file_src: string | null; receipt: FreeeReceipt }> {
  const fd = new FormData();
  fd.append('file', file, file.name);
  if (opts.description) fd.append('description', opts.description);
  if (opts.issue_date) fd.append('issue_date', opts.issue_date);
  // request() は JSON body 前提なので multipart は手動 fetch
  const res = await fetch(`${API_BASE}/api/freee-public/receipts/upload`, {
    method: 'POST',
    body: fd,
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`upload failed ${res.status}: ${txt.slice(0, 200)}`);
  }
  return await res.json();
}

export async function attachReceiptsToWalletTxn(walletTxnId: number, receiptIds: number[]): Promise<{
  ok: boolean;
  wallet_txn_id: number;
  receipt_ids: number[];
  added: number[];
}> {
  return request(`/api/freee-public/wallet-txn-attach-receipt`, {
    method: 'POST',
    body: JSON.stringify({ wallet_txn_id: walletTxnId, receipt_ids: receiptIds }),
  });
}

export async function detachReceiptFromWalletTxn(walletTxnId: number, receiptId: number): Promise<{
  ok: boolean;
  wallet_txn_id: number;
  removed: number;
  remaining_receipt_ids: number[];
}> {
  return request(`/api/freee-public/wallet-txn-detach-receipt`, {
    method: 'POST',
    body: JSON.stringify({ wallet_txn_id: walletTxnId, receipt_id: receiptId }),
  });
}

export interface FreeeReceiptDetail extends FreeeReceipt {
  status?: string;
  mime_type?: string;
  origin?: string;
  document_type?: string | null;
  receipt_metadatum?: { partner_name?: string | null; issue_date?: string | null; amount?: number | null } | null;
  user?: { id: number; email: string; display_name: string } | null;
  created_at?: string;
}

export async function fetchFreeeReceiptDetail(receiptId: number): Promise<{ ok: boolean; receipt: FreeeReceiptDetail }> {
  return request(`/api/freee-public/receipts/${receiptId}`);
}

// freee filebox web 直接 view URL (= /receipts/{id} 形式)
export function freeeReceiptViewUrl(receiptId: number): string {
  return `https://secure.freee.co.jp/receipts/${receiptId}`;
}

// freee filebox PDF binary を worker proxy 経由で取得 (= UI iframe 表示用)
export function freeeReceiptDownloadUrl(receiptId: number): string {
  return `${API_BASE}/api/freee-public/receipts/${receiptId}/download`;
}

// ---------------------------------------------------------------------------
// 仕訳レビュー行への証憑添付 (#journal_review, 2026-07-13)
//   POST /api/deals/:id/attach-receipt
//     { receipt_number } = freee ファイルボックスNo 指定 (worker が receipt_id へ解決して添付)
//     { receipt_id }     = /api/freee-public/receipts/upload 直後の直接指定
//   freee への書込は worker 側 (UI click 起点 = R2 準拠)。
// ---------------------------------------------------------------------------
export interface AttachReceiptToDealResult {
  ok: boolean;
  deal_id?: number;
  receipt_id?: number;
  receipt_ids?: number[];
  receipt_count?: number;
  error?: string;
  message?: string;
  [k: string]: any;
}

export async function attachReceiptToDeal(
  dealId: number,
  opts: { receipt_number?: number; receipt_id?: number },
): Promise<AttachReceiptToDealResult> {
  return request(`/api/deals/${dealId}/attach-receipt`, {
    method: 'POST',
    body: JSON.stringify({
      ...(opts.receipt_number != null ? { receipt_number: opts.receipt_number } : {}),
      ...(opts.receipt_id != null ? { receipt_id: opts.receipt_id } : {}),
    }),
  });
}

// JCB 自動 PDF 添付 (= ARICA → freee filebox 自動経路、 legacy)
export async function autoAttachJcbPdf(walletTxnId: number, transferDate?: string): Promise<{
  ok: boolean;
  wallet_txn_id: number;
  transfer_date: string;
  arica_pdf_count: number;
  attached: Array<{ arica_rowid: number; freee_receipt_id: number; filename: string; file_size: number }>;
  receipt_ids: number[];
  errors: any[];
}> {
  return request(`/api/freee-public/jcb-auto-attach`, {
    method: 'POST',
    body: JSON.stringify({ wallet_txn_id: walletTxnId, transfer_date: transferDate }),
  });
}

// schema-v34 (2026-05-08): R2 staging flow (= 仮保存 → preview → 承認時 commit)
export interface PendingReceipt {
  id: number;
  wallet_txn_id: number;
  source: 'arica_jcb' | 'arica_email' | 'manual_upload';
  source_ref: string | null;
  filename: string;
  mime_type: string;
  file_size: number;
  content_hash: string | null;
  r2_key: string;
  freee_receipt_id: number | null;
  uploaded_to_freee_at: string | null;
  created_at: string;
}

export async function stageJcbForWalletTxn(walletTxnId: number, transferDate?: string): Promise<{
  ok: boolean;
  wallet_txn_id: number;
  transfer_date: string;
  arica_pdf_count: number;
  staged: Array<{ pending_id: number; arica_rowid: number; filename?: string; file_size?: number; r2_key?: string; already_staged?: boolean; freee_receipt_id?: number }>;
  errors: any[];
}> {
  return request(`/api/freee-public/jcb-stage`, {
    method: 'POST',
    body: JSON.stringify({ wallet_txn_id: walletTxnId, transfer_date: transferDate }),
  });
}

// 2026-05-08: NICOS / mufg-transfer 用 (= 1 PDF が 8037+8028 両 merchant cover、 dedupe で 1 件に集約)
export async function stageNicosForWalletTxn(walletTxnId: number, transferDate?: string, merchantNumber?: string): Promise<{
  ok: boolean;
  wallet_txn_id: number;
  transfer_date: string;
  arica_pdf_count_raw: number;
  arica_pdf_count_unique: number;
  staged: Array<{ pending_id: number; arica_rowid: number; merchant_number?: string; filename?: string; file_size?: number; r2_key?: string; already_staged?: boolean; freee_receipt_id?: number }>;
  errors: any[];
}> {
  return request(`/api/freee-public/nicos-stage`, {
    method: 'POST',
    body: JSON.stringify({ wallet_txn_id: walletTxnId, transfer_date: transferDate, merchant_number: merchantNumber }),
  });
}

export async function stageManualUpload(walletTxnId: number, file: File): Promise<{
  ok: boolean;
  pending_id: number;
  filename: string;
  file_size: number;
}> {
  const fd = new FormData();
  fd.append('wallet_txn_id', String(walletTxnId));
  fd.append('file', file, file.name);
  const res = await fetch(`${API_BASE}/api/freee-public/receipts/stage-upload`, {
    method: 'POST',
    body: fd,
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`stage upload failed ${res.status}: ${t.slice(0, 200)}`);
  }
  return await res.json();
}

export async function listPendingReceipts(walletTxnId: number): Promise<{
  ok: boolean;
  items: PendingReceipt[];
  count: number;
}> {
  return request(`/api/freee-public/pending-receipts?wallet_txn_id=${walletTxnId}`);
}

export function pendingReceiptPreviewUrl(pendingId: number): string {
  return `${API_BASE}/api/freee-public/pending-receipts/${pendingId}/preview`;
}

export async function commitPendingReceipt(pendingId: number, opts: {
  description?: string;
  document_type?: string;
  issue_date?: string;
  partner_name?: string;
  amount?: number;
} = {}): Promise<{
  ok: boolean;
  pending_id: number;
  freee_receipt_id: number;
  wallet_txn_id: number;
}> {
  return request(`/api/freee-public/pending-receipts/${pendingId}/commit`, {
    method: 'POST',
    body: JSON.stringify(opts),
  });
}

export async function deletePendingReceipt(pendingId: number): Promise<{ ok: boolean }> {
  return request(`/api/freee-public/pending-receipts/${pendingId}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// 銀行 修正指示 (bank_correction_instructions — 2026-07-14 確定設計 §2)
//   journal 版 (journal_correction_instructions) の wallet_txn 版。
//   POST /api/bank-wallet-txns/:id/corrections が D1 INSERT + 再分析 dispatch まで
//   一括で行う (journal 版と違い createJob / createInstruction の二重書込みは不要)。
//   応答形は防御的に読む: { corrections } | { items } | 配列直 (worker 並行実装)。
// ---------------------------------------------------------------------------
export type BankCorrectionStatus = 'open' | 'in_progress' | 'done' | 'dismissed';
export interface BankCorrection {
  id: number;
  company_id?: number;
  wallet_txn_id: number;
  instruction_text: string;
  status: BankCorrectionStatus;
  /** 1 = 今後のルールとして学習可 / 0 = 一回限りの適用 (worker が /api/learn 到達を構造的に gate) */
  learn?: number | boolean | null;
  created_by?: string | null;
  dispatch_instruction_id?: string | null;
  applied_job_id?: string | null;
  /** 'applied:suggested' | 'applied:needs_review' 等 (complete 連鎖が記録) */
  result_note?: string | null;
  created_at: string;
  updated_at: string;
}

export async function fetchBankCorrections(walletTxnIds: number[]): Promise<{ corrections: BankCorrection[] }> {
  if (!walletTxnIds.length) return { corrections: [] };
  const out: BankCorrection[] = [];
  // 設計指定: 90件 chunk
  for (let i = 0; i < walletTxnIds.length; i += 90) {
    const ids = walletTxnIds.slice(i, i + 90);
    const q = new URLSearchParams({
      company_id: String(FREEE_COMPANY_ID),
      wallet_txn_ids: ids.join(','),
    });
    const r = await request<any>(`/api/bank-wallet-txns/corrections?${q}`);
    const arr = Array.isArray(r) ? r
      : Array.isArray(r?.corrections) ? r.corrections
      : Array.isArray(r?.items) ? r.items : [];
    out.push(...(arr as BankCorrection[]));
  }
  return { corrections: out };
}

export async function createBankCorrection(body: {
  wallet_txn_id: number;
  instruction_text: string;
  /** false = 一回限りの適用(今後のルールとして学習しない)。省略時は true 扱い (worker 側 learn=1) */
  learn?: boolean;
  created_by?: string;
  session_id?: string;
}): Promise<{ ok?: boolean; id?: number | null; dispatched?: boolean; [k: string]: any }> {
  const { wallet_txn_id, ...rest } = body;
  return request(`/api/bank-wallet-txns/${wallet_txn_id}/corrections`, {
    method: 'POST',
    body: JSON.stringify(rest),
  });
}

export async function updateBankCorrectionStatus(
  id: number,
  status: BankCorrectionStatus,
): Promise<{ ok?: boolean; id?: number; status?: string }> {
  return request(`/api/bank-wallet-txns/corrections/${id}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

// ---------------------------------------------------------------------------
// 証憑 自動取得候補 + 汎用 ARICA stage + 証憑 vision (2026-07-14 確定設計 §3)
//   per-family stage (jcb/nicos/ikyu/tokyogas) の乱立を止める汎用経路。
//   worker 並行実装 — endpoint 未配備 (404) / flag off (503) は呼び元が防御的に扱う。
// ---------------------------------------------------------------------------
export interface BankEvidenceCandidate {
  source?: string;                       // 'arica_jcb' | 'arica_nicos' | 'arica_email' | …
  source_ref?: string | number | null;
  kind?: string | null;                  // 'jcb' | 'nicos' | 'ikyu' | 'tokyogas' | 'email_invoice' | …
  label?: string | null;
  filename?: string | null;
  vendor?: string | null;
  date?: string | null;
  amount?: number | null;
  already_staged?: boolean;
  pending_id?: number | null;
  [k: string]: any;
}

export async function fetchBankEvidenceCandidates(walletTxnId: number): Promise<{ candidates: BankEvidenceCandidate[] }> {
  const r = await request<any>(`/api/bank-wallet-txns/${walletTxnId}/evidence-candidates`);
  const arr = Array.isArray(r) ? r
    : Array.isArray(r?.candidates) ? r.candidates
    : Array.isArray(r?.items) ? r.items : [];
  return { candidates: arr as BankEvidenceCandidate[] };
}

export async function stageAricaEvidence(walletTxnId: number, candidate: {
  source?: string;
  source_ref?: string;
  kind?: string;
  filename?: string;
}): Promise<{
  ok?: boolean;
  pending_id?: number | null;
  staged?: Array<{ pending_id?: number | null; already_staged?: boolean; [k: string]: any }>;
  errors?: any[];
  [k: string]: any;
}> {
  return request(`/api/freee-public/arica-stage`, {
    method: 'POST',
    body: JSON.stringify({ wallet_txn_id: walletTxnId, ...candidate }),
  });
}

/** ファイルボックスNo (freee filebox「No」表示値) 指定添付 — worker が receipt id へ解決して
 *  wtp.proposed_deal_json.receipt_ids[] に追加する (deal-update G4b の解決機構と同系)。
 *  現行未対応 worker は 400 { error: 'receipt_ids[] required' } を返すため呼び元が防御表示。 */
export async function attachReceiptNumberToWalletTxn(walletTxnId: number, receiptNumber: number): Promise<{
  ok?: boolean;
  wallet_txn_id?: number;
  receipt_ids?: number[];
  added?: number[];
  resolution?: any[];
  [k: string]: any;
}> {
  return request(`/api/freee-public/wallet-txn-attach-receipt`, {
    method: 'POST',
    body: JSON.stringify({ wallet_txn_id: walletTxnId, receipt_numbers: [receiptNumber] }),
  });
}

// 証憑 vision (wallet_txn_receipt_vision — job type 'bank_receipt_vision_extract')
export type BankReceiptVisionRefKind = 'pending' | 'receipt';
export interface BankReceiptVisionResult {
  id?: number;
  wallet_txn_id?: number;
  ref_kind?: BankReceiptVisionRefKind | string;
  ref_id?: number;
  job_id?: string | null;
  status?: 'queued' | 'done' | 'error' | string;
  result_json?: any;
  created_at?: string;
  updated_at?: string;
  [k: string]: any;
}

export async function requestBankReceiptVision(
  walletTxnId: number,
  refKind: BankReceiptVisionRefKind,
  refId: number,
): Promise<{ ok?: boolean; job_id?: string | null; status?: string; [k: string]: any }> {
  return request(`/api/bank-wallet-txns/${walletTxnId}/receipt-vision`, {
    method: 'POST',
    body: JSON.stringify({ ref_kind: refKind, ref_id: refId }),
  });
}

/** vision 結果の取得。正本は既存 detail GET 同載 (設計 §0-3) だが、widget 単体の
 *  再読込用に GET も叩く。未配備 worker では 404 → 呼び元が catch して detail 同載分で継続。 */
export async function fetchBankReceiptVision(walletTxnId: number): Promise<{ items: BankReceiptVisionResult[] }> {
  const r = await request<any>(`/api/bank-wallet-txns/${walletTxnId}/receipt-vision`);
  const arr = Array.isArray(r) ? r
    : Array.isArray(r?.items) ? r.items
    : Array.isArray(r?.results) ? r.results
    : Array.isArray(r?.receipt_vision) ? r.receipt_vision : [];
  return { items: arr as BankReceiptVisionResult[] };
}

// ---------------------------------------------------------------------------
// Stage 1 / 学習根拠 (= schema-v33 行詳細表示用)
// ---------------------------------------------------------------------------

export interface BankLearningEvidence {
  wallet_txn_id: number;
  lookup: {
    token_candidates: string[];
    entry_side: 'income' | 'expense';
    amount_band: 'micro' | 'small' | 'medium' | 'large';
    amount: number;
    walletable_id: number;
  };
  matched_pattern: {
    description_token_key: string;
    walletable_id: number;
    entry_side: string;
    amount_band: string;
    best_partner_id: number | null;
    best_partner_name: string | null;
    best_section_id: number | null;
    best_account_item_id: number | null;
    ai_confidence: number;
    sample_count: number;
    approved_count: number;
    rejected_count: number;
    modified_count: number;
    suspect_count: number;
    first_observed_at: string | null;
    last_confirmed_at: string | null;
    last_rebuilt_at: string;
    pattern_source: string;
    ai_evidence: any;
  } | null;
  similar_history: Array<{
    wallet_txn_id: number;
    partner_name: string | null;
    partner_id: number | null;
    flow_type: string;
    processing_status: string;
    freee_deal_id: number | null;
    registered_at: string | null;
    date: string;
    amount: number;
    description: string;
  }>;
  stage_decisions: Array<{
    stage_reached: string;
    decision: string;
    confidence: number | null;
    reason: string | null;
    classified_type: string | null;
    ai_dispatched_to_min_reader: number | null;
    created_at: string;
  }>;
  learning_events: Array<{
    event_type: string;
    source_path: string;
    outcome: string | null;
    partner_id: number | null;
    description_token_key: string | null;
    pattern_index_updated: number;
    pattern_learning_log_id: number | null;
    session_id: string | null;
    created_at: string;
  }>;
}

export async function fetchBankLearningEvidence(walletTxnId: number): Promise<BankLearningEvidence> {
  return request<BankLearningEvidence>(`/api/bank-wallet-txns/${walletTxnId}/learning-evidence`);
}

export async function mechanicalApproveBankWalletTxn(
  walletTxnId: number,
  sessionId: string,
  approvedBy: string = 'web_app_user',
): Promise<{
  ok: boolean;
  wallet_txn_id: number;
  ref_number: string;
  freee_deal_id: number | null;
  processing_status: string;
  reconcile: any;
  stage: string;
  auto_completed: boolean;
}> {
  return request(`/api/bank-wallet-txns/${walletTxnId}/mechanical-approve`, {
    method: 'POST',
    body: JSON.stringify({ session_id: sessionId, approved_by: approvedBy }),
  });
}

export async function retryBankRegister(id: number): Promise<any> {
  return request(`/api/bank-wallet-txns/${id}/retry-register`, { method: 'POST' });
}

export async function rerunBankAnalysis(id: number, sessionId?: string): Promise<any> {
  return request(`/api/bank-wallet-txns/${id}/rerun-analysis`, {
    method: 'POST',
    body: JSON.stringify({ session_id: sessionId }),
  });
}

export async function assignBankPartner(id: number, partnerId: number, partnerName?: string): Promise<any> {
  return request(`/api/bank-wallet-txns/${id}/assign-partner`, {
    method: 'POST',
    body: JSON.stringify({ partner_id: partnerId, partner_name: partnerName }),
  });
}

// 2026-07-13: worker は ≤50件/回 の 400 ガードあり — 呼び元 (BankProcessingPage) が
// 50件以下にチャンク分割して順次呼び出す。応答キーは全て optional (queued キーが無い
// 応答でも「undefined件」表示にならないよう、呼び元が results から集計 fallback する)。
export async function bulkEnqueueBankAnalysis(ids: number[], sessionId: string): Promise<{
  instruction_id?: string; total?: number; queued?: number;
  results?: Array<{ wallet_txn_id: number; status: string; reason?: string; ref_number?: string }>;
}> {
  return request('/api/bank-wallet-txns/bulk/enqueue-analysis', {
    method: 'POST',
    body: JSON.stringify({ wallet_txn_ids: ids, session_id: sessionId }),
  });
}

export async function submitBankSuggestion(id: number, payload: any): Promise<any> {
  return request(`/api/bank-wallet-txns/${id}/submit-suggestion`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchAutoRegisterRules(p: {
  walletable_id?: number; status?: string; partner_id?: number; observation?: string;
} = {}): Promise<{ items: any[]; count: number; generated_at?: string }> {
  const q = new URLSearchParams();
  if (p.walletable_id) q.set('walletable_id', String(p.walletable_id));
  if (p.status) q.set('status', p.status);
  if (p.partner_id) q.set('partner_id', String(p.partner_id));
  if (p.observation) q.set('observation', p.observation);
  const qs = q.toString();
  return request(`/api/auto-register-rules${qs ? '?' + qs : ''}`);
}

export async function confirmAutoRegisterRuleRegistered(id: number, freeeRuleId?: number, notes?: string): Promise<any> {
  return request(`/api/auto-register-rules/${id}/confirm-registered`, {
    method: 'POST',
    body: JSON.stringify({ freee_rule_id: freeeRuleId, notes }),
  });
}

// ---------------------------------------------------------------------------
// Phase 3 Reconciler (schema-v23): AutoRegisterRule candidate / observe / mark
// ---------------------------------------------------------------------------

export interface AutoRegisterRuleCandidate {
  candidate_id: string;
  rule_fingerprint: string;
  walletable_id: number;
  walletable_type: 'bank_account' | 'credit_card' | 'wallet';
  entry_side: 'income' | 'expense';
  flow_type: string;
  partner_id: number | null;
  partner_name: string | null;
  description_pattern: string;
  match_type: 'prefix' | 'suffix' | 'partial' | 'exact';
  sample_description: string | null;
  occurrence: {
    count_window: number;
    months_active: number;
    monthly_avg: number;
    first_date: string;
    last_date: string;
  };
  amount_stats: {
    min: number;
    max: number;
    avg: number | null;
    cv: number;
    total: number;
  };
  recommended: {
    account_item_id: number | null;
    account_item_name: string | null;
    tax_code: number | null;
    section_id: number | null;
    section_name: string | null;
  };
  pattern_confidence: number;
  pattern_source: string;
  registry: {
    match_rule: string;
    notes: string | null;
    flow_type: string;
  } | null;
  existing_rule: {
    id: number;
    fingerprint: string;
    status: string;
    freee_rule_id: number | null;
    confirmed_at: string | null;
    last_observed_at: string | null;
    observation_state: string | null;
  } | null;
  status: string;
}

export interface AutoRegisterRuleObservationState {
  kind:
    | 'strong_active' | 'weak_active' | 'mixed_suspect'
    | 'strong_missing' | 'no_observation' | 'undetermined'
    | 'unknown_fingerprint' | 'error';
  matched?: number;
  total?: number;
  window?: number;
  lookback_days?: number;
  error?: string;
}

export interface AutoRegisterRuleEvidence {
  rule_fingerprint: string;
  rule: {
    id: number;
    status: string;
    observation_state: string | null;
    walletable_id: number;
    entry_side: string;
    description_pattern: string | null;
    partner_id: number | null;
    partner_name: string | null;
    freee_rule_id: number | null;
    confirmed_at: string | null;
    confirm_grace_until: string | null;
    last_observed_at: string | null;
  };
  observation: AutoRegisterRuleObservationState;
  wallet_txns: Array<{
    wallet_txn_id: number;
    date: string;
    description: string | null;
    entry_side: string;
    amount: number;
    rule_matched: number;
    status_raw: number;
    last_changed_at: string | null;
    rule_matched_changed_at: string | null;
  }>;
  audit_log: Array<{
    id: number;
    from_status: string | null;
    to_status: string;
    from_observation_state: string | null;
    to_observation_state: string | null;
    changed_by: string;
    changed_at: string;
    cause: string | null;
    reason: string | null;
    metadata: string | null;
  }>;
}

export async function fetchAutoRegisterRuleCandidates(p: {
  walletable_id?: number;
  walletable_type?: string;
  min_occurrence?: number;
  months?: number;
  status_filter?: 'all' | 'unregistered' | 'registered';
  search?: string;
} = {}): Promise<{
  items: AutoRegisterRuleCandidate[];
  count: number;
  total: number;
  generated_at: string;
}> {
  const q = new URLSearchParams();
  if (p.walletable_id) q.set('walletable_id', String(p.walletable_id));
  if (p.walletable_type) q.set('walletable_type', p.walletable_type);
  if (p.min_occurrence) q.set('min_occurrence', String(p.min_occurrence));
  if (p.months) q.set('months', String(p.months));
  if (p.status_filter) q.set('status_filter', p.status_filter);
  if (p.search) q.set('search', p.search);
  const qs = q.toString();
  return request(`/api/auto-register-rules/candidates${qs ? '?' + qs : ''}`);
}

export interface MarkRegisteredTarget {
  walletable_id: number;
  walletable_type?: string;
  entry_side: 'income' | 'expense';
  description_pattern: string;
  match_type?: 'prefix' | 'suffix' | 'partial' | 'exact';
  min_amount?: number;
  max_amount?: number;
  partner_id?: number | null;
  partner_name?: string | null;
  target_account_item_id?: number | null;
  target_account_item_name?: string | null;
  target_tax_code?: number | null;
  target_tax_code_name?: string | null;
  target_section_id?: number | null;
  target_section_name?: string | null;
  freee_rule_id?: number | null;
  rule_fingerprint?: string;
}

export async function markAutoRegisterRulesRegistered(
  targets: MarkRegisteredTarget[],
  opts: { changed_by?: string; notes?: string; session_id?: string } = {},
): Promise<{
  updated: number;
  count: number;
  fingerprints: string[];
  status: string;
  results: Array<{ rule_fingerprint: string | null; id?: number; from_status?: string | null; to_status?: string; inserted?: boolean; error?: string }>;
  generated_at: string;
}> {
  return request('/api/auto-register-rules/mark-registered', {
    method: 'POST',
    body: JSON.stringify({
      targets,
      changed_by: opts.changed_by || 'web_app_user',
      notes: opts.notes,
      session_id: opts.session_id,
    }),
  });
}

export async function markAutoRegisterRuleUnregistered(
  fingerprint: string,
  opts: { changed_by?: string; reason?: string; replace_with_fingerprint?: string; session_id?: string } = {},
): Promise<{ rule_fingerprint: string; old_status: string; new_status: string }> {
  return request(`/api/auto-register-rules/${encodeURIComponent(fingerprint)}/mark-unregistered`, {
    method: 'POST',
    body: JSON.stringify({
      changed_by: opts.changed_by || 'web_app_user',
      reason: opts.reason,
      replace_with_fingerprint: opts.replace_with_fingerprint,
      session_id: opts.session_id,
    }),
  });
}

export async function observeAutoRegisterRule(fingerprint: string): Promise<{
  rule_fingerprint: string;
  observation: AutoRegisterRuleObservationState;
}> {
  const q = new URLSearchParams();
  q.set('fingerprint', fingerprint);
  return request(`/api/auto-register-rules/observe?${q}`);
}

export async function fetchAutoRegisterRuleEvidence(fingerprint: string, limit?: number): Promise<AutoRegisterRuleEvidence> {
  const q = new URLSearchParams();
  if (limit) q.set('limit', String(limit));
  const qs = q.toString();
  return request(`/api/auto-register-rules/${encodeURIComponent(fingerprint)}/evidence${qs ? '?' + qs : ''}`);
}

export async function openSessionForBank(): Promise<{ session_id: string; audit_ok_until: string }> {
  return request('/api/claude/session/open', {
    method: 'POST',
    body: JSON.stringify({ workspace_id: 'kumagai-corp' }),
  });
}

// session lease の生存確認 (2026-08-15)。lease は 240 分で失効するため、
//   画面を開きっぱなしのまま承認/登録すると register-after-approve だけが
//   401 invalid_or_expired_session で落ち、「承認済なのに freee 未登録」で止まる。
export async function validateSession(sessionId: string): Promise<boolean> {
  try {
    const r = await request<{ valid?: boolean }>(
      `/api/claude/session/validate?session_id=${encodeURIComponent(sessionId)}`,
    );
    return !!(r && r.valid);
  } catch {
    return false;
  }
}

export async function fetchBankSyncState(): Promise<{ items: any[]; count: number }> {
  return request('/api/bank-wallet-txns/sync-state');
}

// ---------------------------------------------------------------------------
// FREEEMAN診断 (schema-v25, 2026-04-24)
// ---------------------------------------------------------------------------
export type HealthGroup = 'A' | 'B' | 'C' | 'excluded';
export type AnomalyGrade = 'green' | 'yellow' | 'red' | 'stale' | 'expired' | 'new' | 'excluded';

export interface PartnerHealthSnapshot {
  id: number;
  target_month: string;
  partner_id: number | null;
  partner_name: string | null;
  group_type: HealthGroup;
  group_reason: string | null;
  cadence_pattern: string | null;
  cadence_confidence: number | null;
  same_month_ratio: number | null;
  past_12m_deal_count: number;
  past_12m_total: number;
  median_amount: number;
  mad_amount: number;
  expected_issue_date: string | null;
  expected_due_date: string | null;
  expected_amount: number | null;
  actual_deal_ids: string;
  actual_total: number | null;
  actual_count: number | null;
  anomaly_grade: AnomalyGrade;
  anomaly_score: number | null;
  anomaly_reasons: string;
  monthly_history_json: string;
  explanation_seed_json: string | null;
  last_issue_date: string | null;
  last_computed_at: string;
}

export interface HealthAnalysisRun {
  id: number;
  target_month: string;
  triggered_by: string;
  triggered_at: string;
  completed_at: string | null;
  status: 'running' | 'completed' | 'failed';
  partners_analyzed: number;
  deals_analyzed: number;
  anomalies_red: number;
  anomalies_yellow: number;
  missing_count: number;
  error_message: string | null;
}

export interface PartnerHealthSnapshotResponse {
  items: PartnerHealthSnapshot[];
  count: number;
  summary: Array<{ group_type: HealthGroup; anomaly_grade: AnomalyGrade; n: number; total: number }>;
  latest_run: HealthAnalysisRun | null;
}

export async function runPartnerHealthAnalysis(targetMonth: string): Promise<{
  ok: boolean; run_id: number; target_month: string;
  partners_analyzed: number; deals_analyzed: number;
  anomalies_red: number; anomalies_yellow: number; missing_count: number;
  duration_ms: number;
}> {
  return request('/api/partner-health/analyze', {
    method: 'POST',
    body: JSON.stringify({ target_month: targetMonth, triggered_by: 'user' }),
  });
}

export async function fetchPartnerHealthSnapshot(params: {
  target_month: string; group?: HealthGroup; grade?: AnomalyGrade;
}): Promise<PartnerHealthSnapshotResponse> {
  const q = new URLSearchParams();
  q.set('target_month', params.target_month);
  if (params.group) q.set('group', params.group);
  if (params.grade) q.set('grade', params.grade);
  return request(`/api/partner-health/snapshot?${q}`);
}

export async function fetchPartnerHealthRuns(limit: number = 20): Promise<{ items: HealthAnalysisRun[] }> {
  return request(`/api/partner-health/runs?limit=${limit}`);
}

export async function fetchPartnerHealthDetail(partnerId: number, targetMonth: string): Promise<{
  snapshot: PartnerHealthSnapshot;
  deals: Array<{ deal_id: number; issue_date: string; total_amount: number; total_vat: number; first_account_item_name: string | null }>;
}> {
  return request(`/api/partner-health/partner/${partnerId}?target_month=${targetMonth}`);
}

// ============================================================================
// Payroll (給与仕訳) — schema-v26, 2026-04-25
// ============================================================================

export type PayrollDealType =
  | 'salary_expense'
  | 'health_insurance'
  | 'pension'
  | 'income_netting'
  | 'residence_tax';

export type PayrollRunStatus = 'draft' | 'approved' | 'registered' | 'failed' | 'rolled_back';

export interface PayrollDealDetail {
  entry_side: 'debit' | 'credit';
  account_item_id: number | null;
  account_item_name: string;
  section_id?: number;
  section_name?: string;
  tax_code?: number;
  walletable_id?: number;
  walletable_type?: string;
  amount: number;
  description: string;
}

export interface PayrollDealFreeePayload {
  issue_date: string;
  due_date?: string;
  type: 'expense' | 'income';
  memo: string;
  from_walletable_id?: number;
  from_walletable_type?: string;
  debit_details: PayrollDealDetail[];
  credit_details: PayrollDealDetail[];
}

export interface PayrollUserAction {
  step: number;
  label: string;
  detail: string;
}
export interface PayrollExpectedPayment {
  date: string;
  walletable_id: number;
  walletable_name: string;
  purpose: string;
  approximate_amount: number | null;
}

export interface PayrollDealPreview {
  deal_seq: number;
  deal_type: PayrollDealType;
  deal_label: string;
  amount: number;
  balance_diff: number;
  uses_bank_actual?: boolean;
  hq_residual?: number;
  net_breakdown?: Record<string, number>;
  user_actions?: PayrollUserAction[];
  expected_payments?: PayrollExpectedPayment[];
  freee_payload: PayrollDealFreeePayload;
}

export interface PayrollClearingEvent {
  date: string;
  phase: 'accrual' | 'settlement';
  label: string;
  deal_seqs: number[];
  description: string;
  expected_walletable?: number;
  expected_walletables?: number[];
}
export interface PayrollClearingMap {
  events: PayrollClearingEvent[];
  summary: { total_accrual_month_end: number; total_to_settle: number };
}

export interface PayrollPreviewResponse {
  run_id?: number;
  payment_year: number;
  payment_month: number;
  closing_group_ids: number[];
  dates: {
    issue_date: string;
    salary_payment_date: string;
    residence_tax_payment_date: string;
    shakai_hoken_payment_date: string;
  };
  kot_totals: {
    employee_count: number;
    total_amount: number;
    total_deducation: number;
    total_transfer: number;
    insurance_individual?: {
      kenpo: number; kaigo: number; kounen: number; koyo: number; kids: number;
    };
    gensen_shotokuzei?: number;
    jumin_tokubetsu?: number;
    year_end_adjustment_refund?: number;
  };
  actuals?: { kenpo: number | null; pension: number | null } | null;
  deals: PayrollDealPreview[];
  balance_zero: boolean;
  balance_diff: number;
  balance_detail: Array<Record<string, unknown>>;
  section_breakdown: Record<string, unknown>;
  clearing_map?: PayrollClearingMap;
  unmapped_employees?: Array<{ rowid: number; name: string; department_id: number; department_code: string }>;
}

export interface PayrollBankProbeResponse {
  payment_year: number;
  payment_month: number;
  search_range: { start: string; end: string };
  expected_anchor?: string;
  rule?: string;
  wallets: number[];
  total_candidates: number;
  pair_dates_found?: string[];
  not_yet_settled?: boolean;
  note?: string;
  auto_classify: {
    kenpo_candidate: BankProbeCandidate | null;
    pension_candidate: BankProbeCandidate | null;
  };
  all_candidates: BankProbeCandidate[];
}
export interface BankProbeCandidate {
  wallet_txn_id: number;
  date: string;
  amount: number;
  description: string;
  walletable_id: number;
  entry_side: string;
}

export interface PayrollRunSummary {
  id: number;
  payment_year: number;
  payment_month: number;
  closing_group_id: number | null;
  status: PayrollRunStatus;
  kot_employee_count: number;
  kot_total_amount: number;
  balance_zero: number;
  balance_diff: number | null;
  triggered_at: string;
  approved_at: string | null;
  registered_at: string | null;
}

export async function fetchPayrollPreview(
  paymentYear: number,
  paymentMonth: number,
  opts?: { closingGroupId?: number; kenpoActual?: number; pensionActual?: number }
): Promise<PayrollPreviewResponse> {
  const q = new URLSearchParams();
  q.set('payment_year', String(paymentYear));
  q.set('payment_month', String(paymentMonth));
  if (opts?.closingGroupId) q.set('closing_group_id', String(opts.closingGroupId));
  if (opts?.kenpoActual) q.set('kenpo_actual', String(opts.kenpoActual));
  if (opts?.pensionActual) q.set('pension_actual', String(opts.pensionActual));
  return request(`/api/payroll/preview?${q}`);
}

export async function buildPayrollRun(
  paymentYear: number,
  paymentMonth: number,
  opts?: { closing_group_id?: number; triggered_by?: string; insurance_actuals?: { kenpo?: number; pension?: number } }
): Promise<PayrollPreviewResponse> {
  return request('/api/payroll/build', {
    method: 'POST',
    body: JSON.stringify({
      payment_year: paymentYear,
      payment_month: paymentMonth,
      closing_group_id: opts?.closing_group_id,
      triggered_by: opts?.triggered_by || 'user',
      insurance_actuals: opts?.insurance_actuals,
    }),
  });
}

export async function fetchPayrollBankProbe(paymentYear: number, paymentMonth: number): Promise<PayrollBankProbeResponse> {
  const q = new URLSearchParams();
  q.set('payment_year', String(paymentYear));
  q.set('payment_month', String(paymentMonth));
  return request(`/api/payroll/bank-insurance-probe?${q}`);
}

export async function approvePayrollRun(runId: number, approvedBy: string = 'user'): Promise<{ run_id: number; status: string }> {
  return request('/api/payroll/approve', {
    method: 'POST',
    body: JSON.stringify({ run_id: runId, approved_by: approvedBy }),
  });
}

export interface PayrollRegisterResult {
  ok: boolean;
  run_id: number;
  total_count: number;
  failed_count: number;
  results: Array<{
    deal_seq: number;
    deal_type?: string;
    status: string;  // 'registered' | 'failed' | 'already_registered' | 'dry_run'
    freee_deal_id?: number;
    route?: string;  // 'manual_journal' | 'deal_with_payment'
    wallet_txn_scrub?: any;
    error?: string;
  }>;
}

export async function registerPayrollRunToFreee(
  runId: number,
  opts?: { dryRun?: boolean; onlyDealSeq?: number; skipReconcile?: boolean }
): Promise<PayrollRegisterResult> {
  const body: any = { run_id: runId };
  if (opts?.dryRun) body.dry_run = true;
  if (opts?.onlyDealSeq) body.only_deal_seq = opts.onlyDealSeq;
  if (opts?.skipReconcile) body.skip_reconcile = true;
  return request('/api/payroll/register-to-freee', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// 給与関連 銀行振替 (通勤費 西武→決済口座 + 給与振込 三井住友/西武→決済口座) auto-reconcile
export interface PayrollBankTransferCandidate {
  wallet_txn_id: number;
  date: string;
  amount: number;
  description: string;
  walletable_id: number;
  status_raw: number;
}
export interface PayrollBankTransferCombination {
  sum: number;
  diff: number;
  count: number;
  items: PayrollBankTransferCandidate[];
}
export interface PayrollBankTransferResult {
  ok: boolean;
  run_id: number;
  commute: {
    kot_total: number;
    exact_match_count: number;
    results: Array<{
      wallet_txn_id: number;
      status: string;
      amount: number;
      target?: string;
      error?: string;
    }>;
  };
  salary: {
    kot_total: number;
    exact_match_count: number;
    candidates: PayrollBankTransferCandidate[];
    best_combination: PayrollBankTransferCombination | null;
    exact_sum_combination: PayrollBankTransferCombination | null;
    sum_match_combinations: PayrollBankTransferCombination[];
    results: Array<{
      wallet_txn_id: number;
      status: string;
      amount: number;
      from?: string;
      target?: string;
      error?: string;
    }>;
    requires_user_confirmation: boolean;
  };
}

// v4 仕訳表 表示用 lookup tables (= worker.js の AI / SECTION_NAMES と同じ canonical 値)
export const ACCOUNT_ITEM_NAMES: Record<number, string> = {
  858355814: '役員報酬',
  864550585: '給与手当',
  858355817: '雑給',
  892034816: '通勤費',
  858355768: '未払金',
  858355769: '未払費用',
  858355727: '仮払金',
  864550402: '源泉課税預り金',
  864550403: '住民税預り金',
  864550401: '社員預り金',
  858355820: '法定福利費',
  858355726: '未収入金',
  858355721: '立替金',
  897278677: '役員立替金',
};

// 2026-05-01 freee 真値で完全更新 (= D1 sections_cache + worker.js SECTION_NAMES と一致)
export const SECTION_NAMES_MAP: Record<number, string> = {
  3068540: '001 本部',
  3068541: '102 VinoBuono',
  3068542: '101 ワイン販売課',
  3068543: '103 煙草販売',
  3068544: '002 La SOFFITTA',
  3068545: '004 人間関係',
  3068546: '005 信州屋 渋谷店',
  3068547: '006 信州屋 新宿南口店',
  3068548: '008 BiOcafe',
  3068549: '012a 山城屋庄藏',
  3068550: '012b 山城屋分店',
  3068551: '013 chiocciol@pizzeria',
  3068552: '017 喜乃字屋',
  3068553: '019 Bistrot Gourmand',
  3068554: '021 備中屋長左衛門',
  3068555: '999 その他',
  3095534: '012c 山城屋EC',
  3095535: '104 デリバリー',
};

export const TAX_CODE_NAMES: Record<number, string> = {
  0: '不課税',
  2: '対象外',
  21: '課対仕入8%',
  136: '課対仕入(軽)',
};

export function lookupAccountName(id?: number | null): string {
  if (id == null) return '-';
  return ACCOUNT_ITEM_NAMES[id] || `acct=${id}`;
}
export function lookupSectionName(id?: number | null): string {
  if (id == null) return '-';
  return SECTION_NAMES_MAP[id] || `sec=${id}`;
}
export function lookupTaxName(code?: number | null): string {
  if (code == null) return '対象外';
  return TAX_CODE_NAMES[code] || `tax=${code}`;
}

// freee user_matchers (= 自動登録ルール) Public API wrapper
//   freee 公式 API /api/1/user_matchers proxy 経由 /api/freee-public/user-matchers
export interface FreeeUserMatcher {
  id: number;
  act: number;            // 0:推測 / 1:登録 / 2:振替推測 / 3:振替登録 / 4:無視 / 7:auto_scrub / etc
  active: boolean;
  condition: number;      // 0:partial / 1:forward / 2:backward / 3:exact / 4:wildcard
  description: string;
  entry_side_str: 'income' | 'expense';
  priority: number;
  walletable: string | null;
  // ↓ 条件一致時の処理 (= action fields) ↓
  account_item_name: string | null;
  partner_name: string | null;
  item_name: string | null;
  section_name: string | null;
  tax_name: string | null;
  min_amount: number | null;
  max_amount: number | null;
  deal_description: string | null;
  qualified_invoice_setting: string | null;  // non_qualified / qualified / depends_on_partner
  transfer_walletable: string | null;
  card_label: string | null;
  card_label_id: number | null;
  division_tag_1_name: string | null;
  division_tag_2_name: string | null;
  division_tag_3_name: string | null;
  default_tag_names: string[] | null;
  suggest_tax_from_walletable_invoice: boolean | null;
  // ↓ メタデータ ↓
  last_updated_user_id: number | null;
  corrected_wallet_txn_count: number | null;
  corrected_wallet_txn_count_percentage: number | null;
  user_name: string | null;
  updated_at: string | null;
}

export const USER_MATCHER_ACT_NAMES: Record<number, string> = {
  0: '取引推測',
  1: '取引登録',
  2: '振替推測',
  3: '振替登録',
  4: '無視 (自動)',
  5: 'manual_template',
  6: 'manual_scrub',
  7: 'auto_scrub (振替消込)',
  8: 'manual_output_zengin_scrub',
  9: 'auto_output_zengin_scrub',
  10: '無視 (推測)',
  11: 'プライベート (推測)',
  12: 'プライベート (登録)',
};

export const USER_MATCHER_CONDITION_NAMES: Record<number, string> = {
  0: '部分一致',
  1: '前方一致',
  2: '後方一致',
  3: '完全一致',
  4: '指定なし',
};

export async function fetchFreeeUserMatchers(opts?: { limit?: number; offset?: number; walletable_type?: string; walletable_id?: number }): Promise<{ ok: boolean; data: FreeeUserMatcher[]; meta?: { total_count?: number } }> {
  const q = new URLSearchParams();
  q.set('limit', String(opts?.limit ?? 100));
  if (opts?.offset) q.set('offset', String(opts.offset));
  if (opts?.walletable_type) q.set('walletable_type', opts.walletable_type);
  if (opts?.walletable_id) q.set('walletable_id', String(opts.walletable_id));
  return request(`/api/freee-public/user-matchers?${q}`);
}

// 全件取得 (= page 越えで合計を返す)
export async function fetchAllFreeeUserMatchers(): Promise<FreeeUserMatcher[]> {
  const all: FreeeUserMatcher[] = [];
  let offset = 0;
  const pageSize = 100;
  for (let i = 0; i < 50; i++) { // safety: max 5000 rules
    const r = await fetchFreeeUserMatchers({ limit: pageSize, offset });
    const items = r.data || [];
    all.push(...items);
    if (items.length < pageSize) break;
    offset += pageSize;
  }
  return all;
}

// 新規 user_matcher 作成 body type
export interface CreateUserMatcherBody {
  act: number;
  active: boolean;
  condition: number;
  description: string;
  entry_side_str: 'income' | 'expense';
  priority: number;
  tax_name?: string | null;
  walletable?: string | null;
  card_label?: string | null;
  card_label_id?: number | null;
  transfer_walletable?: string | null;
  min_amount?: number | null;
  max_amount?: number | null;
  deal_description?: string | null;
  qualified_invoice_setting?: 'non_qualified' | 'qualified' | 'depends_on_partner' | null;
  suggest_tax_from_walletable_invoice?: boolean | null;
  account_item_name?: string | null;
  partner_name?: string | null;
  item_name?: string | null;
  section_name?: string | null;
  division_tag_1_name?: string | null;
  division_tag_2_name?: string | null;
  division_tag_3_name?: string | null;
  default_tag_names?: string[] | null;
}

export async function createFreeeUserMatcher(body: CreateUserMatcherBody): Promise<{ ok: boolean; status: number; user_matcher?: FreeeUserMatcher; data?: FreeeUserMatcher; error?: unknown }> {
  return request('/api/freee-public/user-matchers', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function deleteFreeeUserMatcher(id: number): Promise<{ ok: boolean; status: number }> {
  return request(`/api/freee-public/user-matchers/${id}`, { method: 'DELETE' });
}

// freee リスト取得 (= dropdown 用、 typo による auto-tag-create を防ぐ)
export interface FreeePartnerLite { id: number; name: string; long_name?: string | null; shortcut1?: string | null; }
export async function fetchFreeePartners(opts?: { offset?: number; limit?: number }): Promise<{ partners: FreeePartnerLite[] }> {
  const q = new URLSearchParams({
    company_id: '11518223',
    limit: String(opts?.limit ?? 100),
  });
  if (opts?.offset) q.set('offset', String(opts.offset));
  return request(`/api/freee-proxy/api/1/partners?${q}`);
}
export async function fetchAllFreeePartners(): Promise<FreeePartnerLite[]> {
  const all: FreeePartnerLite[] = [];
  let offset = 0;
  for (let i = 0; i < 100; i++) {
    const r = await fetchFreeePartners({ offset, limit: 100 });
    const items = r.partners || [];
    all.push(...items);
    if (items.length < 100) break;
    offset += 100;
  }
  return all;
}

// ---------------------------------------------------------------------------
// freee マスタ (account_items / sections / tax_codes) 名称解決用
//   - proposed_deal_json は生 ID しか持たないため、フロントで ID→名称 を解決する。
//   - module-level cache (会社内で固定マスタなので session 中 1 回取得で十分)。
//   - GET 読取のみ・freee-proxy 経由。FREEEMAN_CLI_OVERRIDE 不要 (読取は恒久許可)。
// ---------------------------------------------------------------------------
const FREEE_COMPANY_ID = '11518223';

export interface FreeeAccountItemLite { id: number; name: string; }
export interface FreeeSectionLite { id: number; name: string; long_name?: string | null; }
export interface FreeeTaxCodeLite { code: number; name: string; name_ja?: string | null; }

let _accountItemsMapCache: Map<number, string> | null = null;
let _sectionsMapCache: Map<number, string> | null = null;
let _taxCodesMapCache: Map<number, string> | null = null;
let _accountItemsInflight: Promise<Map<number, string>> | null = null;
let _sectionsInflight: Promise<Map<number, string>> | null = null;
let _taxCodesInflight: Promise<Map<number, string>> | null = null;

export async function fetchAccountItemsMap(): Promise<Map<number, string>> {
  if (_accountItemsMapCache) return _accountItemsMapCache;
  if (_accountItemsInflight) return _accountItemsInflight;
  _accountItemsInflight = (async () => {
    const r = await request<{ account_items?: FreeeAccountItemLite[] }>(
      `/api/freee-proxy/api/1/account_items?company_id=${FREEE_COMPANY_ID}`,
    );
    const m = new Map<number, string>();
    for (const it of r.account_items || []) if (it && it.id != null) m.set(Number(it.id), it.name);
    _accountItemsMapCache = m;
    _accountItemsInflight = null;
    return m;
  })().catch((e) => { _accountItemsInflight = null; throw e; });
  return _accountItemsInflight;
}

export async function fetchSectionsMap(): Promise<Map<number, string>> {
  if (_sectionsMapCache) return _sectionsMapCache;
  if (_sectionsInflight) return _sectionsInflight;
  _sectionsInflight = (async () => {
    const r = await request<{ sections?: FreeeSectionLite[] }>(
      `/api/freee-proxy/api/1/sections?company_id=${FREEE_COMPANY_ID}`,
    );
    const m = new Map<number, string>();
    // name は "001 本部" のように番号 prefix 付き。long_name ("本部") を優先表示。
    for (const s of r.sections || []) if (s && s.id != null) m.set(Number(s.id), s.long_name || s.name);
    _sectionsMapCache = m;
    _sectionsInflight = null;
    return m;
  })().catch((e) => { _sectionsInflight = null; throw e; });
  return _sectionsInflight;
}

export async function fetchTaxCodesMap(): Promise<Map<number, string>> {
  if (_taxCodesMapCache) return _taxCodesMapCache;
  if (_taxCodesInflight) return _taxCodesInflight;
  _taxCodesInflight = (async () => {
    const r = await request<{ taxes?: FreeeTaxCodeLite[] }>(
      `/api/freee-proxy/api/1/taxes/codes?company_id=${FREEE_COMPANY_ID}`,
    );
    const m = new Map<number, string>();
    for (const t of r.taxes || []) {
      if (t && t.code != null) m.set(Number(t.code), t.name_ja || t.name);
    }
    _taxCodesMapCache = m;
    _taxCodesInflight = null;
    return m;
  })().catch((e) => { _taxCodesInflight = null; throw e; });
  return _taxCodesInflight;
}

// ---------------------------------------------------------------------------
// freee マスタ (items / tags / partners) 名称解決用 (2026-06 追加)
//   - proposed_deal_json の detail.item_id / detail.tag_ids[] / deal.partner_id を ID→名称 解決。
//   - 上の account_items/sections/tax_codes と同形式の module-level cache + inflight 共有。
//   - GET 読取のみ・freee-proxy 経由。
// ---------------------------------------------------------------------------
export interface FreeeItemLite { id: number; name: string; }
export interface FreeeTagLite { id: number; name: string; }
export interface FreeePartnerNameLite { id: number; name: string; long_name?: string | null; }

let _itemsMapCache: Map<number, string> | null = null;
let _tagsMapCache: Map<number, string> | null = null;
let _partnersMapCache: Map<number, string> | null = null;
let _itemsInflight: Promise<Map<number, string>> | null = null;
let _tagsInflight: Promise<Map<number, string>> | null = null;
let _partnersInflight: Promise<Map<number, string>> | null = null;

export async function fetchItemsMap(): Promise<Map<number, string>> {
  if (_itemsMapCache) return _itemsMapCache;
  if (_itemsInflight) return _itemsInflight;
  _itemsInflight = (async () => {
    // freee /api/1/items は limit 無指定だと 50 件しか返さない (default cap)。
    // 50 件超の品目 (例 228548627=通信費) が Map に入らず #id 生コード表示になる
    // bug の根本原因。partners と同様に全件 paginate して解決する。
    const m = new Map<number, string>();
    let offset = 0;
    for (let i = 0; i < 100; i++) {
      const r = await request<{ items?: FreeeItemLite[] }>(
        `/api/freee-proxy/api/1/items?company_id=${FREEE_COMPANY_ID}&limit=100&offset=${offset}`,
      );
      const items = r.items || [];
      for (const it of items) if (it && it.id != null) m.set(Number(it.id), it.name);
      if (items.length < 100) break;
      offset += 100;
    }
    _itemsMapCache = m;
    _itemsInflight = null;
    return m;
  })().catch((e) => { _itemsInflight = null; throw e; });
  return _itemsInflight;
}

export async function fetchTagsMap(): Promise<Map<number, string>> {
  if (_tagsMapCache) return _tagsMapCache;
  if (_tagsInflight) return _tagsInflight;
  _tagsInflight = (async () => {
    // メモタグも同様の 50 件 cap リスクに備え全件 paginate (現状 15 件だが将来増加対策)。
    const m = new Map<number, string>();
    let offset = 0;
    for (let i = 0; i < 100; i++) {
      const r = await request<{ tags?: FreeeTagLite[] }>(
        `/api/freee-proxy/api/1/tags?company_id=${FREEE_COMPANY_ID}&limit=100&offset=${offset}`,
      );
      const tags = r.tags || [];
      for (const t of tags) if (t && t.id != null) m.set(Number(t.id), t.name);
      if (tags.length < 100) break;
      offset += 100;
    }
    _tagsMapCache = m;
    _tagsInflight = null;
    return m;
  })().catch((e) => { _tagsInflight = null; throw e; });
  return _tagsInflight;
}

// 取引先 id→名称 (全件 paginate)。長い long_name より freee 表示名 (name) を優先。
export async function fetchPartnersMap(): Promise<Map<number, string>> {
  if (_partnersMapCache) return _partnersMapCache;
  if (_partnersInflight) return _partnersInflight;
  _partnersInflight = (async () => {
    const m = new Map<number, string>();
    let offset = 0;
    for (let i = 0; i < 100; i++) {
      const r = await request<{ partners?: FreeePartnerNameLite[] }>(
        `/api/freee-proxy/api/1/partners?company_id=${FREEE_COMPANY_ID}&limit=100&offset=${offset}`,
      );
      const items = r.partners || [];
      for (const p of items) if (p && p.id != null) m.set(Number(p.id), p.name);
      if (items.length < 100) break;
      offset += 100;
    }
    _partnersMapCache = m;
    _partnersInflight = null;
    return m;
  })().catch((e) => { _partnersInflight = null; throw e; });
  return _partnersInflight;
}

// v4 spec preview (csv_row_json ベース 完璧 0 検証)
export interface PayrollV4Payment {
  walletable_type: string;
  walletable_id: number;
  amount: number;
  date: string;
  description: string;
}
export interface PayrollV4PreviewResult {
  ok: boolean;
  spec_version: string;
  payment_year: number;
  payment_month: number;
  kot_totals: any;
  sections: any;
  tatekae_negative: Array<{ name: string; code: string; section_id: number; amount: number; desc: string }>;
  retired_with_jumin: Array<{ name: string; code: string; resigned_date: string; monthly_jumin: number; bulk_amount: number; desc: string }>;
  actuals?: { kenpo?: number; pension?: number };
  deal2?: any;
  deal3?: any;
  deal4?: any;
  deal1: {
    deal_seq: number;
    deal_type: string;
    deal_label: string;
    amount: number;
    balance_diff: number;
    debit_sum: number;
    credit_sum: number;
    payments_sum: number;
    perfect_zero: boolean;
    freee_payload: { issue_date: string; due_date: string; type: string; details: any[]; payments: PayrollV4Payment[] };
  };
  deal_jumin: {
    deal_seq: number;
    deal_type: string;
    deal_label: string;
    amount: number;
    expected_payment_amount: number;
    freee_payload: any;
  };
  verification: {
    tri_main: number; tri_grad: number; tri_sum: number;
    cash_total: number; decchi_amount: number;
    payments_sum: number; deal1_amount: number;
    balance_diff: number; perfect_zero: boolean;
  };
  ctx: any;
}

export async function fetchPayrollV4Preview(year: number, month: number): Promise<PayrollV4PreviewResult> {
  return request(`/api/payroll/preview-v4?payment_year=${year}&payment_month=${month}`);
}

export async function buildPayrollV4(year: number, month: number, triggeredBy?: string): Promise<PayrollV4PreviewResult & { run_id: number }> {
  return request('/api/payroll/build-v4', {
    method: 'POST',
    body: JSON.stringify({ payment_year: year, payment_month: month, triggered_by: triggeredBy || 'freeeman-ui-v4' }),
  });
}

export interface PayrollV4RegisterResult {
  ok: boolean;
  spec_version: string;
  run_id: number;
  total_count: number;
  failed_count: number;
  results: Array<{
    deal_seq: number;
    deal_type: string;
    status: string;
    freee_deal_id?: number;
    amount?: number;
    payments_count?: number;
    error?: string;
    scrub_results?: Array<{
      wallet_txn_id?: number;
      wallet?: string;
      status: string;
      amount?: number;
      splits_count?: number;
      attempts?: number;
      error?: string;
      date?: string;
    }>;
  }>;
  bank_transfers?: any;
}

export async function registerPayrollV4ToFreee(runId: number, opts?: { dryRun?: boolean; withBankTransfers?: boolean }): Promise<PayrollV4RegisterResult> {
  return request('/api/payroll/register-to-freee-v4', {
    method: 'POST',
    body: JSON.stringify({ run_id: runId, dry_run: opts?.dryRun, with_bank_transfers: opts?.withBankTransfers }),
  });
}

export interface PayrollV5RegisterResult {
  ok: boolean;
  spec_version: string;
  run_id: number;
  results: Array<{
    kind: string;
    status: string;
    deal_id?: number;
    amount?: number;
    sections?: number;
    error?: string;
  }>;
}

export async function cleanupPayrollDeals(runId: number): Promise<{ ok: boolean; deleted_count: number; failed_count: number; results: any[] }> {
  return request('/api/payroll/cleanup-deals', {
    method: 'POST',
    body: JSON.stringify({ run_id: runId }),
  });
}

export async function registerPayrollV5ToFreee(runId: number, opts?: { dryRun?: boolean }): Promise<PayrollV5RegisterResult> {
  return request('/api/payroll/register-to-freee-v5', {
    method: 'POST',
    body: JSON.stringify({ run_id: runId, dry_run: opts?.dryRun }),
  });
}

export async function registerPayrollBankTransfers(
  runId: number,
  opts?: { dryRun?: boolean; salaryWalletTxnIds?: number[] }
): Promise<PayrollBankTransferResult> {
  const body: any = { run_id: runId };
  if (opts?.dryRun) body.dry_run = true;
  if (opts?.salaryWalletTxnIds) body.salary_wallet_txn_ids = opts.salaryWalletTxnIds;
  return request('/api/payroll/register-bank-transfers', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function markPayrollDealRegistered(runId: number, dealSeq: number, freeeDealId: number): Promise<{ run_id: number; deal_seq: number; freee_deal_id: number }> {
  return request('/api/payroll/mark-registered', {
    method: 'POST',
    body: JSON.stringify({ run_id: runId, deal_seq: dealSeq, freee_deal_id: freeeDealId }),
  });
}

export async function markPayrollDealFailed(runId: number, dealSeq: number, errorMessage: string): Promise<{ run_id: number; deal_seq: number; status: string }> {
  return request('/api/payroll/mark-failed', {
    method: 'POST',
    body: JSON.stringify({ run_id: runId, deal_seq: dealSeq, error_message: errorMessage }),
  });
}

export async function fetchPayrollRuns(limit: number = 20): Promise<{ items: PayrollRunSummary[] }> {
  return request(`/api/payroll/runs?limit=${limit}`);
}

export async function fetchPayrollStatus(runId: number): Promise<{
  run: PayrollRunSummary & { deals_json: string | null; error_message: string | null; notes: string | null };
  deals: Array<{
    id: number;
    run_id: number;
    deal_seq: number;
    deal_type: PayrollDealType;
    amount: number | null;
    status: 'pending' | 'registered' | 'failed';
    freee_deal_id: number | null;
    registered_at: string | null;
    error_message: string | null;
    freee_payload: PayrollDealFreeePayload;
  }>;
}> {
  return request(`/api/payroll/status?run_id=${runId}`);
}

// ============================================================================
// Bank Ledger (銀行出納帳) — schema-v27, 2026-04-25
// ============================================================================

export interface BankLedgerPattern {
  id: string;
  label: string;
  description: string;
  side: 'income' | 'expense';
  debit_walletable?: number;
  credit_walletable?: number;
  debit_account_item_id?: number;
  credit_account_item_id?: number;
  typical_date?: string;
  typical_amount_range?: [number, number];
  related_deal?: string;
  requires_manual?: boolean;
}

export interface BankLedgerClassification {
  classification_id: number;
  pattern_id: string;
  pattern_label: string;
  payload_amount: number;
  freee_deal_id: number | null;
  status: 'classified' | 'registered' | 'failed' | 'rolled_back';
  classified_at: string;
  registered_at: string | null;
}

export interface BankLedgerTxn {
  wallet_txn_id: number;
  date: string;
  amount: number;
  description: string;
  walletable_id: number;
  walletable_type: string;
  entry_side: 'income' | 'expense';
  status_raw: number;
  rule_matched: number;
  is_auto_registered: boolean;
  auto_registered_freee_deal_id: number | null;
  classification: BankLedgerClassification | null;
}

export interface BankLedgerListResponse {
  walletable_id: number;
  walletable_name: string;
  month: string;
  total: number;
  classified: number;
  pending: number;
  items: BankLedgerTxn[];
}

export interface BankLedgerPayloadDetail {
  account_item_id?: number;
  account_item_name?: string;
  walletable_id?: number;
  walletable_type?: string;
  amount: number;
  note?: string;
}

export interface BankLedgerClassifyResponse {
  classification_id: number;
  wallet_txn_id: number;
  pattern_id: string;
  pattern_label: string;
  payload: {
    pattern_id: string;
    pattern_label: string;
    deal_type: 'expense' | 'income';
    issue_date: string;
    description: string;
    source_wallet_txn: { id: number; date: string; amount: number; description: string };
    debit: BankLedgerPayloadDetail;
    credit: BankLedgerPayloadDetail;
    related_deal: string | null;
    requires_manual: boolean;
  };
}

export async function fetchBankLedgerPatterns(walletableId: number): Promise<{
  walletable_id: number;
  walletable_name: string;
  patterns: BankLedgerPattern[];
}> {
  return request(`/api/bank-ledger/patterns?walletable_id=${walletableId}`);
}

export async function fetchBankLedgerUnclassified(walletableId: number, month: string): Promise<BankLedgerListResponse> {
  return request(`/api/bank-ledger/wallet/${walletableId}/unclassified?month=${month}`);
}

export async function classifyBankTxn(opts: {
  wallet_txn_id: number;
  pattern_id: string;
  override_amount?: number;
  notes?: string;
  classified_by?: string;
}): Promise<BankLedgerClassifyResponse> {
  return request('/api/bank-ledger/classify', {
    method: 'POST',
    body: JSON.stringify(opts),
  });
}

export async function markBankClassificationRegistered(classificationId: number, freeeDealId: number): Promise<{ classification_id: number; freee_deal_id: number; status: string }> {
  return request('/api/bank-ledger/mark-registered', {
    method: 'POST',
    body: JSON.stringify({ classification_id: classificationId, freee_deal_id: freeeDealId }),
  });
}

export async function unclassifyBankTxn(classificationId: number): Promise<{ classification_id: number; deleted: boolean }> {
  return request('/api/bank-ledger/unclassify', {
    method: 'POST',
    body: JSON.stringify({ classification_id: classificationId }),
  });
}

// ============================================================================
// 摘要空白 pattern feedback (schema-v28)
// ============================================================================
export async function submitEmptyDescFeedback(walletTxnId: number, userComment: string): Promise<{
  feedback_id: number;
  wallet_txn_id: number;
  walletable_name: string;
  status: string;
  message: string;
}> {
  return request('/api/bank-processing/empty-desc-feedback', {
    method: 'POST',
    body: JSON.stringify({ wallet_txn_id: walletTxnId, user_comment: userComment }),
  });
}

// ============================================================================
// ALSOK 入金処理 API (schema-v31, 2026-05-07)
// ============================================================================
export interface AlsokDealDetail {
  entry_side: 'debit' | 'credit';
  account_item_id: number;
  accountName: string;
  tax_code: number;
  taxCodeName: string;
  amount: number;
  section_id: number;
  sectionName: string;
  description: string;
}

export interface AlsokDealPayload {
  issue_date: string;
  due_date: string;
  type: 'income' | 'expense';
  partner_id: number | null;
  ref_number: string;
  memo: string;
  amount: number;
  balance_diff: number;
  details: AlsokDealDetail[];
  debit_details: AlsokDealDetail[];
  credit_details: AlsokDealDetail[];
  payments: Array<{
    from_walletable_type: 'bank_account' | 'wallet';
    from_walletable_id: number;
    amount: number;
    date: string;
  }>;
}

export interface AlsokWalletTxn {
  wallet_txn_id: number;
  walletable_id: number;
  date: string;
  entry_side: 'income' | 'expense';
  amount: number;
  description: string;
  status_raw: number;
}

export interface AlsokRegistrationStatus {
  run: {
    closing_date: string;
    status: 'proposed' | 'approved' | 'registered' | 'registered_backfill' | 'partial' | 'failed';
    freee_deal_a_id: number | null;
    freee_deal_b_id: number | null;
    freee_deal_c_id: number | null;
    freee_deal_d_id: number | null;
    matched_wallet_txn_id: number | null;
    failed_reason: string | null;
    registered_at: string | null;
  } | null;
  ir_a: { freee_deal_id: number | null; status: string } | null;
  ir_b: { freee_deal_id: number | null; status: string } | null;
}

export interface AlsokDayPreview {
  closing_date: string;
  issue_date: string;
  arica_closing_dates: string[];
  is_monday: boolean;
  is_tuesday: boolean;
  is_friday: boolean;
  is_holiday_followup: boolean;
  consolidated_days: number;
  record_count: number;
  section_count: number;
  unknown_regi_codes: string[];
  regi100_sum: number;
  regi101_sum: number;
  deal_a: AlsokDealPayload;
  deal_b: AlsokDealPayload | null;
  deal_c: AlsokDealPayload | null;
  deal_d: AlsokDealPayload | null;
  bank_actual_amount: number | null;
  expected_a_amount: number;
  amount_diff: number | null;
  audit_warnings: string[];
  wallet_txn: AlsokWalletTxn | null;
  registration_status: AlsokRegistrationStatus;
}

export interface AlsokMonthPreview {
  target_month: string;
  days: AlsokDayPreview[];
  summary: {
    total_days: number;
    registered_count: number;
    pending_count: number;
    bank_amount_total: number;
  };
}

export async function fetchAlsokPreviewMonth(targetMonth: string): Promise<AlsokMonthPreview> {
  return request('/api/alsok/preview-month', {
    method: 'POST',
    body: JSON.stringify({ target_month: targetMonth }),
  });
}

export async function fetchAlsokPreviewDay(closingDate: string): Promise<AlsokDayPreview> {
  return request('/api/alsok/preview-day', {
    method: 'POST',
    body: JSON.stringify({ closing_date: closingDate }),
  });
}

export interface AlsokRegisterDayResult {
  ok: boolean;
  closing_date: string;
  status: 'registered' | 'partial' | 'already_registered' | 'failed';
  results?: {
    deal_a?: { freee_deal_id: number; was_phantom: boolean } | { error: string } | null;
    deal_b?: { freee_deal_id: number; was_phantom: boolean } | { error: string } | null;
    deal_c?: { freee_deal_id: number; was_phantom: boolean } | { error: string } | null;
    deal_d?: { freee_deal_id: number; was_phantom: boolean } | { error: string } | null;
    reconcile?: any;
  };
  alsok_run?: any;
}

export async function cleanupAlsokDay(closingDate: string, sessionId: string, alsoDeleteFreeeDeals = true): Promise<any> {
  return request('/api/alsok/cleanup-day', {
    method: 'POST',
    body: JSON.stringify({ closing_date: closingDate, session_id: sessionId, also_delete_freee_deals: alsoDeleteFreeeDeals }),
  });
}

export async function registerAlsokDay(closingDate: string, sessionId: string, approvedBy = 'web_app_user'): Promise<AlsokRegisterDayResult> {
  return request('/api/alsok/register-day', {
    method: 'POST',
    body: JSON.stringify({ closing_date: closingDate, session_id: sessionId, approved_by: approvedBy }),
  });
}

export async function fetchAlsokRuns(targetMonth?: string): Promise<{ items: any[] }> {
  const q = targetMonth ? `?target_month=${encodeURIComponent(targetMonth)}` : '';
  return request(`/api/alsok/runs${q}`);
}

// ============================================================================
// ALSOK 振替バックフィル (取引B/C/D — 入金機投入高 → wallet 振替) (2026-06-26)
// ----------------------------------------------------------------------------
// 2026-04 以降、 取引A (= alsok:DATE:a) は手動登録済みだが、 入金機投入高 の本部滞留を
// wallet (本部両替金 6361178 / 本部入金高 6361308) へ相殺する 振替 (取引B/C/D) が抜けている。
// transfer-preview = 既存 取引A から相殺対象額を抽出 (read-only / recompute 禁止)。
// transfer-register = ユーザー UI クリック起点で 不足分の B/C/D のみ freee POST (R2)。
// 取引A は 一切生成・変更しない (二重計上回避)。
// ============================================================================
export interface AlsokTransferSourceRows {
  exchange_carry: number; // 両替金持参 (= 取引A の 負 credit 行 magnitude) → 取引B
  regi100: number;        // 本部両替金cr (= regi100) → 取引C
  regi101: number;        // 本部入金高cr (= regi101) → 取引D
}

export interface AlsokTransferBalanceCheck {
  clearing_residual_before: number; // 取引A が 入金機投入高(本部) に残した滞留額
  clearing_residual_after: number;  // B/C/D 相殺後 (= 0 になるはず)
}

export type AlsokTransferStatus =
  | 'needs_transfer'
  | 'fully_registered'
  | 'partial'
  | 'blocked';

export interface AlsokTransferDay {
  closing_date: string;
  deal_a_ref: string;            // 'alsok:DATE:a'
  deal_a_id: number | null;      // freee deal id of 取引A (source of truth)
  has_deal_a: boolean;           // false → 振替候補ではない (skip)
  source_rows: AlsokTransferSourceRows;
  deal_b: AlsokDealPayload | null; // 本部両替金 補充 (両替金持参 offset)
  deal_c: AlsokDealPayload | null; // 本部両替金 戻し (regi100 offset)
  deal_d: AlsokDealPayload | null; // 本部入金高 (regi101 offset)
  transfer_status: AlsokTransferStatus;
  existing: { b: boolean; c: boolean; d: boolean }; // already in invoice_registrations OR freee
  balance_check: AlsokTransferBalanceCheck;
  audit_warnings: string[];
}

export interface AlsokTransferPreview {
  target_month?: string;
  closing_date?: string;
  days: AlsokTransferDay[];
  summary: {
    total_days: number;
    fully_registered: number;
    needs_transfer: number;
    blocked: number;
  };
}

export interface AlsokTransferRegisterResult {
  ok: boolean;
  closing_date: string;
  status: AlsokTransferStatus | 'registered' | 'partial' | 'already_registered' | 'failed';
  results?: {
    deal_b?: { freee_deal_id: number; was_phantom: boolean } | { error: string } | { skipped: true } | null;
    deal_c?: { freee_deal_id: number; was_phantom: boolean } | { error: string } | { skipped: true } | null;
    deal_d?: { freee_deal_id: number; was_phantom: boolean } | { error: string } | { skipped: true } | null;
  };
  error?: string;
}

// transfer-preview: target_month で 振替未登録の日を列挙 (read-only)
export async function fetchAlsokTransferPreview(targetMonth: string): Promise<AlsokTransferPreview> {
  return request('/api/alsok/transfer-preview', {
    method: 'POST',
    body: JSON.stringify({ target_month: targetMonth }),
  });
}

// transfer-preview: 単日 (closing_date) の振替 detail を取得 (read-only)
export async function fetchAlsokTransferPreviewDay(closingDate: string): Promise<AlsokTransferPreview> {
  return request('/api/alsok/transfer-preview', {
    method: 'POST',
    body: JSON.stringify({ closing_date: closingDate }),
  });
}

// transfer-register: ユーザー UI クリック起点で 不足分の 取引B/C/D のみ freee 登録 (R2)
export async function registerAlsokTransfer(
  closingDate: string,
  sessionId: string,
  approvedBy = 'web_app_user',
  allowPartial = false,
): Promise<AlsokTransferRegisterResult> {
  return request('/api/alsok/transfer-register', {
    method: 'POST',
    body: JSON.stringify({
      closing_date: closingDate,
      session_id: sessionId,
      approved_by: approvedBy,
      allow_partial: allowPartial,
    }),
  });
}

// session/open helper (= return value 直接返す、 closure stale 回避)
export async function openClaudeSessionForAlsok(): Promise<string | null> {
  try {
    const r = await fetch(`${API_BASE}/api/claude/session/open`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ workspace_id: 'kumagai-corp', reason: 'AlsokPage user click' }),
    });
    const j: any = await r.json();
    if (!r.ok && r.status === 409) notifyAuditBlocked(j);
    return j.session_id || null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Invoice Coverage Audit (P1-2, 2026-05-09)
// 25日払い対象スパン (前月21日 〜 今月31日) の coverage summary fetch helper
// ---------------------------------------------------------------------------
export interface InvoiceCoverageSummary {
  receive_window: { from: string; to: string };
  total_received: number;
  by_status: Record<string, number>;
  untouched_total: number;
  untouched_5_25_due_estimate: number;
  likely_5_25_total_amount: number;
}

export async function fetchInvoiceCoverageSummary(from: string, to?: string): Promise<{
  summary: InvoiceCoverageSummary;
  rows?: any[];
}> {
  const q = new URLSearchParams({ from });
  if (to) q.set('to', to);
  const r = await fetch(`${API_BASE}/api/admin/invoice-coverage-audit?${q}`);
  if (!r.ok) throw new Error(`fetchInvoiceCoverageSummary failed: ${r.status}`);
  return r.json();
}

// ---------------------------------------------------------------------------
// Deferred Queue (schema-v41, 2026-05-09)
// ---------------------------------------------------------------------------
export type DeferReason = 'pdf_extract_failed' | 'partner_id_null' | 'bank_auto_debit' | 'no_recommend_data' | 'other';

export interface DeferredInvoiceItem {
  id: number;
  arica_rowid: number;
  partner_id: number | null;
  partner_name: string | null;
  total_amount: number | null;
  attachment_filename: string | null;
  defer_reason: DeferReason;
  defer_detail: string | null;
  deferred_by: string | null;
  deferred_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution: string | null;
  proposal_id: number | null;
  notes: string | null;
}

export interface DeferredInvoiceListResponse {
  ok: boolean;
  status: string;
  reason: string | null;
  count: number;
  totals: { open_count: number; resolved_count: number; total: number };
  by_reason: Array<{ defer_reason: string; open_count: number; resolved_count: number; total: number }>;
  items: DeferredInvoiceItem[];
}

export interface DeferredInvoiceDetail {
  ok: boolean;
  item: DeferredInvoiceItem;
  pdf_url: string | null;
  tracking: any | null;
  proposal: any | null;
}

export async function fetchDeferredInvoices(params: { status?: 'open' | 'resolved' | 'all'; reason?: string; limit?: number }): Promise<DeferredInvoiceListResponse> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.reason) q.set('reason', params.reason);
  if (params.limit) q.set('limit', String(params.limit));
  const r = await fetch(`${API_BASE}/api/admin/deferred-invoices?${q}`);
  if (!r.ok) throw new Error(`fetchDeferredInvoices failed: ${r.status}`);
  return r.json();
}

export async function fetchDeferredInvoice(id: number): Promise<DeferredInvoiceDetail> {
  const r = await fetch(`${API_BASE}/api/admin/deferred-invoices/${id}`);
  if (!r.ok) throw new Error(`fetchDeferredInvoice failed: ${r.status}`);
  return r.json();
}

export async function resolveDeferredInvoice(id: number, body: {
  resolution?: string;
  resolved_by?: string;
  notes?: string;
  partner_id?: number;
  total_amount?: number;
}): Promise<{ ok: boolean; item: any }> {
  const r = await fetch(`${API_BASE}/api/admin/deferred-invoices/${id}/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    let m = '';
    try { m = (await r.json()).error || ''; } catch {}
    throw new Error(`resolveDeferredInvoice failed: ${r.status} ${m}`);
  }
  return r.json();
}

export async function requeueDeferredInvoice(id: number, body: {
  partner_id?: number;
  total_amount?: number;
  requested_by?: string;
  note?: string;
}): Promise<{ ok: boolean; requeued: boolean; idl_id: number; arica_rowid: number; instruction_id: string; overrides: any }> {
  const r = await fetch(`${API_BASE}/api/admin/deferred-invoices/${id}/requeue`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    let m = '';
    try { m = (await r.json()).error || ''; } catch {}
    throw new Error(`requeueDeferredInvoice failed: ${r.status} ${m}`);
  }
  return r.json();
}

// ---------------------------------------------------------------------------
// Payment Window Config + Bulk Override + Bulk Dispatch (schema-v52/v53, 2026-05-18)
// ---------------------------------------------------------------------------

export interface PaymentWindow {
  id: number;
  label: string;
  receive_from: string;
  receive_to: string;
  target_payment_month: string;
  flow_type: '25th_payment' | 'bank_auto_debit' | 'other';
  is_active: number;
  notes: string | null;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface PaymentWindowOverlapConflict {
  id: number;
  label: string;
  receive_from: string;
  receive_to: string;
  target_payment_month: string;
}

export async function fetchPaymentWindows(activeOnly?: boolean): Promise<{ ok: boolean; items: PaymentWindow[] }> {
  const q = new URLSearchParams();
  if (activeOnly) q.set('active_only', '1');
  const r = await fetch(`${API_BASE}/api/admin/payment-windows?${q}`);
  if (!r.ok) throw new Error(`fetchPaymentWindows failed: ${r.status}`);
  const data = await r.json();
  // worker returns { ok, windows: [...] }; normalize to { ok, items: [...] }
  return { ok: data.ok, items: data.windows || data.items || [] };
}

export async function createPaymentWindow(opts: {
  label: string;
  receive_from: string;
  receive_to: string;
  target_payment_month: string;
  flow_type?: '25th_payment' | 'bank_auto_debit' | 'other';
  is_active?: number;
  notes?: string;
  session_id: string;
}): Promise<{ ok: boolean; item?: PaymentWindow; conflicts?: PaymentWindowOverlapConflict[]; error?: string }> {
  const r = await fetch(`${API_BASE}/api/admin/payment-windows`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    if (r.status === 409) return { ok: false, ...(j as any) };
    throw new Error((j as any)?.error || `createPaymentWindow failed: ${r.status}`);
  }
  return j as any;
}

export async function updatePaymentWindow(
  id: number,
  opts: {
    label?: string;
    receive_from?: string;
    receive_to?: string;
    target_payment_month?: string;
    flow_type?: '25th_payment' | 'bank_auto_debit' | 'other';
    is_active?: number;
    notes?: string;
    session_id: string;
  },
): Promise<{ ok: boolean; item?: PaymentWindow; conflicts?: PaymentWindowOverlapConflict[]; error?: string }> {
  const r = await fetch(`${API_BASE}/api/admin/payment-windows/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    if (r.status === 409) return { ok: false, ...(j as any) };
    throw new Error((j as any)?.error || `updatePaymentWindow failed: ${r.status}`);
  }
  return j as any;
}

export async function deletePaymentWindow(
  id: number,
  hard?: boolean,
  sessionId?: string,
): Promise<{ ok: boolean; deleted?: 'soft' | 'hard' }> {
  const u = new URL(`${API_BASE}/api/admin/payment-windows/${id}`);
  if (hard) u.searchParams.set('hard', '1');
  if (sessionId) u.searchParams.set('session_id', sessionId);
  const r = await fetch(u.toString(), { method: 'DELETE' });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error((j as any)?.error || `deletePaymentWindow failed: ${r.status}`);
  }
  return r.json();
}

export interface BulkOverrideByWindowResponse {
  ok: boolean;
  dry_run: boolean;
  window: PaymentWindow;
  preview: {
    total_in_window: number;
    eligible_for_override: number;
    skip_already_overridden: number;
    skip_processed: number;
    skip_arica_direct_debit: number;
  };
  applied: { inserted: number } | null;
  sample_rids?: number[];
}

export async function bulkOverrideByWindow(opts: {
  window_id: number;
  flow_type?: '25th_payment' | 'bank_auto_debit' | 'other';
  dry_run: boolean;
  exclude_arica_direct_debit?: boolean;
  session_id: string;
}): Promise<BulkOverrideByWindowResponse> {
  const r = await fetch(`${API_BASE}/api/admin/invoice-override/bulk-by-window`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error((j as any)?.error || `bulkOverrideByWindow failed: ${r.status}`);
  }
  return r.json();
}

// ----------------------------------------------------------------------------
// 選択ベース 一括 override (= rowids 直接指定、 window 経由しない)
// reader_a backend: POST /api/admin/invoice-override/bulk-by-rowids
// ----------------------------------------------------------------------------

export interface BulkOverrideByRowidsResponse {
  ok: boolean;
  dry_run: boolean;
  preview: {
    total_requested: number;
    eligible: number;
    skip_already_overridden: number;
    skip_processed: number;
    skip_not_found: number;
  };
  applied: { inserted: number } | null;
  sample_rids?: number[];
}

export async function bulkOverrideByRowids(opts: {
  rowids: number[];
  flow_type?: '25th_payment' | 'bank_auto_debit' | 'other';
  doc_type?: 'invoice' | 'receipt' | 'quotation' | 'order' | 'other';
  skip?: boolean;
  partner_id?: number;
  partner_name?: string;
  reason?: string;
  dry_run: boolean;
  session_id: string;
}): Promise<BulkOverrideByRowidsResponse> {
  const r = await fetch(`${API_BASE}/api/admin/invoice-override/bulk-by-rowids`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error((j as any)?.error || `bulkOverrideByRowids failed: ${r.status}`);
  }
  return r.json();
}

export interface BulkDispatchStartResponse {
  ok: boolean;
  dry_run: boolean;
  batch_id?: string;
  total: number;
  eligible_rids?: number[];
}

export async function startBulkDispatch(opts: {
  window_id?: number;
  rowids?: number[];
  flow_type?: '25th_payment' | 'bank_auto_debit' | 'other';
  concurrency?: number;
  dry_run: boolean;
  force_4_reader?: boolean;
  session_id: string;
}): Promise<BulkDispatchStartResponse> {
  const r = await fetch(`${API_BASE}/api/admin/invoice-bulk-dispatch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error((j as any)?.error || `startBulkDispatch failed: ${r.status}`);
  }
  return r.json();
}

export interface BulkDispatchBatchItem {
  id: number;
  batch_id: string;
  rowid: number;
  status: 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped';
  stage: string | null;
  attempts: number;
  result_summary: string | null;
  error_message: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface BulkDispatchBatchStatus {
  ok: boolean;
  batch: {
    batch_id: string;
    window_id: number;
    flow_type: string;
    concurrency: number;
    force_4_reader: number;
    total: number;
    succeeded: number;
    failed: number;
    in_progress: number;
    status: 'queued' | 'running' | 'finished' | 'aborted';
    started_at: string | null;
    finished_at: string | null;
    created_at: string;
    created_by: string | null;
    notes: string | null;
  };
  items?: BulkDispatchBatchItem[];
}

export async function getBulkDispatchStatus(batchId: string, includeItems?: boolean): Promise<BulkDispatchBatchStatus> {
  const u = new URL(`${API_BASE}/api/admin/invoice-bulk-dispatch/${batchId}`);
  if (includeItems) u.searchParams.set('include_items', '1');
  const r = await fetch(u.toString());
  if (!r.ok) throw new Error(`getBulkDispatchStatus failed: ${r.status}`);
  return r.json();
}

export async function abortBulkDispatch(batchId: string, sessionId: string): Promise<{ ok: boolean; aborted: boolean }> {
  const r = await fetch(`${API_BASE}/api/admin/invoice-bulk-dispatch/${batchId}/abort`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error((j as any)?.error || `abortBulkDispatch failed: ${r.status}`);
  }
  return r.json();
}

export async function resumeBulkDispatch(batchId: string, sessionId: string): Promise<{ ok: boolean; resumed: boolean }> {
  const r = await fetch(`${API_BASE}/api/admin/invoice-bulk-dispatch/${batchId}/resume`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error((j as any)?.error || `resumeBulkDispatch failed: ${r.status}`);
  }
  return r.json();
}

// ---------------------------------------------------------------------------
// Phase 2 (2026-05-19): processing_flags 透明化 API
// ---------------------------------------------------------------------------
export interface ProcessingFlagRow {
  arica_rowid: number;
  ref_number: string;
  current_gate: string | null;
  current_status: string | null;
  vision_reader_outcome: string | null;
  reconcile_verdict: string | null;
  proposal_build_outcome: string | null;
  proposal_status: string | null;
  registration_outcome: string | null;
  canonical_chain_json: string | null;
  past_details_chain_json: string | null;
  template_chain_json: string | null;
  pattern_index_hit_json: string | null;
  skip_decisions_json: string | null;
  account_name_provenance: string | null;
  tax_code_provenance: string | null;
  section_provenance: string | null;
  item_provenance: string | null;
  last_batch_id: string | null;
  last_session_id: string | null;
  gate_violations_history_json: string | null;
  past_details_consultation_required: number;
  past_details_consultation_done: number;
  amount_3way_validation_required: number;
  amount_3way_validation_done: number;
  canonical_provenance_required: number;
  canonical_provenance_done: number;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

export interface ProcessingFlagEvent {
  id: number;
  arica_rowid: number;
  step: string;
  outcome: string;
  reason: string | null;
  evidence_json: string | null;
  actor: string | null;
  batch_id: string | null;
  session_id: string | null;
  created_at: string;
}

export interface ProcessingFlagGetResponse {
  ok: boolean;
  arica_rowid: number;
  flag: ProcessingFlagRow | null;
  events: ProcessingFlagEvent[];
  events_count: number;
}

export interface ProcessingFlagsCoverage {
  ok: boolean;
  total_active: number;
  total_proposed: number;
  total_approved: number;
  total_registered: number;
  total_rejected: number;
  past_details_done_count: number;
  past_details_pct: number;
  canonical_done_count: number;
  canonical_pct: number;
  amount_3way_done_count: number;
  amount_3way_pct: number;
  account_name_provenance_done_count: number;
  account_name_pct: number;
  gate_blocked_count: number;
  gate_blocked_24h_count: number;
  last_24h_event_count: number;
  generated_at: string;
}

// refNumber 形式は 'arica:NNNN' を想定。ARICA rid のみ抽出して GET。
function parseAricaRid(refNumber: string): number | null {
  const m = String(refNumber || '').match(/^arica:(\d+)$/);
  return m ? Number(m[1]) : null;
}

export async function fetchProcessingFlagByRef(
  refNumber: string,
  includeEvents = true,
  limit = 50,
): Promise<ProcessingFlagGetResponse | null> {
  const rid = parseAricaRid(refNumber);
  if (!rid) return null;
  const q = new URLSearchParams();
  if (includeEvents) q.set('include_events', '1');
  if (limit) q.set('limit', String(limit));
  const r = await fetch(`${API_BASE}/api/processing-flags/${rid}?${q.toString()}`);
  if (!r.ok) throw new Error(`fetchProcessingFlagByRef failed: ${r.status}`);
  return r.json();
}

export async function fetchProcessingFlagsCoverage(): Promise<ProcessingFlagsCoverage> {
  const r = await fetch(`${API_BASE}/api/processing-flags/coverage`);
  if (!r.ok) throw new Error(`fetchProcessingFlagsCoverage failed: ${r.status}`);
  return r.json();
}

// ---------------------------------------------------------------------------
// 日報処理 (= スマレジ → 複式仕訳 review/approve, worker /api/nippou/journals)
// 2026-06-09: 純フロントエンド表示。 freee 直接呼出なし・仕訳生成は worker 側。
//   approve は本段では status 更新のみ (= freee 登録は後段で結線予定)。
// ---------------------------------------------------------------------------

export type NippouJournalStatus =
  | 'suggested' | 'proposed' | 'approved' | 'rejected' | 'registered' | 'error'
  | 'held_kakeuri' | 'held_unknown' | 'held_imbalance' | 'blocked_no_bank' | string;

export interface NippouDealDetail {
  entry_side: 'debit' | 'credit';
  account_item_id?: number | null;
  item_id?: number | null;
  tax_code?: number | null;
  amount: number;
  vat?: number | null;
  description?: string | null;
  accountName?: string | null;
  itemName?: string | null;
  taxCodeName?: string | null;
  sectionName?: string | null;
  // 明細単位の取引先 (= deal-level partnerName より優先)。 未付与の場合あり。
  partner_id?: number | null;
  partnerName?: string | null;
}

export interface NippouDeal {
  type: 'income' | 'expense';
  section_id?: number | null;
  partner_id?: number | null;
  partnerName?: string | null;
  details: NippouDealDetail[];
  payments?: Array<{ amount: number }>;
}

export interface NippouJournal {
  id: number;
  section_id: number | null;
  store_name: string | null;
  business_date: string;
  deals_json: NippouDeal[];
  debit_total: number | null;
  credit_total: number | null;
  balanced: number; // 0 | 1
  gross_tax_include: number | null;
  discrepancy: number | null;
  status: NippouJournalStatus;
  freee_deal_ids: number[] | null;
  audit_json: any | null;
  session_id?: string | null;
  created_by?: string | null;
  reject_reason: string | null;
  created_at: string;
  updated_at?: string | null;
  registered_at?: string | null;
}

export interface NippouJournalsResponse {
  ok: boolean;
  total: number;
  items: NippouJournal[];
  meta: { by_section: Record<string, number>; by_status?: Record<string, number>; balanced_count: number };
}

export async function fetchNippouJournals(
  yearMonth: string,
  sectionId?: number | string | null,
  status?: NippouJournalStatus | '',
): Promise<NippouJournalsResponse> {
  const q = new URLSearchParams();
  if (yearMonth) q.set('year_month', yearMonth);
  if (sectionId !== undefined && sectionId !== null && String(sectionId) !== '') {
    q.set('section_id', String(sectionId));
  }
  if (status) q.set('status', status);
  return request<NippouJournalsResponse>(`/api/nippou/journals?${q}`);
}

// 日付範囲版 (= 自動生成システム UI 用。 date_from/date_to は year_month より優先)。
export async function fetchNippouJournalsRange(
  dateFrom: string,
  dateTo: string,
  sectionId?: number | string | null,
  status?: NippouJournalStatus | '',
): Promise<NippouJournalsResponse> {
  const q = new URLSearchParams();
  if (dateFrom) q.set('date_from', dateFrom);
  if (dateTo) q.set('date_to', dateTo);
  if (sectionId !== undefined && sectionId !== null && String(sectionId) !== '') {
    q.set('section_id', String(sectionId));
  }
  if (status) q.set('status', status);
  return request<NippouJournalsResponse>(`/api/nippou/journals?${q}`);
}

export async function fetchNippouJournal(id: number): Promise<{ ok: boolean; item: NippouJournal }> {
  return request(`/api/nippou/journals/${id}`);
}

export interface NippouApproveResult {
  ok: boolean;
  id?: number;
  status?: string; // 'registered' | 'error' | ...
  note?: string;
  already?: boolean;
  freee_deal_ids?: number[];
  deal_count?: number;
  // partial_register_failed (HTTP 502) 時:
  error?: string;
  failed_deal_seq?: number;
  registered_freee_deal_ids?: number[];
}

// 段3配線済: dry_run=false を明示し worker が実際に freee /api/1/deals へ登録する。
//   ※実 freee 登録は user の承認 click 時のみ (R2 [[feedback_verification_cleanup]] 準拠)。
export async function approveNippouJournal(
  id: number,
  sessionId: string | null,
  approvedBy?: string,
): Promise<NippouApproveResult> {
  // timeout 90s (2026-07-13): 1 journal ≤ ~12 deals ≈ 数秒〜十数秒。 ハングで UI が固まるのを防ぐ。
  //   timeout でも worker は deal 単位で freee_deal_ids を永続化しており、 再承認は resume で安全。
  const t = requestTimeout(90000);
  try {
    return await request(`/api/nippou/journals/${id}/approve`, {
      method: 'POST',
      signal: t.signal,
      body: JSON.stringify({
        session_id: sessionId,
        approved_by: approvedBy || 'web_app_user',
        dry_run: false,
      }),
    });
  } finally {
    t.clear();
  }
}

export async function rejectNippouJournal(
  id: number,
  reason: string,
  sessionId: string | null,
): Promise<{ ok: boolean; id: number; status: string }> {
  return request(`/api/nippou/journals/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason || null, session_id: sessionId }),
  });
}

// ---------------------------------------------------------------------------
// 日報 自動生成システム拡張 (2026-06-15, hash #nippou)
//   worker N-P1〜N-P7 の generate / held(掛売・未知) / blocked(銀行未取得) / 学習 /
//   一括承認 / bank 再評価 endpoint を結線。 既存 fetch/approve/reject 関数は不変。
//   ※ 実 freee 登録 (approve/bulk-approve dry_run:false) は UI 承認クリック起点のみ (R2 準拠)。
// ---------------------------------------------------------------------------

// 掛売 directive 確定値 (worker N-P4 / [[feedback_nippou_payment_mapping]])
export const NIPPOU_KAKEURI_ACCOUNTS = [
  { value: 858355710, name: '売掛金 (858355710)' },
  { value: 891995955, name: '未収金 (891995955)' },
] as const;

// generate counts (worker /api/nippou/generate の counts)
export interface NippouGenerateCounts {
  generated: number; no_data: number; held: number; error: number;
  upserted: number; already_registered: number; skipped: number;
  proposed: number; held_kakeuri: number; held_unknown: number; held_imbalance: number;
  blocked_no_bank: number; blocked_store_days: number;
  kakeuri_instances: number; learning_pending: number;
  bank_pending_upserted: number; bank_pending_resolved: number;
  [k: string]: number;
}
export interface NippouGenerateResult {
  ok: boolean;
  dry_run: boolean;
  detect_held?: boolean;
  detect_blocked?: boolean;
  date_from: string;
  date_to: string;
  sections: number[];
  day_count: number;
  store_count: number;
  counts: NippouGenerateCounts;
  results: any[];
  note?: string;
}

// POST /api/nippou/generate — date 範囲 × 全(指定)section を生成し nippou_journal へ upsert。
//   dry_run:false が既定運用 (= D1 へ反映)。 detect_held/detect_blocked は既定 true。
export async function generateNippouJournals(body: {
  date_from: string;
  date_to: string;
  sections?: number[];
  dry_run?: boolean;
  detect_held?: boolean;
  detect_blocked?: boolean;
  force?: boolean;
  session_id?: string | null;
}, opts?: { signal?: AbortSignal }): Promise<NippouGenerateResult> {
  return request('/api/nippou/generate', {
    method: 'POST',
    ...(opts?.signal ? { signal: opts.signal } : {}),
    body: JSON.stringify({
      date_from: body.date_from,
      date_to: body.date_to,
      ...(body.sections && body.sections.length ? { sections: body.sections } : {}),
      dry_run: body.dry_run === true,
      detect_held: body.detect_held !== false,
      detect_blocked: body.detect_blocked !== false,
      ...(body.force ? { force: true } : {}),
      ...(body.session_id ? { session_id: body.session_id } : {}),
      created_by: 'web_app_user',
    }),
  });
}

// 承認 dry_run (= 送信予定 freee deal payload + Σ検算)。 freee に POST しない。
//   worker は summary.per_deal[] + payloads[] を返す (月末の validation/payload とは別形)。
export interface NippouApproveDryRunAccount {
  account_item_id: number;
  tax_code: number;
  amount: number;
  entry_side: 'debit' | 'credit';
  section_id: number | null;
  item_id: number | null;
  tag_ids: number[] | null;
}
export interface NippouApproveDryRunDeal {
  seq: number;
  type: 'income' | 'expense';
  ref_number: string;
  partner_id: number | null;
  accounts: NippouApproveDryRunAccount[];
  payments: Array<{ amount: number; [k: string]: any }>;
  sums: { debit: number; credit: number; payments: number };
}
export interface NippouApproveDryRun {
  ok: boolean;
  dry_run: true;
  id: number;
  status: string;
  errors: string[];
  summary: {
    id: number;
    section_id: number | null;
    store_name: string | null;
    business_date: string;
    deal_count: number;
    sum_credit: number;
    sum_debit: number;
    debit_total_stored: number | null;
    credit_total_stored: number | null;
    gross_tax_include: number | null;
    balanced: boolean;
    per_deal: NippouApproveDryRunDeal[];
  };
  payloads: any[];
  note?: string;
}

export async function approveNippouJournalDryRun(id: number): Promise<NippouApproveDryRun> {
  return request(`/api/nippou/journals/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ dry_run: true }),
  });
}

// ===== 掛売 都度指示 (kakeuri-directive) =====
export interface NippouKakeuriDirectiveResult {
  ok: boolean;
  journal_id: number;
  directive_id?: number;
  patched_lines: number;
  balanced: boolean;
  remaining_unresolved: number;
  pending_directives: number;
  has_unknown_factor: boolean;
  status: string;
  note?: string;
}
//   2026-07 拡張 (後方互換): no_partner (取引先なし=タグ管理系勘定・ブランド不明クレジット用) /
//   item_id (品目) / add_tag_ids (メモタグ複数)。 worker 側は対応済 (POST /api/nippou/kakeuri-directive)。
//   従来呼出 (partner_id 数値のみ) はそのまま動く。 no_partner:true 時は partner_id を null で送る。
//   2026-07-13 追加: line_note (備考 = freee 摘要へ入る)。 既存 policy_note は方針メモ (監査用) のまま。
export async function saveNippouKakeuriDirective(body: {
  journal_id: number;
  line_ref?: string | null;
  account_item_id: number;
  partner_id?: number | null;
  no_partner?: boolean;
  item_id?: number | null;
  add_tag_ids?: number[] | null;
  policy_note?: string;
  line_note?: string | null;
  decided_by?: string;
}): Promise<NippouKakeuriDirectiveResult> {
  return request('/api/nippou/kakeuri-directive', {
    method: 'POST',
    body: JSON.stringify({
      journal_id: body.journal_id,
      line_ref: body.line_ref ?? null,
      account_item_id: body.account_item_id,
      partner_id: body.no_partner ? null : (body.partner_id ?? null),
      ...(body.no_partner ? { no_partner: true } : {}),
      item_id: body.item_id ?? null,
      ...(body.add_tag_ids && body.add_tag_ids.length ? { add_tag_ids: body.add_tag_ids } : {}),
      policy_note: body.policy_note || null,
      line_note: body.line_note || null,
      decided_by: body.decided_by || 'web_app_user',
    }),
  });
}

// ===== 未知分類 学習 (learn-classification) =====
export async function saveNippouClassification(body: {
  kind: string;
  contract_id?: string | number | null;
  source_key: string;
  category: string;
  account_item_id?: number | null;
  partner_id?: number | null;
  item_id?: number | null;
  source_name?: string | null;
  decided_by?: string;
}): Promise<{ ok: boolean; id: number; kind: string; contract_id: string | null; source_key: string; status: string }> {
  return request('/api/nippou/learn-classification', {
    method: 'POST',
    body: JSON.stringify({
      kind: body.kind,
      contract_id: body.contract_id ?? null,
      source_key: body.source_key,
      category: body.category,
      account_item_id: body.account_item_id ?? null,
      partner_id: body.partner_id ?? null,
      item_id: body.item_id ?? null,
      source_name: body.source_name ?? null,
      decided_by: body.decided_by || 'web_app_user',
    }),
  });
}

// ===== 学習 pending 台帳 (GET /api/nippou/learning) =====
export interface NippouLearningRow {
  id: number;
  kind: string;
  contract_id: string | null;
  source_key: string;
  source_name: string | null;
  category: string | null;
  account_item_id: number | null;
  partner_id: number | null;
  item_id: number | null;
  status: string;
  decided_by: string | null;
  confirmed_at: string | null;
  [k: string]: any;
}
export async function fetchNippouLearning(
  status?: string,
  kind?: string,
): Promise<{ ok: boolean; total: number; items: NippouLearningRow[]; meta: { by_status: Record<string, number> } }> {
  const q = new URLSearchParams();
  if (status) q.set('status', status);
  if (kind) q.set('kind', kind);
  return request(`/api/nippou/learning?${q}`);
}

// ===== 銀行/入金機 未取得 pending 台帳 (GET /api/nippou/pending-bank) =====
export interface NippouBankPendingRow {
  id: number;
  section_id: number | null;
  store_name: string | null;
  business_date: string;
  expected_deposit_date: string | null;
  cash_total: number | null;
  reason: string | null;
  status: string;
  [k: string]: any;
}
export async function fetchNippouBankPending(
  status?: string,
  sectionId?: number | string | null,
): Promise<{ ok: boolean; total: number; items: NippouBankPendingRow[]; meta: { by_reason: Record<string, number> } }> {
  const q = new URLSearchParams();
  if (status) q.set('status', status);
  if (sectionId !== undefined && sectionId !== null && String(sectionId) !== '') q.set('section_id', String(sectionId));
  return request(`/api/nippou/pending-bank?${q}`);
}

// ===== bank 再評価 (POST /api/nippou/bank-reeval) =====
export interface NippouBankReevalResult {
  ok: boolean;
  today?: string;
  counts?: { evaluated: number; resolved: number; still_blocked: number; escalated: number; registered_skip: number; error: number; no_data: number; [k: string]: number };
  results?: any[];
  [k: string]: any;
}
export async function reevalNippouBank(body: {
  date_from?: string;
  date_to?: string;
  section?: number | null;
  session_id?: string | null;
}): Promise<NippouBankReevalResult> {
  return request('/api/nippou/bank-reeval', {
    method: 'POST',
    body: JSON.stringify({
      ...(body.date_from ? { date_from: body.date_from } : {}),
      ...(body.date_to ? { date_to: body.date_to } : {}),
      ...(body.section != null ? { section: body.section } : {}),
      ...(body.session_id ? { session_id: body.session_id } : {}),
    }),
  });
}

// ===== 日報 一括承認・登録 (bulk-approve) =====
export interface NippouBulkDryRunItem {
  id: number;
  section_id: number | null;
  store_name: string | null;
  business_date: string;
  debit_total: number | null;
  credit_total: number | null;
}
export interface NippouBulkDryRun {
  ok: boolean;
  dry_run: true;
  date_from: string;
  date_to: string;
  eligible_count: number;
  not_eligible: Record<string, number>;
  note?: string;
  items: NippouBulkDryRunItem[];
}
export interface NippouBulkResult {
  ok: boolean;
  date_from: string;
  date_to: string;
  registered_count: number;
  failed_count: number;
  // 2026-07-13: server 側 1 リクエスト上限 (journal ≤50 / 累積 deal ≤200) 導入。
  //   remaining_count > 0 の間、 client は同条件で再リクエストして続きを処理する
  //   (登録済は WHERE で自然に消える = resume 安全)。
  processed_count?: number;
  remaining_count?: number;
  not_eligible: Record<string, number>;
  results: any[];
}
// AbortController timeout (2026-07-13 監査 fix: request() に timeout がなく 1 リクエストの
//   ハングが bulk ループ全体を無期限停止させていた)。 caller は clear() を finally で呼ぶ。
function requestTimeout(ms: number): { signal: AbortSignal; clear: () => void } {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  return { signal: ctrl.signal, clear: () => clearTimeout(timer) };
}
// dry_run:true = 登録対象一覧 (proposed + stale approved 遺骸・balanced)。 freee 非 POST。
export async function bulkApproveNippouDryRun(
  dateFrom: string,
  dateTo: string,
  sections?: number[],
): Promise<NippouBulkDryRun> {
  const t = requestTimeout(60000);
  try {
    return await request('/api/nippou/bulk-approve', {
      method: 'POST',
      signal: t.signal,
      body: JSON.stringify({
        date_from: dateFrom,
        date_to: dateTo,
        ...(sections && sections.length ? { sections } : {}),
        dry_run: true,
      }),
    });
  } finally {
    t.clear();
  }
}
// dry_run:false = 対象を順次 freee 登録 (session lease 必須・UI クリック起点のみ=R2)。
//   server が 1 リクエスト上限で丸めて remaining_count を返す (呼び側は >0 の間ループ)。
//   timeout 240s: 上限一杯 (deal ≈200 × ~0.5s) でも収まる。 timeout 時も worker 側は
//   deal 単位で freee_deal_ids を永続化しており、 再実行は resume (登録済スキップ) で安全。
export async function bulkApproveNippou(
  dateFrom: string,
  dateTo: string,
  sessionId: string | null,
  sections?: number[],
): Promise<NippouBulkResult> {
  const t = requestTimeout(240000);
  try {
    return await request('/api/nippou/bulk-approve', {
      method: 'POST',
      signal: t.signal,
      body: JSON.stringify({
        date_from: dateFrom,
        date_to: dateTo,
        ...(sections && sections.length ? { sections } : {}),
        session_id: sessionId,
        approved_by: 'web_app_user',
        dry_run: false,
      }),
    });
  } finally {
    t.clear();
  }
}

// ---------------------------------------------------------------------------
// 出納帳 (Cash Ledger) — schema-v67 (2026-06-11)
//   西武信用金庫現金管理 等の wallet_txn を自動取込し、各行に 用途/備考/処理済 を入力。
// ---------------------------------------------------------------------------
export interface CashLedgerRow {
  wallet_txn_id: number;
  date: string;
  amount: number;
  entry_side: 'income' | 'expense';
  balance: number | null;
  description: string | null;
  status_raw: 1 | 2;
  rule_matched: number;
  purpose: string;
  memo: string;
  is_done: boolean;
  updated_by: string | null;
  updated_at: string | null;
}

export interface CashLedgerResponse {
  walletable_id: number;
  walletable_name: string;
  month: string;
  total: number;
  done_count: number;
  undone_count: number;
  income_total: number;
  expense_total: number;
  closing_balance: number | null;
  items: CashLedgerRow[];
}

export async function fetchCashLedger(walletableId: number, month: string): Promise<CashLedgerResponse> {
  const q = new URLSearchParams({ walletable_id: String(walletableId), month });
  return request<CashLedgerResponse>(`/api/cash-ledger?${q}`);
}

export async function saveCashLedgerAnnotation(body: {
  wallet_txn_id: number;
  walletable_id?: number;
  purpose?: string;
  memo?: string;
  is_done?: boolean;
  updated_by?: string;
}): Promise<{ ok: boolean; wallet_txn_id: number; purpose: string | null; memo: string | null; is_done: boolean }> {
  return request(`/api/cash-ledger/annotate`, {
    method: 'POST',
    body: JSON.stringify({ updated_by: 'web_app_user', ...body }),
  });
}

// ---------------------------------------------------------------------------
// 月末仕訳 (Month-End Journal) — schema-v70 (2026-06-13)
//   定額/在庫/按分/前払/社員割引/原価率 の約22 kind を freee 振替伝票 (manual_journals)
//   で計上。生成→確認→承認→登録 chain。dry_run:true=送信予定payload+Σ検算 / dry_run:false=実登録。
// ---------------------------------------------------------------------------

export type MonthEndJournalStatus =
  | 'draft' | 'suggested' | 'proposed' | 'approved'
  | 'registered' | 'rejected' | 'error' | 'needs_user_input';

export interface MonthEndJournalLine {
  entry_side: 'debit' | 'credit';
  account_item_id: number;
  account_name?: string | null;
  amount: number;
  tax_code: number;
  tax_code_name?: string | null;
  section_id?: number | null;
  section_name?: string | null;
  item_id?: number | null;
  item_name?: string | null;
  partner_id?: number | null;
  partner_name?: string | null;
  vat?: number | null;
  description?: string | null;
}

export interface MonthEndJournal {
  id: number;
  year_month: string;
  journal_kind: string;
  title: string | null;
  status: MonthEndJournalStatus;
  issue_date: string;
  lines: MonthEndJournalLine[] | string;
  total_debit: number;
  total_credit: number;
  balanced: number; // 0 | 1
  adjustment: number; // 0 | 1
  source_meta: any | null;
  freee_mj_id: number | null;
  idempotency_key?: string | null;
  error_message: string | null;
  created_at: string;
  updated_at?: string | null;
  approved_at?: string | null;
  registered_at?: string | null;
}

export interface MonthEndJournalsResponse {
  ok: boolean;
  total: number;
  items: MonthEndJournal[];
  meta?: { by_status?: Record<string, number>; balanced_count?: number };
}

export async function fetchMonthEndJournals(
  yearMonth: string,
  opts?: { status?: MonthEndJournalStatus | ''; journalKind?: string },
): Promise<MonthEndJournalsResponse> {
  const q = new URLSearchParams();
  if (yearMonth) q.set('year_month', yearMonth);
  if (opts?.status) q.set('status', opts.status);
  if (opts?.journalKind) q.set('journal_kind', opts.journalKind);
  return request<MonthEndJournalsResponse>(`/api/month-end/journals?${q}`);
}

export async function fetchMonthEndJournal(id: number): Promise<{ ok: boolean; item: MonthEndJournal }> {
  return request(`/api/month-end/journals/${id}`);
}

export interface MonthEndGenerateResult {
  ok: boolean;
  year_month?: string;
  generated?: number;
  kinds?: string[];
  results?: any[];
  [k: string]: any;
}

export async function generateMonthEndJournals(
  yearMonth: string,
  kinds?: string[],
): Promise<MonthEndGenerateResult> {
  const body: { year_month: string; kinds?: string[] } = { year_month: yearMonth };
  if (kinds && kinds.length) body.kinds = kinds;
  return request(`/api/month-end/generate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function upsertMonthEndJournal(
  id: number,
  body: { lines?: MonthEndJournalLine[]; title?: string; issue_date?: string; source_meta?: any },
): Promise<{ ok: boolean; item?: MonthEndJournal }> {
  return request(`/api/month-end/journals/${id}/upsert`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface MonthEndApproveDryRun {
  ok: boolean;
  dry_run: true;
  id: number;
  journal_kind: string;
  year_month: string;
  status: MonthEndJournalStatus;
  errors: string[];
  validation: {
    balanced: boolean;
    total_debit: number;
    total_credit: number;
    issue_date: string;
    expected_issue_date: string;
    issue_date_ok: boolean;
    line_count: number;
  };
  payload: {
    company_id: number;
    issue_date: string;
    adjustment: boolean;
    ref_number: string;
    details: Array<{
      entry_side: 'debit' | 'credit';
      account_item_id: number;
      tax_code: number;
      amount: number;
      description?: string;
      section_id?: number;
      item_id?: number;
      partner_id?: number;
      vat?: number;
    }>;
  };
  note?: string;
}

export interface MonthEndApproveResult {
  ok: boolean;
  dry_run: false;
  id: number;
  journal_kind?: string;
  status?: MonthEndJournalStatus;
  freee_mj_id?: number | null;
  already?: boolean;
  error?: string;
  errors?: string[];
  note?: string;
}

// dry_run:true = 送信予定 payload + Σ検算 + 検証結果を返すのみ (freee POST しない)
export async function approveMonthEndJournalDryRun(id: number): Promise<MonthEndApproveDryRun> {
  return request(`/api/month-end/journals/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ dry_run: true }),
  });
}

// dry_run:false = freee 振替伝票 実登録 (session lease 必須)。
//   ※実 freee 登録は user の承認 click 起点のみ (R2 [[feedback_verification_cleanup]] 準拠)。
export async function approveMonthEndJournal(
  id: number,
  sessionId: string | null,
): Promise<MonthEndApproveResult> {
  return request(`/api/month-end/journals/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({
      session_id: sessionId,
      approved_by: 'web_app_user',
      dry_run: false,
    }),
  });
}

export async function rejectMonthEndJournal(
  id: number,
  reason: string,
  sessionId: string | null,
): Promise<{ ok: boolean; id: number; status: string }> {
  return request(`/api/month-end/journals/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason || null, session_id: sessionId }),
  });
}

export interface MonthEndSetting {
  setting_key: string;
  effective_year_month: string | null;
  value: any;
  note?: string | null;
}

export async function fetchMonthEndSettings(
  settingKey: string,
  yearMonth: string,
): Promise<{ ok: boolean; setting_key: string; year_month: string; item: MonthEndSetting | null }> {
  const q = new URLSearchParams({ setting_key: settingKey, year_month: yearMonth });
  return request(`/api/month-end/settings?${q}`);
}

export async function saveMonthEndSetting(body: {
  setting_key: string;
  effective_year_month: string;
  value_json: any;
  note?: string;
}): Promise<{ ok: boolean; setting_key: string; effective_year_month: string }> {
  return request(`/api/month-end/settings`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function openSessionForMonthEnd(): Promise<{ session_id: string; audit_ok_until: string }> {
  return request('/api/claude/session/open', {
    method: 'POST',
    body: JSON.stringify({ workspace_id: 'kumagai-corp' }),
  });
}

// ===== 月末仕訳 一括承認・登録 (bulk-approve) =====
export interface MonthEndBulkDryRunItem {
  id: number;
  journal_kind: string;
  title?: string;
  total_debit: number;
  total_credit: number;
  issue_date?: string;
}
export interface MonthEndBulkDryRun {
  ok: boolean;
  dry_run: true;
  year_month: string;
  eligible_count: number;
  grand_total_debit: number;
  not_eligible: Record<string, number>;
  has_yamashiroya: boolean;
  note?: string;
  items: MonthEndBulkDryRunItem[];
}
export interface MonthEndBulkResultItem {
  id: number;
  journal_kind: string;
  ok: boolean;
  status?: string;
  freee_mj_id?: number | null;
  error?: string;
  errors?: string[];
  skipped?: boolean;
  reason?: string;
  regenerated?: boolean;
}
export interface MonthEndBulkResult {
  ok: boolean;
  year_month: string;
  registered_count: number;
  failed_count: number;
  grand_total_debit?: number;
  results: MonthEndBulkResultItem[];
}

// 一括登録 dry_run (= 登録対象一覧。freee 非 POST)
export async function bulkApproveMonthEndDryRun(yearMonth: string): Promise<MonthEndBulkDryRun> {
  return request('/api/month-end/bulk-approve', {
    method: 'POST',
    body: JSON.stringify({ year_month: yearMonth, dry_run: true }),
  });
}
// 一括登録 実行 (= proposed+balanced を順次 freee 登録。session lease 必須。UI クリック起点のみ=R2 準拠)
export async function bulkApproveMonthEnd(
  yearMonth: string,
  sessionId: string | null,
): Promise<MonthEndBulkResult> {
  return request('/api/month-end/bulk-approve', {
    method: 'POST',
    body: JSON.stringify({ year_month: yearMonth, session_id: sessionId, dry_run: false }),
  });
}

// ---------------------------------------------------------------------------
// 支払突合表 (Payment Aging / 年齢表) — schema-v72 (2026-06-24)
//   freee の取引(deal)を 業者 × 決済期日(due_date)月 でクロス集計し、3区分
//   (買掛金/未払費用/家賃・顧問料)別に表示する年齢表。マトリクス本体は freee 真値を
//   都度集計(D1 キャッシュなし)。D1 が持つのは 業者→区分 の人手確定(override)のみ。
// ---------------------------------------------------------------------------

// 区分定数 (UI 全体で共有。表示順 = この順)
export const PAYMENT_CATEGORIES = ['買掛金', '未払費用', '家賃・顧問料'] as const;
export type PaymentCategory = typeof PAYMENT_CATEGORIES[number];

export interface PaymentAgingCellCheck {
  checked: boolean;
  checked_by?: string | null;
  checked_at?: string | null;
  stale: boolean;
  stale_reason?: string | null;
}

export interface PaymentAgingCell {
  unsettled: number;
  settled: number;
  due: number;
  deal_ids: number[];
  settled_deal_ids: number[];
  /** チェック済みマーク (2026-07-22 worker 付与。未チェックセルは undefined) */
  check?: PaymentAgingCellCheck;
}

export interface PaymentAgingVendorRow {
  partner_id: number;
  partner_name: string;
  category_source: 'user_input' | 'auto_default' | 'unassigned';
  confidence: number | null;
  cells: Record<string, PaymentAgingCell>;
  row_total: { unsettled: number; settled: number; due: number };
}

export interface PaymentAgingCategoryGroup {
  category: string;
  vendors: PaymentAgingVendorRow[];
  subtotal: {
    by_month: Record<string, { unsettled: number; settled: number; due: number }>;
    total: { unsettled: number; settled: number; due: number };
  };
}

export interface PaymentAgingResponse {
  ok: boolean;
  header: {
    end_month: string;
    months: number;
    month_keys: string[];
    month_labels: string[];
    status: 'both' | 'unsettled' | 'settled';
    pay_day?: '25' | 'all';
    generated_at: string;
    company_id: number;
  };
  categories: PaymentAgingCategoryGroup[];
  column_totals: Record<string, { unsettled: number; settled: number; due: number }>;
  grand_total: { unsettled: number; settled: number; due: number };
  unassigned_count: number;
  pending_due_date: Array<{
    partner_id: number;
    partner_name: string;
    deal_id: number;
    issue_date: string;
    estimated_due_date: string | null;
    reason: string;
  }>;
  warnings: string[];
}

export interface PaymentAgingVendor {
  partner_id: number;
  partner_name: string;
  assigned_category: string | null;
  source: string | null;
  suggested_category: string;
  confidence: number;
  top_account_item_id: number | null;
  top_account_name: string | null;
  sample_count: number;
  total_amount: number;
}

export interface PaymentAgingVendorsResponse {
  ok: boolean;
  vendors: PaymentAgingVendor[];
  counts: Record<string, number>;
}

export function fetchPaymentAgingMatrix(
  endMonth: string,
  months: number,
  status: 'both' | 'unsettled' | 'settled',
  payDay: '25' | 'all' = '25',
  categories?: string[],
): Promise<PaymentAgingResponse> {
  const q = new URLSearchParams({ end_month: endMonth, months: String(months), status, pay_day: payDay });
  if (categories && categories.length) q.set('categories', categories.join(','));
  return request<PaymentAgingResponse>(`/api/payment-aging/matrix?${q}`);
}

export function fetchPaymentAgingVendors(
  endMonth: string,
  months: number,
  payDay: '25' | 'all' = '25',
): Promise<PaymentAgingVendorsResponse> {
  const q = new URLSearchParams({ end_month: endMonth, months: String(months), pay_day: payDay });
  return request<PaymentAgingVendorsResponse>(`/api/payment-aging/vendors?${q}`);
}

export function savePaymentVendorCategory(body: {
  partner_id: number;
  partner_name?: string;
  category: string;
  source?: string;
  confidence?: number;
  rationale?: any;
}): Promise<{ ok: boolean; partner_id: number; assigned_category: string; source: string; updated_at: string }> {
  return request('/api/payment-aging/vendor-category', {
    method: 'POST',
    body: JSON.stringify({ assigned_by: 'web_app_user', ...body }),
  });
}

export function bulkSavePaymentVendorCategory(
  assignments: Array<{ partner_id: number; category: string; source?: string; confidence?: number; partner_name?: string }>,
): Promise<{ ok: boolean; applied: number; skipped: Array<{ partner_id: number; reason: string }> }> {
  return request('/api/payment-aging/vendor-category/bulk', {
    method: 'POST',
    body: JSON.stringify({ assignments, assigned_by: 'web_app_user' }),
  });
}

export function clearPaymentVendorCategory(
  partnerId: number,
): Promise<{ ok: boolean; partner_id: number; deleted: boolean }> {
  return request(`/api/payment-aging/vendor-category/${partnerId}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// 支払年齢表 拡張 (2026-07-22): 動的カテゴリ / チェック済みマーク / セル詳細 / 証憑
// ---------------------------------------------------------------------------
export interface PaymentAgingCategoryDef {
  label: string; sort_order: number; builtin: boolean; vendor_count: number;
}

export function fetchPaymentAgingCategories(): Promise<{ ok: boolean; categories: PaymentAgingCategoryDef[] }> {
  return request('/api/payment-aging/categories');
}

export function createPaymentAgingCategory(label: string): Promise<{ ok: boolean; label: string }> {
  return request('/api/payment-aging/categories', { method: 'POST', body: JSON.stringify({ label }) });
}

export function deletePaymentAgingCategory(label: string, force = false): Promise<{ ok: boolean; label: string; cleared_assignments?: number }> {
  return request(`/api/payment-aging/categories/${encodeURIComponent(label)}${force ? '?force=1' : ''}`, { method: 'DELETE' });
}

export function reorderPaymentAgingCategories(labels: string[]): Promise<{ ok: boolean; order: string[] }> {
  return request('/api/payment-aging/categories/reorder', { method: 'POST', body: JSON.stringify({ labels }) });
}

export function setPaymentAgingCheck(body: {
  partner_id: number; month: string; pay_day: '25' | 'all'; checked: boolean;
  snapshot?: { unsettled: number; settled: number; deal_ids: number[]; settled_deal_ids: number[] };
}): Promise<{ ok: boolean; checked: boolean }> {
  return request('/api/payment-aging/check', {
    method: 'POST', body: JSON.stringify({ checked_by: 'web_app_user', ...body }),
  });
}

/** セル詳細: freee 取引をフル明細 (details/receipts + masters 名前解決) で取得 */
export interface PaymentAgingDealDetailLine {
  account_item_id: number; accountName?: string; tax_code: number; taxCodeName?: string;
  amount: number; vat?: number | null; entry_side: 'debit' | 'credit';
  section_id?: number | null; sectionName?: string | null; item_id?: number | null; itemName?: string | null;
  description?: string | null; [k: string]: any;
}
export interface PaymentAgingDealReceipt {
  id: number; mime_type?: string | null; status?: string | null; issue_date?: string | null;
  receipt_metadatum?: { partner_name?: string | null; issue_date?: string | null; amount?: number | null } | null;
  [k: string]: any;
}
export interface PaymentAgingDeal {
  id: number; issue_date: string; due_date?: string | null; amount: number; due_amount?: number | null;
  type: 'expense' | 'income'; status: 'settled' | 'unsettled'; partner_id?: number | null; partnerName?: string | null;
  details: PaymentAgingDealDetailLine[];
  payments?: Array<{ id: number; date: string; from_walletable_type: string; from_walletable_id: number; amount: number }>;
  receipts?: PaymentAgingDealReceipt[];
  [k: string]: any;
}

export function fetchPaymentAgingCell(dealIds: number[]): Promise<{ ok: boolean; deals: PaymentAgingDeal[]; errors: any[] }> {
  return request(`/api/payment-aging/cell?deal_ids=${dealIds.join(',')}`);
}

/** 証憑バイナリの表示 URL (worker が freee /receipts/{id}/download を OAuth proxy) */
export function paymentAgingReceiptUrl(receiptId: number): string {
  return `${API_BASE}/api/payment-aging/receipt/${receiptId}`;
}

// ---------------------------------------------------------------------------
// 小口現金出納帳 (2026-07-22)
// ---------------------------------------------------------------------------
export type PettyCashStatus = 'submitted' | 'analyzing' | 'proposed' | 'needs_review' | 'approved' | 'registered' | 'rejected';
// 帳簿: petty_cash=小口現金 (口座6311747・西武突合) / chief_advance=室長立替金 (口座=現金6248106・メモタグ・手動出金マーク)
export type PettyCashBook = 'petty_cash' | 'chief_advance';
export const PETTY_CASH_CHIEFS = ['長岡亮太', '長沼章太', '村岡正智'] as const;

export interface PettyCashEntry {
  id: number; section_id: number; status: PettyCashStatus;
  book: PettyCashBook; chief_name: string | null;
  has_slip: boolean; receipt_count: number;
  submitted_by: string | null; submitted_at: string; note: string | null;
  occurred_on: string | null; amount: number | null;
  partner_id: number | null; partner_name: string | null;
  partner_unregistered: boolean; invoice_reg_number: string | null;
  proposed_deal: any | null; evidence: any | null; analysis_note: string | null;
  job_id: string | null; week_key: string | null;
  freee_deal_id: number | null; registered_at: string | null;
  withdrawn: boolean; withdrawal_txn_id: number | null; withdrawn_at: string | null;
  comment_count?: number; updated_at: string;
}

export interface PettyCashComment {
  id: number; author: string | null; text: string; kind: 'comment' | 'instruction';
  learn: number; status: 'open' | 'done' | 'dismissed'; created_at: string;
}

export async function submitPettyCashEntry(form: {
  section_id: number; note?: string; slip?: File | Blob | null; receipts: Array<File | Blob>;
  book?: PettyCashBook; chief_name?: string;
}): Promise<{ ok: boolean; id: number; week_key: string; job: any }> {
  const fd = new FormData();
  fd.append('section_id', String(form.section_id));
  if (form.book) fd.append('book', form.book);
  if (form.chief_name) fd.append('chief_name', form.chief_name);
  if (form.note) fd.append('note', form.note);
  if (form.slip) fd.append('slip', form.slip, 'slip.jpg');
  form.receipts.forEach((f, i) => fd.append('receipts', f, `receipt_${i}.jpg`));
  const res = await fetch(`${API_BASE}/api/petty-cash/entries`, { method: 'POST', body: fd });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

export function fetchPettyCashEntries(opts?: { section_id?: number; status?: string; week_key?: string }): Promise<{ ok: boolean; entries: PettyCashEntry[] }> {
  const q = new URLSearchParams();
  if (opts?.section_id) q.set('section_id', String(opts.section_id));
  if (opts?.status) q.set('status', opts.status);
  if (opts?.week_key) q.set('week_key', opts.week_key);
  return request(`/api/petty-cash/entries?${q}`);
}

export function fetchPettyCashEntry(id: number): Promise<{ ok: boolean; entry: PettyCashEntry; comments: PettyCashComment[] }> {
  return request(`/api/petty-cash/entries/${id}`);
}

export function pettyCashImageUrl(entryId: number, slot: 'slip' | `r${number}`): string {
  return `${API_BASE}/api/petty-cash/image/${entryId}/${slot}`;
}

export function postPettyCashComment(entryId: number, body: { text: string; kind?: 'comment' | 'instruction'; learn?: boolean; author?: string }): Promise<{ ok: boolean; comment_id: number; dispatched: any }> {
  return request(`/api/petty-cash/entries/${entryId}/comments`, { method: 'POST', body: JSON.stringify(body) });
}

export function registerPettyCashEntry(entryId: number, sessionId?: string | null): Promise<{ ok: boolean; freee_deal_id?: number; idempotent?: boolean; error?: string; errors?: string[] }> {
  return request(`/api/petty-cash/entries/${entryId}/register`, { method: 'POST', body: JSON.stringify({ session_id: sessionId || null }) });
}

export function rejectPettyCashEntry(entryId: number, reason?: string): Promise<{ ok: boolean }> {
  return request(`/api/petty-cash/entries/${entryId}/reject`, { method: 'POST', body: JSON.stringify({ reason }) });
}

// 手動修正 (freee マスタから選択して直接編集 — AI 再分析なし・freee 書込なし)
export function manualEditPettyCashEntry(entryId: number, body: {
  occurred_on?: string;
  partner_id?: number | null;
  partner_name?: string | null;
  proposed_deal?: { details: any[] };
  updated_by?: string;
}): Promise<{ ok: boolean; entry: PettyCashEntry; error?: string; errors?: string[] }> {
  return request(`/api/petty-cash/entries/${entryId}/manual-edit`, { method: 'POST', body: JSON.stringify(body) });
}

export interface PettyCashLedgerResponse {
  ok: boolean; section_id: number | 'all'; float_amount: number;
  outstanding_total: number; balance: number; unknown_amount_count: number;
  entries: PettyCashEntry[];
}
// sectionId='all' → 全店舗横断 (定額は各店舗合計)
export function fetchPettyCashLedger(sectionId: number | 'all', book: PettyCashBook = 'petty_cash'): Promise<PettyCashLedgerResponse> {
  return request(`/api/petty-cash/ledger?section_id=${sectionId}&book=${book}`);
}

export interface PettyCashWeeklySection {
  section_id: number; total: number; unknown_amount: number; entries: PettyCashEntry[];
  withdrawn: boolean; withdrawal_txn_id: number | null;
}
export function fetchPettyCashWeekly(weekKey?: string, book: PettyCashBook = 'petty_cash'): Promise<{ ok: boolean; week_key: string; book: PettyCashBook; cutoff_note?: string; sections: PettyCashWeeklySection[]; grand_total: number; sweep: any }> {
  const q = new URLSearchParams({ book });
  if (weekKey) q.set('week_key', weekKey);
  return request(`/api/petty-cash/weekly?${q}`);
}

export function fetchPettyCashWeeks(book: PettyCashBook = 'petty_cash'): Promise<{ ok: boolean; weeks: Array<{ week_key: string; n: number; withdrawn_n: number }> }> {
  return request(`/api/petty-cash/weeks?book=${book}`);
}

// 室長立替金の週次出金済 手動マーク (本部現金出金は銀行明細に出ず自動突合不能)
export function markPettyCashWeeklyWithdrawn(weekKey: string, sectionId: number, withdrawn: boolean): Promise<{ ok: boolean; changed: number }> {
  return request('/api/petty-cash/weekly/mark', {
    method: 'POST',
    body: JSON.stringify({ book: 'chief_advance', week_key: weekKey, section_id: sectionId, withdrawn }),
  });
}

export function fetchPettyCashSettings(): Promise<{ ok: boolean; settings: Array<{ section_id: number; float_amount: number }> }> {
  return request('/api/petty-cash/settings');
}
export function savePettyCashSetting(sectionId: number, floatAmount: number): Promise<{ ok: boolean }> {
  return request('/api/petty-cash/settings', { method: 'POST', body: JSON.stringify({ section_id: sectionId, float_amount: floatAmount }) });
}

// ---------------------------------------------------------------------------
// 損益分岐点分析 (実質家賃 / 市場家賃) — 2026-06-25
//   全データは worker 側で freee trial_pl / trial_bs (GET 読取) + D1 month_end_setting
//   (市場家賃) + ROUMU API (勤務時間) を集約して返す。クライアントは描画と派生切替のみ。
//   会社=クマガイコーポレーション (freee company_id=11518223)、80期=2025-10〜2026-09。
//   GET /api/breakeven/report?period=month|half|full&end_month=YYYY-MM[&compare_prev=1][&include_ec=0]
// ---------------------------------------------------------------------------

export type BreakevenPeriod = 'month' | 'half' | 'full';

/**
 * 1 グループ / 店舗の全指標。worker が period 活動 (費用=DB−CR / 収益=CR−DB) ベースで
 * section 横断合算した確定値。市場家賃は D1 month_end_setting を真値とする。
 * 値は全て円 (整数)。勤務時間は時間 (h)。比率は小数 (0.30 = 30%)。
 */
export interface BreakevenMetrics {
  売上: number;
  原材料: number;
  原価率: number;             // 原材料 ÷ 売上 (小数)
  粗利益: number;
  労務費: number;
  販売費: number;
  一般管理費: number;
  営業利益: number;
  市場家賃: number;
  地代家賃: number;           // = 実質家賃
  家賃減額差: number;         // 市場家賃 − 地代家賃
  一般管理費_市場: number;
  営業利益_市場: number;      // = 市場損益 (粗利益 − 一般管理費_市場)
  損益分岐点: number;         // 一般管理費 ÷ 粗利益率
  損益分岐点_市場: number;    // 一般管理費_市場 ÷ 粗利益率
  安全余裕率: number;         // (売上 − 損益分岐点) ÷ 売上 (小数)
  達成率: number;             // 売上 ÷ 損益分岐点 (小数)
  勤務時間: number;
  人時生産性: number;         // 粗利益 ÷ 勤務時間 (円/時)
  減価償却費: number;
  営業外収益: number;
  営業外費用: number;
  経常利益: number;
  銀行返済: number;           // 借入金元本返済 (全社/本部行のみ非0)
  CF: number;                 // 経常利益 + 減価償却費 − 銀行返済
  CF必要売上: number;
}

export interface BreakevenStore {
  name: string;
  section_id?: number | null;
  section_ids?: number[];
  metrics: BreakevenMetrics;
  prior?: BreakevenMetrics | null;
  /** 市場家賃が D1>0 だが当月 trial_pl 投入=0 (月末仕訳未投入)。市場家賃版指標は「未確定」表示。 */
  market_rent_missing?: boolean;
  /** ROUMU 工数未確定 (打刻未締め) → 人時生産性が null 扱い。 */
  roumu_pending?: boolean;
  /** ワイン販売系など工数配分対象外で勤務時間データが存在しない。 */
  no_hours?: boolean;
}

export interface BreakevenGroup {
  group: string;       // company|stores|soba|restaurant|cafe|washoku|nonstore ...
  label?: string;
  stores: BreakevenStore[];
  /** worker が返すグループ合計 (配下 section をまとめて期間活動で算出した確定値)。 */
  metrics?: BreakevenMetrics;
  prior?: BreakevenMetrics | null;
}

export interface BreakevenReport {
  ok?: boolean;
  period: BreakevenPeriod;
  range: { start: string; end: string; label?: string };
  prior_range?: { start: string; end: string; label?: string } | null;
  groups: BreakevenGroup[];
  /** 全社合算 (= section_id 省略の単一 trial_pl + ROUMU/返済 全社)。 */
  company?: BreakevenStore | null;
  /** 市場家賃未投入の section / 月 などの注意文。 */
  warnings?: string[];
}

/**
 * 損益分岐点レポート取得。worker が重い集約 (多数 trial_pl 並列 fetch) を担うので
 * クライアントは 1 リクエストで全グループ × 店舗 × 当期/前年 を受け取る。
 */
export function fetchBreakevenReport(params: {
  period: BreakevenPeriod;
  end_month: string;          // 単月/半期の基準月 'YYYY-MM' (通期は会計期末でも可)
  compare_prev?: boolean;
  include_ec?: boolean;       // 山城屋EC を山城屋合算に含めるか (既定 false)
}): Promise<BreakevenReport> {
  const q = new URLSearchParams({
    period: params.period,
    end_month: params.end_month,
  });
  if (params.compare_prev) q.set('compare_prev', '1');
  if (params.include_ec) q.set('include_ec', '1');
  return request<BreakevenReport>(`/api/breakeven/report?${q.toString()}`);
}

// ---------------------------------------------------------------------------
// 仕訳レビュー (schema-v73, 2026-07-02) — freee deals GET(読取のみ) + 修正指示 D1
// ---------------------------------------------------------------------------
export interface JournalReviewDealDetail {
  id: number; account_item_id: number; tax_code: number;
  section_id?: number | null; item_id?: number | null; tag_ids?: number[];
  amount: number; vat?: number | null; entry_side: 'debit' | 'credit';
  description?: string | null;
}
export interface JournalReviewDeal {
  id: number; issue_date: string; due_date?: string | null;
  type: 'income' | 'expense' | string;
  amount: number; due_amount?: number | null;
  partner_id: number | null; ref_number: string | null; status: string;
  details?: JournalReviewDealDetail[];
  payments?: Array<{ id?: number; date: string; amount: number;
    from_walletable_id: number | null; from_walletable_type: string | null }>;
  first_seen_at?: string | null;  // ≈登録日(FREEEMAN初回把握・近似フォールバック)
  last_seen_at?: string | null;   // ≈更新日(変化検知・近似フォールバック)
  freee_created_at?: string | null; // freee真値 (仕訳帳generic_v2 作成日時, schema-v74)
  freee_updated_at?: string | null; // freee真値 (仕訳帳generic_v2 更新日時)
  ts_source?: 'freee' | 'approx';
  // freee deal に添付済みの証憑 (単体GET/一覧が返す場合のみ。証憑✓チップの初期件数に使用)
  receipts?: Array<{ id: number; [k: string]: any }> | null;
}
export type JournalCorrectionStatus = 'open' | 'in_progress' | 'done' | 'dismissed';
export interface JournalCorrection {
  id: number; company_id: number; deal_id: number; instruction_text: string;
  status: JournalCorrectionStatus;
  created_by: string | null; created_at: string; updated_at: string;
}

export type JournalReviewDateField = 'issue' | 'due' | 'created' | 'updated';
export async function fetchJournalReviewDeals(opts: {
  start: string; end: string; dateField?: JournalReviewDateField;
  companyId?: number; limit?: number; offset?: number;
}): Promise<{ deals: JournalReviewDeal[]; meta: { total_count?: number } | null }> {
  const q = new URLSearchParams({
    company_id: String(opts.companyId ?? 11518223),
    start: opts.start, end: opts.end,
    date_field: opts.dateField ?? 'issue',
    limit: String(opts.limit ?? 100),
  });
  if (opts.offset) q.set('offset', String(opts.offset));
  return request(`/api/journal-review/deals?${q}`);
}

// 全ページ取得 (freee limit=100 固定 → offset loop。fetchAllFreeePartners と同型)
export async function fetchAllJournalReviewDeals(opts: {
  start: string; end: string; dateField?: JournalReviewDateField;
}): Promise<JournalReviewDeal[]> {
  const all: JournalReviewDeal[] = [];
  let offset = 0;
  for (let i = 0; i < 50; i++) {
    const r = await fetchJournalReviewDeals({ ...opts, offset, limit: 100 });
    const items = r.deals || [];
    all.push(...items);
    const total = r.meta?.total_count;
    if (items.length < 100 || (total != null && all.length >= total)) break;
    offset += 100;
  }
  return all;
}

// 展開時の詳細 lazy fetch (list mode は details[].description を返さないため)
export async function fetchJournalReviewDealDetail(dealId: number): Promise<JournalReviewDeal | null> {
  const r = await request<{ deal?: JournalReviewDeal }>(
    `/api/freee-proxy/api/1/deals/${dealId}?company_id=${FREEE_COMPANY_ID}&accruals=with`,
  );
  return r.deal || null;
}

export async function fetchJournalCorrections(opts?: {
  companyId?: number; dealId?: number; status?: JournalCorrectionStatus;
  /** 表示中の deal に絞る (?deal_ids=1,2,3 — worker 並行実装。未対応 worker は無視して全件返すだけなので安全) */
  dealIds?: number[];
}): Promise<{ corrections: JournalCorrection[] }> {
  const q = new URLSearchParams({ company_id: String(opts?.companyId ?? 11518223) });
  if (opts?.dealId) q.set('deal_id', String(opts.dealId));
  if (opts?.dealIds && opts.dealIds.length) q.set('deal_ids', opts.dealIds.join(','));
  if (opts?.status) q.set('status', opts.status);
  const r = await request<any>(`/api/journal-review/corrections?${q}`);
  // 防御的: { corrections } | { items } | 配列直 のいずれにも耐える (worker 並行実装)
  const arr = Array.isArray(r) ? r
    : Array.isArray(r?.corrections) ? r.corrections
    : Array.isArray(r?.items) ? r.items : [];
  return { corrections: arr as JournalCorrection[] };
}

export async function createJournalCorrection(body: {
  deal_id: number; instruction_text: string; created_by?: string; company_id?: number;
  /** false = 一回限りの適用(今後のルールとして学習しない)。省略時は true 扱い */
  learn?: boolean;
}): Promise<{ ok: boolean; id: number | null; job?: { job_id?: string; error?: string } | null }> {
  return request('/api/journal-review/corrections', {
    method: 'POST', body: JSON.stringify(body),
  });
}

export async function updateJournalCorrectionStatus(
  id: number,
  status: JournalCorrectionStatus,
): Promise<{ ok: boolean; id: number; status: string }> {
  return request(`/api/journal-review/corrections/${id}/status`, {
    method: 'POST', body: JSON.stringify({ status }),
  });
}

// ---------------------------------------------------------------------------
// レビュー済みマーク (POST /api/journal-review/marks — worker 並行実装。
// 応答形は防御的に読む: { marks } | { items } | 配列直、reviewed 明示 false/0 のみ未レビュー)
// ---------------------------------------------------------------------------
export interface JournalReviewMark {
  deal_id: number;
  reviewed: boolean;
  marked_by?: string | null;
  marked_at?: string | null;
}

export async function fetchJournalReviewMarks(opts?: {
  companyId?: number; dealIds?: number[];
}): Promise<JournalReviewMark[]> {
  const q = new URLSearchParams({ company_id: String(opts?.companyId ?? 11518223) });
  if (opts?.dealIds && opts.dealIds.length) q.set('deal_ids', opts.dealIds.join(','));
  const r = await request<any>(`/api/journal-review/marks?${q}`);
  const arr: any[] = Array.isArray(r) ? r
    : Array.isArray(r?.marks) ? r.marks
    : Array.isArray(r?.items) ? r.items : [];
  const out: JournalReviewMark[] = [];
  for (const m of arr) {
    const dealId = Number(m?.deal_id ?? m?.dealId ?? m?.id);
    if (!Number.isFinite(dealId) || dealId <= 0) continue;
    const rv = m?.reviewed ?? m?.is_reviewed ?? m?.mark;
    out.push({
      deal_id: dealId,
      // マーク行が存在 = レビュー済み扱い (reviewed が明示的に false/0 の行のみ未レビュー)
      reviewed: !(rv === false || rv === 0 || rv === '0' || rv === 'false'),
      marked_by: m?.marked_by ?? m?.created_by ?? null,
      marked_at: m?.marked_at ?? m?.updated_at ?? m?.created_at ?? null,
    });
  }
  return out;
}

export async function setJournalReviewMark(
  dealId: number,
  reviewed: boolean,
  markedBy: string = 'web_app_user',
): Promise<{ ok?: boolean; [k: string]: any }> {
  return request('/api/journal-review/marks', {
    method: 'POST',
    body: JSON.stringify({ company_id: 11518223, deal_id: dealId, reviewed, marked_by: markedBy }),
  });
}

// ---------------------------------------------------------------------------
// 類似仕訳検索 (GET /api/deals/search — worker 並行実装。応答形は防御的に読む)
//   初期条件は UI 側で 同一取引先・発生日±90日・金額±20% を組み立てて渡す。
// ---------------------------------------------------------------------------
export interface DealSearchRowDetail {
  account_item_id?: number | null; account_item_name?: string | null;
  section_id?: number | null; section_name?: string | null;
  tax_code?: number | null; amount?: number | null; description?: string | null;
  entry_side?: string | null;
  [k: string]: any;
}
export interface DealSearchRow {
  id: number;
  issue_date?: string | null;
  amount?: number | null;
  type?: string | null;
  partner_id?: number | null;
  partner_name?: string | null;
  ref_number?: string | null;
  description?: string | null;
  details?: DealSearchRowDetail[];
  [k: string]: any;
}

export async function searchDeals(params: {
  partnerId?: number | null;
  dateFrom?: string; dateTo?: string;
  amountMin?: number; amountMax?: number;
  limit?: number; companyId?: number;
}): Promise<DealSearchRow[]> {
  const q = new URLSearchParams({ company_id: String(params.companyId ?? 11518223) });
  if (params.partnerId != null) q.set('partner_id', String(params.partnerId));
  if (params.dateFrom) q.set('date_from', params.dateFrom);
  if (params.dateTo) q.set('date_to', params.dateTo);
  if (params.amountMin != null) q.set('amount_min', String(Math.max(0, Math.round(params.amountMin))));
  if (params.amountMax != null) q.set('amount_max', String(Math.round(params.amountMax)));
  q.set('limit', String(params.limit ?? 50));
  const r = await request<any>(`/api/deals/search?${q}`);
  const arr: any[] = Array.isArray(r) ? r
    : Array.isArray(r?.deals) ? r.deals
    : Array.isArray(r?.items) ? r.items
    : Array.isArray(r?.results) ? r.results : [];
  return arr
    .filter((d) => d && (d.id != null || d.deal_id != null))
    .map((d) => ({ ...d, id: Number(d.id ?? d.deal_id) }));
}

// freee 真値タイムスタンプ同期 (仕訳帳 generic_v2 非同期job → D1)。freee へは GET のみ。
export interface JournalReviewSyncResult {
  ok: boolean; pending?: boolean; report_id?: number;
  deals_upserted?: number; rows_total?: number; synced_at?: string;
}
export async function syncJournalReviewTimestamps(
  startDate: string, endDate: string,
): Promise<{ status: number; body: JournalReviewSyncResult }> {
  const res = await fetch(`${API_BASE}/api/journal-review/sync-freee-timestamps`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ start_date: startDate, end_date: endDate }),
  });
  return { status: res.status, body: await res.json().catch(() => ({ ok: false })) };
}
export async function completeJournalReviewTimestampsSync(
  reportId: number, startDate?: string, endDate?: string,
): Promise<{ status: number; body: JournalReviewSyncResult }> {
  const res = await fetch(`${API_BASE}/api/journal-review/sync-freee-timestamps/complete`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ report_id: reportId, start_date: startDate, end_date: endDate }),
  });
  return { status: res.status, body: await res.json().catch(() => ({ ok: false })) };
}

// walletables 名称解決 (type|id → 口座名) — 仕訳レビューの貸方(決済側)表示用
let _walletablesMapCache: Map<string, string> | null = null;
let _walletablesInflight: Promise<Map<string, string>> | null = null;
export async function fetchWalletablesMap(): Promise<Map<string, string>> {
  if (_walletablesMapCache) return _walletablesMapCache;
  if (_walletablesInflight) return _walletablesInflight;
  _walletablesInflight = (async () => {
    const r = await request<{ walletables?: Array<{ id: number; name: string; type: string }> }>(
      `/api/freee-proxy/api/1/walletables?company_id=${FREEE_COMPANY_ID}`,
    );
    const m = new Map<string, string>();
    for (const w of r.walletables || []) {
      if (w && w.id != null) m.set(`${w.type}|${w.id}`, w.name);
    }
    _walletablesMapCache = m;
    _walletablesInflight = null;
    return m;
  })().catch((e) => { _walletablesInflight = null; throw e; });
  return _walletablesInflight;
}

// ---------------------------------------------------------------------------
// バッチ完全性台帳 (schema-v75, blueprint v2 Phase 0)
//   一括承認N件の「全件間違いなく処理された」を台帳+freee独立照合で証明する。
// ---------------------------------------------------------------------------
export interface BatchCounts {
  pending: number; registering: number; posted: number;
  verified: number; verify_failed: number; failed: number; unverifiable: number;
  // 証憑添付済み件数 (worker が counts_json に追加予定 — 未提供の間は undefined。UI は 0 表示で耐える)
  receipts_attached?: number;
}
export interface BatchSummary {
  id: string; kind: string; created_by: string | null; created_at: number;
  total_count: number; total_amount: number | null; manifest_hash: string;
  counts_json: string | null; status: 'open' | 'attention' | 'closed';
  closed_by?: string | null; closed_at?: number | null; close_reason?: string | null;
}
export interface BatchItem {
  id: string; seq: number; target_ref: string;
  state: 'pending' | 'registering' | 'posted' | 'verified' | 'verify_failed' | 'failed';
  freee_deal_id: number | null; attempt: number; verify_attempt: number;
  posted_at: number | null; verified_at: number | null; recovered_at: number | null;
  error_json: string | null; verify_evidence_json: string | null; snapshot_json: string;
}

export async function createBatch(refs: string[], createdBy?: string): Promise<{ ok: boolean; batch_id: string; total_count: number; total_amount: number }> {
  return request('/api/batches', {
    method: 'POST',
    body: JSON.stringify({ kind: 'deal_register', refs, created_by: createdBy || 'web_app_user' }),
  });
}
export async function fetchBatches(status?: string): Promise<{ batches: BatchSummary[] }> {
  const q = new URLSearchParams();
  if (status) q.set('status', status);
  return request(`/api/batches?${q}`);
}
export async function fetchBatchDetail(id: string): Promise<{ batch: BatchSummary; items: BatchItem[] }> {
  return request(`/api/batches/${encodeURIComponent(id)}`);
}
export async function retryBatchFailed(id: string): Promise<{ ok: boolean; retried: number }> {
  return request(`/api/batches/${encodeURIComponent(id)}/retry-failed`, { method: 'POST', body: '{}' });
}
export async function closeBatch(id: string, reason: string): Promise<{ ok: boolean; all_verified: boolean }> {
  return request(`/api/batches/${encodeURIComponent(id)}/close`, {
    method: 'POST', body: JSON.stringify({ reason, closed_by: 'web_app_user' }),
  });
}

// ---------------------------------------------------------------------------
// 請求書処理run (カーソル起点一括処理, 2026-07-07)
//   「前回最後に分析した請求書から後の分」を ◯月◯日支払分としてワンクリック一括処理。
//   worker 並行実装中のため型は防御的 ([k: string]: any で未知フィールドを許容)。
// ---------------------------------------------------------------------------
export interface InvoiceRunPreviewItem {
  rowid: number;
  partner_name: string | null;
  receive_date: string | null;
  amount_hint: number | null;
  excluded_suggested: boolean | number | null;
  reason: string | null;
  [k: string]: any;
}
export interface InvoiceRunPreview {
  cursor: number | null;
  items: InvoiceRunPreviewItem[];
  count: number;
  max_rowid?: number | null;
  excluded_suggested_count?: number;
  [k: string]: any;
}
export interface InvoiceRunSummary {
  id: string | number;
  status: string; // 'open' | 'attention' | 'closed' 想定
  payment_date: string | null;
  from_rowid: number | null;
  to_rowid: number | null;
  counts_json: string | null;
  created_at?: number | string | null;
  created_by?: string | null;
  closed_at?: number | null;
  close_reason?: string | null;
  total_count?: number;
  [k: string]: any;
}
export interface InvoiceRunItem {
  rowid: number;
  partner_name: string | null;
  receive_date: string | null;
  state: string; // pending/extracting/proposed/needs_review/excluded/failed 系
  job_id: string | null;
  job_status?: string | null;  // jobs join (claimed/running = 今まさに抽出中)。無い場合はUI側が jobs API を thread_id={run_id} で引いて突合
  live_state?: string | null;  // worker 並行実装の別名 (dispatched 等)
  proposal_ref: string | null;
  proposal_id?: string | number | null;      // GET detail が dp JOIN で返す (worker.js:31280)
  proposal_status?: string | null;           // duplicate_review/duplicate_rejected/rejected 等の生存判定に使う
  note: string | null;
  [k: string]: any;
}

// 請求書PDFの閲覧URL (worker GET /pdf/{rowid} — %PDF magic 検証済み application/pdf を返す。
// 新規タブで人がそのまま閲覧できる。複数頁も PDF ビューアで頁送り可)
export function invoicePdfUrl(rowid: number | string): string {
  return `${API_BASE}/pdf/${encodeURIComponent(String(rowid))}`;
}

export async function fetchInvoiceRunPreview(): Promise<InvoiceRunPreview> {
  return request('/api/invoice-runs/preview');
}
export async function createInvoiceRun(
  paymentDate: string, excludeRowids?: number[],
): Promise<{
  run_id: string | number; total: number; dispatched: number; excluded: number;
  // 全件除外 run は worker 側で即 close + cursor 前進まで行う (open run を作らない)
  skip_only?: boolean; status?: string; cursor_advanced?: boolean; cursor?: number;
  [k: string]: any;
}> {
  return request('/api/invoice-runs', {
    method: 'POST',
    body: JSON.stringify({
      payment_date: paymentDate,
      ...(excludeRowids && excludeRowids.length ? { exclude_rowids: excludeRowids } : {}),
    }),
  });
}
export async function fetchInvoiceRuns(): Promise<{ runs: InvoiceRunSummary[] }> {
  return request('/api/invoice-runs');
}
export async function fetchInvoiceRunDetail(id: string | number): Promise<{ run: InvoiceRunSummary; items: InvoiceRunItem[] }> {
  return request(`/api/invoice-runs/${encodeURIComponent(String(id))}`);
}
export async function closeInvoiceRun(
  id: string | number, reason?: string,
): Promise<{ ok?: boolean; cursor?: number; new_cursor?: number; [k: string]: any }> {
  return request(`/api/invoice-runs/${encodeURIComponent(String(id))}/close`, {
    method: 'POST',
    body: JSON.stringify({ by: 'web_app_user', ...(reason ? { reason } : {}) }),
  });
}
export async function redispatchInvoiceRunFailed(
  id: string | number,
  opts?: { rowids?: number[]; include_pending?: boolean },
): Promise<{ ok?: boolean; redispatched?: number; retried?: number; reissued?: number; skipped?: number; failed_total?: number; [k: string]: any }> {
  return request(`/api/invoice-runs/${encodeURIComponent(String(id))}/redispatch-failed`, {
    method: 'POST',
    body: JSON.stringify({
      by: 'web_app_user',
      ...(opts?.rowids?.length ? { rowids: opts.rowids } : {}),
      ...(opts?.include_pending ? { include_pending: true } : {}),
    }),
  });
}
// needs_review (部門未確定等) の item を部門補完で再build。rowids 指定で item 単位再build (2026-07-08 W1)。
// レスポンスのキー名は worker 並行実装のため揺れを許容 (成功=rebuilt/resolved、解決不能=unresolved/still_blocked)
export async function rebuildInvoiceRunBlocked(
  id: string | number,
  opts?: { rowids?: number[] },
): Promise<{
  ok?: boolean; rebuilt?: number; resolved?: number; unresolved?: number; still_blocked?: number;
  attempted?: number; re_extracted?: number;
  details?: Array<{ rowid: number; outcome?: string; reason?: string; ref_number?: string; [k: string]: any }>;
  [k: string]: any;
}> {
  return request(`/api/invoice-runs/${encodeURIComponent(String(id))}/rebuild-blocked`, {
    method: 'POST',
    body: JSON.stringify({
      by: 'web_app_user',
      ...(opts?.rowids?.length ? { rowids: opts.rowids } : {}),
    }),
  });
}
// 部門未確定 (section_unresolved / RULE_7) の item に人が部門を明示指定して proposal 化する (2026-07-08)。
// sections = freee 部門ID配列 (現状UIは単一選択 = 全明細行に同一部門を適用)。
// 部門一覧は fetchSectionsMap() (freee-proxy /api/1/sections canonical) を流用する — 定数化禁止。
// レスポンスは worker 並行実装のためキー名の揺れを許容 (成功=ok/resolved/proposal_ref、失敗=error/reason)。
export async function resolveInvoiceRunItem(
  id: string | number,
  rowid: number,
  sections: number[],
): Promise<{
  ok?: boolean; resolved?: number; outcome?: string; proposal_ref?: string;
  error?: string; reason?: string; [k: string]: any;
}> {
  return request(`/api/invoice-runs/${encodeURIComponent(String(id))}/resolve-item`, {
    method: 'POST',
    body: JSON.stringify({ rowid, sections, by: 'web_app_user' }),
  });
}

export interface PipelineHealth {
  ts: number;
  batches: { open: number; attention: number };
  instructions: { pending: number };
  sweep: { last_run_at: number | null; stale: boolean };
}
export async function fetchHealthPipeline(): Promise<PipelineHealth> {
  return request('/api/health/pipeline');
}

// 仕訳提案の行編集 (worker /update は実装済 = 修正diffが学習へ還流する。Phase 0 でUI結線)
export async function updateDealProposal(
  refNumber: string,
  details: Array<{ account_item_id: number; tax_code: number; amount: number; section_id?: number | null; item_id?: number | null; description?: string | null; entry_side?: string }>,
  reason?: string,
): Promise<{ ok: boolean }> {
  return request(`/api/deal-proposals/${encodeURIComponent(refNumber)}/update`, {
    method: 'POST',
    body: JSON.stringify({ details, updated_by: 'web_app_user', reason: reason || null }),
  });
}

// ---------------------------------------------------------------------------
// ジョブ基盤 (schema-v75, blueprint v2 Phase 1 — 2026-07-05)
//   UI全操作をジョブとして POST → ミニPC常駐Runnerが claim → 実行 → complete。
//   worker: /api/jobs (POST/GET), /api/jobs/:id (GET events込み),
//           /api/jobs/:id/approve|cancel|retry (POST), /api/job-events?cursor=N
// ---------------------------------------------------------------------------

export type JobStatus =
  | 'queued' | 'awaiting_approval' | 'claimed' | 'running'
  | 'succeeded' | 'failed' | 'cancelled' | 'dead_letter';

export interface Job {
  id: string;
  workspace_id: string;
  type: string;
  payload_json: string | null;
  origin: 'ui' | 'cron' | 'cli' | 'system' | string;
  requires_approval: number;
  status: JobStatus;
  priority: number;
  dedupe_key: string | null;
  parent_job_id: string | null;
  thread_id: string | null;
  batch_id: string | null;
  attempt: number;
  max_attempts: number;
  timeout_sec: number;
  claimed_by: string | null;
  claim_expires_at: number | null;
  heartbeat_at: number | null;
  next_retry_at: number | null;
  result_json: string | null;
  error_json: string | null;
  approved_by: string | null;
  approved_at: number | null;
  created_by: string | null;
  created_at: number; // epoch ms
  updated_at: number; // epoch ms
  /** 実行中の最新活動1行 (worker jobs応答, 2026-07-08)。null/未配信 = まだ活動報告なし */
  progress_note?: string | null;
}

export interface JobEvent {
  id: number;
  job_id: string;
  ts: number; // epoch ms
  event: string; // queued/awaiting_approval/claimed/progress/completed/failed/retried/dead_letter/approved/cancelled
  actor: string | null;
  detail_json: string | null;
}

/** /api/job-events feed row = JobEvent + jobs join (type/status) */
export interface JobEventFeedItem extends JobEvent {
  type: string | null;
  status: JobStatus | null;
}

export interface CreateJobResponse {
  ok: boolean;
  id: string;
  status: JobStatus;
  deduped?: boolean;
}

export function createJob(
  type: string,
  payload: Record<string, any>,
  opts?: {
    thread_id?: string;
    created_by?: string;
    dedupe_key?: string;
    priority?: number;
    parent_job_id?: string;
    batch_id?: string;
  },
): Promise<CreateJobResponse> {
  return request<CreateJobResponse>('/api/jobs', {
    method: 'POST',
    body: JSON.stringify({
      type,
      payload,
      origin: 'ui',
      thread_id: opts?.thread_id,
      created_by: opts?.created_by || 'web_app_ui',
      dedupe_key: opts?.dedupe_key,
      priority: opts?.priority,
      parent_job_id: opts?.parent_job_id,
      batch_id: opts?.batch_id,
    }),
  });
}

export function fetchJobs(params: {
  status?: JobStatus | string;
  type?: string;
  thread_id?: string;
  batch_id?: string;
  limit?: number;
} = {}): Promise<{ jobs: Job[]; counts?: Record<string, number>; total?: number }> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', String(params.status));
  if (params.type) q.set('type', params.type);
  if (params.thread_id) q.set('thread_id', params.thread_id);
  if (params.batch_id) q.set('batch_id', params.batch_id);
  if (params.limit) q.set('limit', String(params.limit));
  // counts/total = LIMIT非依存のstatus別実数 (表示上限で「N件しかない」ように見える誤解の根絶)
  return request<{ jobs: Job[]; counts?: Record<string, number>; total?: number }>(`/api/jobs?${q}`);
}

export function fetchJobDetail(id: string): Promise<{ job: Job; events: JobEvent[] }> {
  return request<{ job: Job; events: JobEvent[] }>(`/api/jobs/${encodeURIComponent(id)}`);
}

export function approveJob(id: string, approvedBy?: string): Promise<{ ok: boolean; id: string; action: string }> {
  return request(`/api/jobs/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    body: JSON.stringify({ approved_by: approvedBy || 'web_app_user' }),
  });
}

export function cancelJob(id: string, by?: string): Promise<{ ok: boolean; id: string; action: string }> {
  return request(`/api/jobs/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ by: by || 'web_app_user' }),
  });
}

export function retryJob(id: string, by?: string): Promise<{ ok: boolean; id: string; action: string }> {
  return request(`/api/jobs/${encodeURIComponent(id)}/retry`, {
    method: 'POST',
    body: JSON.stringify({ by: by || 'web_app_user' }),
  });
}

export function fetchJobEvents(
  cursor: number = 0,
  wait: boolean = false,
): Promise<{ events: JobEventFeedItem[]; cursor: number }> {
  const q = new URLSearchParams({ cursor: String(cursor) });
  if (wait) q.set('wait', '1');
  return request(`/api/job-events?${q}`);
}

// ---------------------------------------------------------------------------
// Rulebook — ルールブック (blueprint v2 Phase 2, 2026-07-05)
//   学習内容 (rules / rule_versions / rule_applications / rule_feedback) の
//   閲覧・構造化編集 (CAS)・無効化・feedback・dry-run。裁定3: 構造化編集は
//   Worker 直接 PATCH (CLI 不介在)。schema: schema-v76-rulebook.sql
// ---------------------------------------------------------------------------

export interface RulebookRule {
  rule_id: string;
  domain: string; // bank_pattern/invoice_layout/amount_paradigm/billing_cadence/curated_default/line_account_override/reader_weight/routine/vendor_default/section_alias/knowledge
  match_keys_json: string;
  payload_json: string;
  status: string; // active/staging/disabled/superseded
  origin: string | null; // learned_auto/learned_from_approval/manual_ui/seeded/cli_proposed
  pinned: number;
  confidence: number | null;
  sample_count: number | null;
  approve_count: number | null;
  reject_count: number | null;
  provenance_json: string | null;
  source_table: string | null;
  version: number;
  created_at: number | string | null;
  updated_at: number | string | null;
  [k: string]: any;
}

export interface RulebookRuleVersion {
  version: number;
  snapshot_json?: string | null;
  diff_json?: string | null;
  changed_by?: string | null;
  reason?: string | null;
  ts?: number | string | null;
  [k: string]: any;
}

export interface RulebookApplication {
  applied_to?: string | null; // deal_proposal/bank_txn/invoice_line
  applied_to_id?: string | null;
  job_id?: string | null;
  result?: string | null; // accepted/overridden_by_user
  ts?: number | string | null;
  [k: string]: any;
}

export interface RulebookFeedbackItem {
  verdict?: string; // correct/wrong/partial
  source?: string | null; // ui_button/approval/rejection/correction
  comment?: string | null;
  by?: string | null;
  ts?: number | string | null;
  [k: string]: any;
}

export interface RulebookRuleDetail {
  rule: RulebookRule;
  versions: RulebookRuleVersion[];
  applications: RulebookApplication[];
  feedback: RulebookFeedbackItem[];
}

export interface RulebookDryRunResult {
  matched?: number;
  preview?: any[];
  dry_run_unsupported?: boolean;
  [k: string]: any;
}

export interface RulebookStatsResponse {
  total?: number;
  by_domain?: Record<string, number>;
  by_status?: Record<string, number>;
  domains?: Array<{ domain: string; count?: number; total?: number; [k: string]: any }>;
  [k: string]: any;
}

export function fetchRulebookRules(
  params: { domain?: string; status?: string; q?: string } = {},
): Promise<{ rules: RulebookRule[] }> {
  const q = new URLSearchParams();
  if (params.domain) q.set('domain', params.domain);
  if (params.status) q.set('status', params.status);
  if (params.q) q.set('q', params.q);
  const qs = q.toString();
  return request<{ rules: RulebookRule[] }>(`/api/rulebook${qs ? `?${qs}` : ''}`);
}

export function fetchRulebookRuleDetail(id: string): Promise<RulebookRuleDetail> {
  return request<RulebookRuleDetail>(`/api/rulebook/${encodeURIComponent(id)}`);
}

/** 構造化編集 (CAS)。expected_version 不一致は HTTP 409 → err.status === 409 */
export function patchRulebookRule(
  id: string,
  body: {
    expected_version: number;
    match_keys?: Record<string, any>;
    payload?: Record<string, any>;
    reason: string;
  },
): Promise<{ ok?: boolean; rule?: RulebookRule; version?: number; [k: string]: any }> {
  return request(`/api/rulebook/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function disableRulebookRule(id: string, by?: string): Promise<{ ok?: boolean; [k: string]: any }> {
  return request(`/api/rulebook/${encodeURIComponent(id)}/disable`, {
    method: 'POST',
    body: JSON.stringify({ by: by || 'web_app_user' }),
  });
}

export function enableRulebookRule(id: string, by?: string): Promise<{ ok?: boolean; [k: string]: any }> {
  return request(`/api/rulebook/${encodeURIComponent(id)}/enable`, {
    method: 'POST',
    body: JSON.stringify({ by: by || 'web_app_user' }),
  });
}

export function postRulebookFeedback(
  id: string,
  body: { verdict: 'correct' | 'wrong' | 'partial'; comment?: string; source?: string; by?: string },
): Promise<{ ok?: boolean; [k: string]: any }> {
  return request(`/api/rulebook/${encodeURIComponent(id)}/feedback`, {
    method: 'POST',
    body: JSON.stringify({
      verdict: body.verdict,
      source: body.source || 'ui_button',
      comment: body.comment,
      by: body.by || 'web_app_user',
    }),
  });
}

export function dryRunRulebookRule(
  id: string,
  params: Record<string, any> = {},
): Promise<RulebookDryRunResult> {
  return request<RulebookDryRunResult>(`/api/rulebook/${encodeURIComponent(id)}/dry-run`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export function fetchRulebookStats(): Promise<RulebookStatsResponse> {
  return request<RulebookStatsResponse>('/api/rulebook/stats');
}

/** 手動ルール追加 (POST /api/rulebook)。Worker 側で origin='manual_ui'・pinned=1・rule_versions v1 が付与される。
 *  domain は Worker の RULEBOOK_DOMAINS 検証を通る必要あり (未対応 domain は 400 invalid_domain)。 */
export function createRulebookRule(body: {
  domain: string; // vendor_default/curated_default/section_alias/knowledge ほか
  match_keys: Record<string, any>;
  payload: Record<string, any>;
  status?: 'active' | 'staging' | 'disabled';
  confidence?: number | null;
  provenance?: Record<string, any>;
  note?: string;
  reason?: string;
  created_by?: string;
}): Promise<{ ok?: boolean; rule?: RulebookRule; [k: string]: any }> {
  return request('/api/rulebook', { method: 'POST', body: JSON.stringify(body) });
}

/** 旧学習テーブル (v5学習ほか) → rules への read-through 同期 */
export function syncRulebookLearning(): Promise<{ ok?: boolean; [k: string]: any }> {
  return request('/api/rulebook/sync', { method: 'POST', body: JSON.stringify({}) });
}

// ---------------------------------------------------------------------------
// Dayplan — 今日のルーチン (blueprint v2 裁定11, Phase 2/3 — 2026-07-05)
//   GET /api/dayplan は導出ビュー (完了判定 = 該当deal/batch存在のSQL述語による決定論導出)。
//   永続化されるのは skip/snooze の例外記録 (dayplan_overrides) のみ。
// ---------------------------------------------------------------------------

export type DayplanItemStatus = 'done' | 'pending' | 'overdue' | 'skipped' | 'unknown';

export interface DayplanItem {
  key: string;
  name: string;
  due: boolean;              // 今日dueか (worker実応答はboolean — 2026-07-07 実測修正)
  scheduled_date?: string;   // 予定日 YYYY-MM-DD
  status: DayplanItemStatus;
  detail?: string | null;
  [k: string]: any;
}

export interface DayplanResponse {
  date: string;
  items: DayplanItem[];
  summary?: any;
  queue?: any;
  [k: string]: any;
}

export function fetchDayplan(date?: string): Promise<DayplanResponse> {
  const q = date ? `?date=${encodeURIComponent(date)}` : '';
  return request<DayplanResponse>(`/api/dayplan${q}`);
}

export function overrideDayplan(body: {
  date: string;
  item_key: string;
  action: 'skip' | 'snooze';
  reason: string;
  by?: string;
}): Promise<{ ok?: boolean; [k: string]: any }> {
  return request('/api/dayplan/override', {
    method: 'POST',
    body: JSON.stringify({ ...body, by: body.by || 'web_app_user' }),
  });
}

// ---------------------------------------------------------------------------
// Findings — 監査指摘 (blueprint v2 Phase 4 — 2026-07-05)
//   worker sweep (決定論検査) が検出した指摘の一覧・詳細・confirm/dismiss/reopen。
//   worker: GET /api/findings?status=&kind=&severity= / GET /api/findings/:id
//           POST /api/findings/:id/confirm|dismiss|reopen {by,reason}
//           POST /api/findings/sweep
// ---------------------------------------------------------------------------

export type FindingSeverity = 'high' | 'medium' | 'low' | string;
export type FindingStatus = 'open' | 'confirmed' | 'dismissed' | string;
export type FindingKind =
  | 'batch_verify_failed'   // 照合不一致
  | 'batch_unverifiable'    // 検証不能
  | 'job_dead_letter'       // 処理不能ジョブ
  | 'routine_overdue'       // ルーチン遅延
  | 'duplicate_deal'        // 重複疑い
  | 'vendor_invoice_missing'     // 請求書 未着 (取引先×サイクル, 2026-10-21)
  | 'vendor_invoice_resumed'     // 取引終了除外の自動解除 (low)
  | 'arica_fetch_platform_stuck' // ARICA 取得経路 障害
  | 'arica_fetch_email_stuck'    // ARICA 取得失敗 (メール単位の保険通知)
  | string;

export interface Finding {
  id: string;
  kind: FindingKind;
  severity: FindingSeverity;
  target_type: string; // 'batch' | 'batch_item' | 'job' | 'deal' | ...
  target_id: string;
  summary: string;
  evidence_json: string | null;
  proposed_fix_json: string | null;
  status: FindingStatus;
  created_at: number; // epoch ms
  [k: string]: any;
}

/** finding イベント (タイムライン)。worker 並行実装中のため形は防御的に受ける */
export interface FindingEvent {
  id?: number;
  finding_id?: string;
  ts?: number | string;
  event?: string;
  actor?: string | null;
  by?: string | null;
  reason?: string | null;
  detail_json?: string | null;
  [k: string]: any;
}

export function fetchFindings(params: {
  status?: string; kind?: string; severity?: string;
} = {}): Promise<{ findings: Finding[] }> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.kind) q.set('kind', params.kind);
  if (params.severity) q.set('severity', params.severity);
  const qs = q.toString();
  return request<{ findings: Finding[] }>(`/api/findings${qs ? `?${qs}` : ''}`);
}

export function fetchFindingDetail(id: string): Promise<{ finding: Finding; events: FindingEvent[] }> {
  return request(`/api/findings/${encodeURIComponent(id)}`);
}

export function confirmFinding(id: string, by?: string, reason?: string): Promise<{ ok?: boolean; [k: string]: any }> {
  return request(`/api/findings/${encodeURIComponent(id)}/confirm`, {
    method: 'POST',
    body: JSON.stringify({ by: by || 'web_app_user', reason: reason || null }),
  });
}

/** 却下は理由必須 (監査記録に残る) */
export function dismissFinding(id: string, reason: string, by?: string): Promise<{ ok?: boolean; [k: string]: any }> {
  return request(`/api/findings/${encodeURIComponent(id)}/dismiss`, {
    method: 'POST',
    body: JSON.stringify({ by: by || 'web_app_user', reason }),
  });
}

export function reopenFinding(id: string, by?: string, reason?: string): Promise<{ ok?: boolean; [k: string]: any }> {
  return request(`/api/findings/${encodeURIComponent(id)}/reopen`, {
    method: 'POST',
    body: JSON.stringify({ by: by || 'web_app_user', reason: reason || null }),
  });
}

/** 今すぐスイープ (worker 決定論検査を即時実行) */
export function sweepFindings(): Promise<{ ok?: boolean; [k: string]: any }> {
  return request('/api/findings/sweep', { method: 'POST', body: '{}' });
}

/** 請求書未着 (vendor_invoice_missing) の「今月なし」= 当サイクルのみ却下。reason 先頭タグを worker が着荷扱いに読む */
export const VENDOR_MISSING_NO_INVOICE_TAG = '[no_invoice_this_cycle]';
export function dismissFindingNoInvoiceThisCycle(id: string, by?: string): Promise<{ ok?: boolean; [k: string]: any }> {
  return dismissFinding(id, `${VENDOR_MISSING_NO_INVOICE_TAG} 今月は請求なし`, by);
}

/** 請求書未着の「取引終了」= 取引先を未着監視から恒久除外 (ended: 再着荷で自動解除 / irregular: 手動のみ) */
export function excludeVendorFinding(id: string, reason: 'ended' | 'irregular', note?: string, by?: string): Promise<{ ok?: boolean; [k: string]: any }> {
  return request(`/api/findings/${encodeURIComponent(id)}/exclude-vendor`, {
    method: 'POST',
    body: JSON.stringify({ by: by || 'web_app_user', reason, note: note || null }),
  });
}

// ============================================================
// 本部両替金 / 本部現金 出納帳 (2026-07-28, 紙帳簿全廃のSoT)
// ============================================================
export type HqCashBook = 'ryogae' | 'honbu';

export interface HqCashLedgerRow {
  id: number;
  date: string;
  date_raw: string | null;
  seq: number | null;
  direction: 'in' | 'out';
  amount: number;
  category: string;
  summary: string | null;
  note: string | null;
  paper_balance: number | null;
  source_doc: string | null;
  source_page: number | null;
  source_line: number | null;
  conf: string | null;
  link_status: string;
  running_balance: number;
  paper_mismatch: number;
}

export interface HqCashLedgerResponse {
  ok: boolean;
  book: HqCashBook;
  walletable_id: number;
  label: string;
  month: string;
  anchor: { anchor_date: string; anchor_physical_balance: number; note?: string | null } | null;
  carry_in: number;
  rows: HqCashLedgerRow[];
  income_total: number;
  expense_total: number;
  final_balance: number;
  row_count: number;
  mismatch_count: number;
  months: string[];
}

export function fetchHqCashLedger(book: HqCashBook, month?: string): Promise<HqCashLedgerResponse> {
  const q = new URLSearchParams({ book });
  if (month) q.set('month', month);
  return request(`/api/hq-cash/ledger?${q}`);
}

export { API_BASE };
